import { useState, useRef, useCallback, useEffect, useMemo, useReducer, lazy, Suspense } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import greenvisionLogoPng from "@/assets/greenvision-logo.png";

const SitePlanner3DView = lazy(() => import("@/components/crm/SitePlanner3DView"));
import { useNavigate, useParams } from "react-router-dom";

// ============================================================================
// GREENVISION SITE PLANNER EDITOR — v5
// Standalone editor component for /crm/tekenaar/:projectId
// ============================================================================

// DESIGN TOKENS (Greenvision huisstijl)
const C = {
  primary:"#014336",primaryLt:"#02714D",accent:"#00C853",
  accentDim:"rgba(0,200,83,0.10)",accentMid:"rgba(0,200,83,0.30)",
  bg:"#F7F9F8",surface:"#FFFFFF",surfAlt:"#EFF3F1",
  border:"#DCE4E0",borderLt:"#EBF0ED",
  text:"#1A1A1A",textSec:"#5F6B66",textMut:"#94A09A",
  err:"#DC2626",warn:"#F59E0B",info:"#3B82F6",snap:"#FF6B00",
  gl:"rgba(1,67,54,0.04)",gm:"rgba(1,67,54,0.10)",
  bag:"rgba(139,92,246,0.15)",bagS:"#8B5CF6",
};

const LC = {
  gebouw:{f:"rgba(1,67,54,0.82)",s:"#014336",tx:"#ffffff"},
  logistiek:{f:"rgba(220,38,38,0.72)",s:"#DC2626",tx:"#ffffff"},
  parking:{f:"rgba(59,130,246,0.68)",s:"#2563EB",tx:"#ffffff"},
  terrein:{f:"rgba(0,200,83,0.62)",s:"#16A34A",tx:"#ffffff"},
  weg:{f:"rgba(55,65,81,0.70)",s:"#374151",tx:"#ffffff"},
  voertuigen:{f:"rgba(245,158,11,0.78)",s:"#D97706",tx:"#ffffff"},
  lijnen:{f:"none",s:"#6B7280",tx:"#374151"},
};

// SNAP & GRID
const SG = 0.5;
const SBD = 3;
const sg = (v) => Math.round(v / SG) * SG;

// ============================================================================
// VERTEX SNAP ENGINE — snap polygon/kavel vertices to other geometry
// ============================================================================
function snapVertex(x, y, excludeBlockId, blocks, kavel) {
  let bestDist = SBD;
  let snapX = x, snapY = y;
  let snapped = false;
  const guides = [];

  // Helper: snap to a point
  const trySnapPoint = (px, py) => {
    const d = Math.hypot(px - x, py - y);
    if (d < bestDist) { bestDist = d; snapX = px; snapY = py; snapped = true; }
  };

  // Helper: snap to nearest point on line segment
  const trySnapEdge = (ax, ay, bx, by) => {
    const dx = bx - ax, dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) { trySnapPoint(ax, ay); return; }
    let t = ((x - ax) * dx + (y - ay) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    trySnapPoint(ax + t * dx, ay + t * dy);
  };

  // 1. Snap to kavel vertices and edges
  if (kavel && kavel.length >= 3) {
    kavel.forEach((p) => trySnapPoint(p[0], p[1]));
    kavel.forEach((p, i) => {
      const next = kavel[(i + 1) % kavel.length];
      trySnapEdge(p[0], p[1], next[0], next[1]);
    });
  }

  // 2. Snap to other block vertices and edges
  (blocks || []).forEach((b) => {
    if (b.id === excludeBlockId) return;
    if (b.polygon && b.polygon.length >= 3) {
      b.polygon.forEach((p) => trySnapPoint(p[0], p[1]));
      b.polygon.forEach((p, i) => {
        const next = b.polygon[(i + 1) % b.polygon.length];
        trySnapEdge(p[0], p[1], next[0], next[1]);
      });
    } else {
      // Rect corners
      const corners = [[b.x,b.y],[b.x+b.w,b.y],[b.x+b.w,b.y+b.h],[b.x,b.y+b.h]];
      corners.forEach((p) => trySnapPoint(p[0], p[1]));
      // Rect edges
      corners.forEach((p, i) => {
        const next = corners[(i + 1) % 4];
        trySnapEdge(p[0], p[1], next[0], next[1]);
      });
    }
  });

  if (snapped) {
    guides.push({ type: "v", pos: snapX });
    guides.push({ type: "h", pos: snapY });
  }

  return { x: sg(snapX), y: sg(snapY), snapped, guides };
}

// ============================================================================
// BLOCK TYPE DEFINITIONS
// ============================================================================

// Convex hull (Graham scan) for merge
function convexHull(points) {
  const pts = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (pts.length <= 2) return pts;
  const cross = (O, A, B) => (A[0]-O[0])*(B[1]-O[1]) - (A[1]-O[1])*(B[0]-O[0]);
  const lower = [];
  for (const p of pts) { while (lower.length >= 2 && cross(lower[lower.length-2], lower[lower.length-1], p) <= 0) lower.pop(); lower.push(p); }
  const upper = [];
  for (const p of pts.reverse()) { while (upper.length >= 2 && cross(upper[upper.length-2], upper[upper.length-1], p) <= 0) upper.pop(); upper.push(p); }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

const BT = [
  { id:"warehouse", label:"Warehouse", icon:"◻", cat:"gebouw", dw:86.4, dh:129.6,
    params:{ load:50, bayX:10.8, bayY:21.6, baysX:8, baysY:6, ffl:1.2, height:12 },
    pd:[{k:"load",l:"Vloerlast kN/m²",s:5},{k:"bayX",l:"Stramien X",s:0.6},{k:"bayY",l:"Stramien Y",s:0.6},
        {k:"baysX",l:"Bays X",s:1},{k:"baysY",l:"Bays Y",s:1},{k:"height",l:"Bouwhoogte (m)",s:0.5}] },
  { id:"expedition", label:"Expeditie", icon:"⇄", cat:"gebouw", dw:24, dh:80,
    params:{ load:25, height:8 }, pd:[{k:"load",l:"Vloerlast kN/m²",s:5},{k:"height",l:"Bouwhoogte (m)",s:0.5}] },
  { id:"docks", label:"Dockstrip", icon:"▥", cat:"logistiek", dw:4, dh:63,
    params:{ count:18, dw:3.5, height:5 }, pd:[{k:"count",l:"Aantal",s:1},{k:"dw",l:"Breedte",s:0.5},{k:"height",l:"Bouwhoogte (m)",s:0.5}] },
  { id:"laadplein", label:"Laadplein", icon:"⚡", cat:"logistiek", dw:40, dh:30,
    params:{ capacity:10 }, pd:[{k:"capacity",l:"Laadpunten",s:1}], freeform:true },
  { id:"parking_car", label:"Parking auto", icon:"P", cat:"parking", dw:50, dh:16,
    params:{ spaces:40, sw:2.5, sl:5.0, roadW:6.0, rows:2 },
    pd:[{k:"sw",l:"Vakbreedte (m)",s:0.1},{k:"sl",l:"Vakdiepte (m)",s:0.5},{k:"roadW",l:"Rijweg (m)",s:0.5},{k:"rows",l:"Rijen",s:1},{k:"spaces",l:"Plaatsen (auto)",s:1}] },
  { id:"parking_truck", label:"Parking truck", icon:"V", cat:"parking", dw:73.5, dh:42,
    params:{ spaces:21, sw:3.5, sl:18.0, roadW:6.0, rows:2 },
    pd:[{k:"sw",l:"Vakbreedte (m)",s:0.5},{k:"sl",l:"Vakdiepte (m)",s:0.5},{k:"roadW",l:"Rijweg (m)",s:0.5},{k:"rows",l:"Rijen",s:1},{k:"spaces",l:"Plaatsen",s:1}] },
  { id:"office", label:"Kantoor", icon:"⊡", cat:"gebouw", dw:20, dh:15,
    params:{ floors:1, height:3.5 }, pd:[{k:"floors",l:"Verdiepingen",s:1},{k:"height",l:"Bouwhoogte (m)",s:0.5}] },
  { id:"green", label:"Groenstrook", icon:"▧", cat:"terrein", dw:30, dh:10, params:{}, pd:[] },
  { id:"road", label:"Weg", icon:"═", cat:"terrein", dw:60, dh:6,
    params:{ lanes:2, width:6 }, pd:[{k:"width",l:"Wegbreedte (m)",s:0.5},{k:"lanes",l:"Stroken",s:1}] },
  { id:"voorterrein", label:"Voorterrein", icon:"⊞", cat:"logistiek", dw:80, dh:32,
    params:{ depth:32 }, pd:[{k:"depth",l:"Diepte (min 32m)",s:1}] },
  { id:"car", label:"Auto", icon:"🚗", cat:"voertuigen", dw:2, dh:4.5, params:{ height:1.5 }, pd:[{k:"height",l:"Hoogte (m)",s:0.5}] },
  { id:"truck", label:"Vrachtwagen", icon:"🚛", cat:"voertuigen", dw:2.5, dh:12,
    params:{ showTurn:0, turnR:12.5, height:3.5 }, pd:[{k:"turnR",l:"Draaistraal",s:0.5},{k:"height",l:"Hoogte (m)",s:0.5}] },
  { id:"trailer", label:"Trekker+opl.", icon:"🚚", cat:"voertuigen", dw:2.5, dh:18,
    params:{ showTurn:0, turnR:13.5, height:4 }, pd:[{k:"turnR",l:"Draaistraal",s:0.5},{k:"height",l:"Hoogte (m)",s:0.5}] },
];

const getT = (id) => BT.find((t) => t.id === id);
const getLC = (cat, typeId) => {
  if (typeId === "road" || typeId === "voorterrein") return LC.weg;
  return LC[cat] || LC.terrein;
};

// ============================================================================
// UNDO SYSTEM
// ============================================================================
function hR(st, a) {
  switch (a.type) {
    case "P": return { past: [...st.past, { state: st.now, label: a.label || `Actie ${st.past.length+1}` }].slice(-50), now: a.d, future: [], labels: st.labels };
    case "PB": return { past: [...st.past, { state: a.base, label: a.label || `Actie ${st.past.length+1}` }].slice(-50), now: a.d, future: [] };
    case "U": return st.past.length ? { past: st.past.slice(0, -1), now: st.past.at(-1).state, future: [{ state: st.now, label: `Ongedaan` }, ...st.future].slice(0, 50) } : st;
    case "R": return st.future.length ? { past: [...st.past, { state: st.now, label: st.future[0].label || "Redo" }], now: st.future[0].state, future: st.future.slice(1) } : st;
    case "S": return { ...st, now: typeof a.d === "function" ? a.d(st.now) : a.d };
    case "JUMP": {
      const all = [...st.past.map(p => p.state || p), st.now, ...st.future.map(f => f.state || f)];
      const allLabels = [...st.past.map(p => p.label || ""), "Huidig", ...st.future.map(f => f.label || "")];
      const idx = a.idx;
      return {
        past: all.slice(0, idx).map((s, i) => ({ state: s, label: allLabels[i] || `Actie ${i+1}` })),
        now: all[idx],
        future: all.slice(idx+1).map((s, i) => ({ state: s, label: allLabels[idx+1+i] || "" })),
      };
    }
    case "INIT": return { past: [], now: a.d, future: [] };
    default: return st;
  }
}

// ============================================================================
// SNAP-TO-BLOCK ENGINE
// ============================================================================
function calcSnap(m, all) {
  const guides = [];
  let snapX = null, snapY = null;
  const me = { l:m.x, r:m.x+m.w, t:m.y, b:m.y+m.h, cx:m.x+m.w/2, cy:m.y+m.h/2 };
  for (const b of all) {
    if (b.id === m.id) continue;
    const e = { l:b.x, r:b.x+b.w, t:b.y, b:b.y+b.h, cx:b.x+b.w/2, cy:b.y+b.h/2 };
    for (const [mv, bv] of [[me.l,e.l],[me.l,e.r],[me.r,e.l],[me.r,e.r],[me.cx,e.cx]]) {
      const d = Math.abs(mv - bv);
      if (d < SBD && (!snapX || d < Math.abs(snapX.d))) snapX = { d: mv-bv, t: bv };
    }
    for (const [mv, bv] of [[me.t,e.t],[me.t,e.b],[me.b,e.t],[me.b,e.b],[me.cy,e.cy]]) {
      const d = Math.abs(mv - bv);
      if (d < SBD && (!snapY || d < Math.abs(snapY.d))) snapY = { d: mv-bv, t: bv };
    }
  }
  let bx = m.x, by = m.y;
  if (snapX) { bx = m.x - snapX.d; guides.push({ type:"v", pos: snapX.t }); }
  if (snapY) { by = m.y - snapY.d; guides.push({ type:"h", pos: snapY.t }); }
  return { x: sg(bx), y: sg(by), guides };
}

// ============================================================================
// PDOK APIs
// ============================================================================
async function geocode(q) {
  try {
    const r = await fetch(`https://api.pdok.nl/bzk/locatieserver/search/v3_1/free?q=${encodeURIComponent(q)}&rows=5&fq=type:adres OR type:postcode OR type:weg`);
    const d = await r.json();
    return (d.response?.docs || []).map((doc) => {
      const m = (doc.centroide_rd || "").match(/POINT\(([\d.]+)\s+([\d.]+)\)/);
      return { label: doc.weergavenaam, rdX: m ? +m[1] : 0, rdY: m ? +m[2] : 0, type: doc.type };
    }).filter((x) => x.rdX > 0);
  } catch { return []; }
}

async function fetchBAGPanden(rdX, rdY, radius = 150) {
  try {
    const bbox = [rdX-radius, rdY-radius, rdX+radius, rdY+radius].join(",");
    const url = `https://service.pdok.nl/lv/bag/wfs/v2_0?service=WFS&version=2.0.0&request=GetFeature&typeName=bag:pand&outputFormat=application/json&srsName=EPSG:28992&bbox=${bbox},EPSG:28992&count=200`;
    const r = await fetch(url);
    const data = await r.json();
    return (data.features || []).map((f) => {
      const coords = f.geometry?.coordinates?.[0]?.[0] || f.geometry?.coordinates?.[0] || [];
      return { id: f.id, coords: coords.map((c) => ({ x:c[0], y:c[1] })), year: f.properties?.bouwjaar };
    }).filter((p) => p.coords.length >= 3);
  } catch { return []; }
}

function pdokWmsUrl(layer, rdX, rdY, viewport) {
  const half = viewport / 2;
  const bbox = [rdX-half, rdY-half, rdX+half, rdY+half];
  const base = layer === "luchtfoto"
    ? "https://service.pdok.nl/hwh/luchtfotorgb/wms/v1_0"
    : "https://service.pdok.nl/kadaster/kadastralekaart/wms/v5_0";
  const layers = layer === "luchtfoto" ? "Actueel_orthoHR" : "Kadastralekaart";
  return `${base}?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&LAYERS=${layers}&CRS=EPSG:28992&BBOX=${bbox.join(",")}&WIDTH=1200&HEIGHT=1200&FORMAT=image/png&STYLES=`;
}

// ============================================================================
// DXF EXPORT (client-side) — supports polygons + metadata
// ============================================================================
function genDXF(blocks, lines, kavel) {
  let s = "0\nSECTION\n2\nHEADER\n0\nENDSEC\n0\nSECTION\n2\nTABLES\n0\nTABLE\n2\nLAYER\n";
  const ls = ["KAVEL","WAREHOUSE","EXPEDITIE","DOCKS","PARKING","KANTOOR","GROEN","WEG","VOERTUIGEN","LIJNEN","FREEFORM","LAADPLEIN"];
  const cs = [7,1,3,4,5,2,80,8,40,9,6,30];
  ls.forEach((l, i) => { s += `0\nLAYER\n2\n${l}\n70\n0\n62\n${cs[i]}\n6\nContinuous\n`; });
  s += "0\nENDTAB\n0\nENDSEC\n0\nSECTION\n2\nENTITIES\n";
  const lm = { warehouse:"WAREHOUSE", expedition:"EXPEDITIE", docks:"DOCKS", parking_car:"PARKING",
    parking_truck:"PARKING", office:"KANTOOR", green:"GROEN", road:"WEG", voorterrein:"WEG",
    car:"VOERTUIGEN", truck:"VOERTUIGEN", trailer:"VOERTUIGEN", laadplein:"LAADPLEIN" };
  if (kavel?.length >= 3) {
    s += "0\nLWPOLYLINE\n8\nKAVEL\n70\n1\n90\n" + kavel.length + "\n";
    kavel.forEach((p) => { s += `10\n${p[0]}\n20\n${p[1]}\n`; });
  }
  blocks.forEach((b) => {
    const lay = lm[b.type] || "FREEFORM";
    const hasPoly = b.polygon && b.polygon.length >= 3;
    if (hasPoly) {
      // Export actual polygon vertices
      s += `0\nLWPOLYLINE\n8\n${lay}\n70\n1\n90\n${b.polygon.length}\n`;
      b.polygon.forEach((p) => { s += `10\n${p[0]}\n20\n${p[1]}\n`; });
      // Centroid text label
      const cx = b.polygon.reduce((sum, p) => sum + p[0], 0) / b.polygon.length;
      const cy = b.polygon.reduce((sum, p) => sum + p[1], 0) / b.polygon.length;
      let area = 0;
      for (let i = 0; i < b.polygon.length; i++) {
        const j = (i+1) % b.polygon.length;
        area += b.polygon[i][0]*b.polygon[j][1] - b.polygon[j][0]*b.polygon[i][1];
      }
      area = Math.abs(area) / 2;
      const t = getT(b.type);
      if (t) s += `0\nTEXT\n8\n${lay}\n10\n${cx}\n20\n${cy}\n40\n2\n1\n${t.label} ${area.toFixed(0)}m2\n72\n1\n11\n${cx}\n21\n${cy}\n`;
    } else {
      // Rect as 4-point polyline
      s += `0\nLWPOLYLINE\n8\n${lay}\n70\n1\n90\n4\n10\n${b.x}\n20\n${b.y}\n10\n${b.x+b.w}\n20\n${b.y}\n10\n${b.x+b.w}\n20\n${b.y+b.h}\n10\n${b.x}\n20\n${b.y+b.h}\n`;
      const t = getT(b.type);
      if (t) s += `0\nTEXT\n8\n${lay}\n10\n${b.x+b.w/2}\n20\n${b.y+b.h/2}\n40\n2\n1\n${t.label} ${(b.w*b.h).toFixed(0)}m2\n72\n1\n11\n${b.x+b.w/2}\n21\n${b.y+b.h/2}\n`;
    }
  });
  (lines || []).forEach((ln) => {
    if (ln.points?.length >= 2) {
      s += "0\nLWPOLYLINE\n8\nLIJNEN\n70\n0\n90\n" + ln.points.length + "\n";
      ln.points.forEach((p) => { s += `10\n${p.x}\n20\n${p.y}\n`; });
    }
  });
  s += "0\nENDSEC\n0\nEOF\n";
  return s;
}

function dl(content, fn, mime) {
  const b = new Blob([content], { type: mime });
  const u = URL.createObjectURL(b);
  const a = document.createElement("a");
  a.href = u; a.download = fn; document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(u);
}

// ============================================================================
// INLINE IMAGES — fetch external <image> hrefs and convert to base64 data URLs
// ============================================================================
function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}
async function inlineImages(svgClone) {
  const images = svgClone.querySelectorAll("image");
  await Promise.all([...images].map(async (img) => {
    const href = img.getAttribute("href") || img.getAttributeNS("http://www.w3.org/1999/xlink", "href");
    if (!href || href.startsWith("data:")) return;
    try {
      const resp = await fetch(href, { mode: "cors" });
      const blob = await resp.blob();
      const dataUrl = await blobToDataUrl(blob);
      img.setAttribute("href", dataUrl);
      img.removeAttributeNS("http://www.w3.org/1999/xlink", "href");
    } catch { img.remove(); }
  }));
}

// ============================================================================
// CLEAN SVG FOR EXPORT — strips UI elements, optionally adds title block + legend
// ============================================================================
async function cleanSvgForExport(svgEl, { addTitleBlock = false, addLegend = false, projectName = "", projectAddress = "", kpis = {}, blocks = [], kavel = [], logoDataUrl = null, sc = 4, pan = { x: 50, y: 30 }, includeBg = true } = {}) {
  // Calculate bounding box of ALL blocks + kavel so the entire drawing fits in the export
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const expandPt = (px, py) => {
    const sx = pan.x + px * sc;
    const sy = pan.y + py * sc;
    minX = Math.min(minX, sx); minY = Math.min(minY, sy);
    maxX = Math.max(maxX, sx); maxY = Math.max(maxY, sy);
  };
  // Include kavel boundary
  (kavel || []).forEach(([px, py]) => expandPt(px, py));
  // Include all blocks
  (blocks || []).forEach((b) => {
    if (b.polygon && b.polygon.length >= 3) {
      b.polygon.forEach(([px, py]) => expandPt(px, py));
    } else {
      expandPt(b.x, b.y);
      expandPt(b.x + b.w, b.y + b.h);
    }
  });

  // Also include map background origin
  minX = Math.min(minX, pan.x);
  minY = Math.min(minY, pan.y);

  // Fallback to viewport if no blocks
  const rect = svgEl.getBoundingClientRect();
  if (!isFinite(minX)) {
    minX = 0; minY = 0; maxX = rect.width || 1200; maxY = rect.height || 800;
  }

  // Add padding (60px for legend/title space)
  const pad = 80;
  const titleH = addTitleBlock ? 60 : 0;
  const vx = minX - pad;
  const vy = minY - pad;
  const vw = (maxX - minX) + pad * 2;
  const vh = (maxY - minY) + pad * 2 + titleH;

  const clone = svgEl.cloneNode(true);

  // Set explicit dimensions and viewBox to fit ALL content
  clone.setAttribute("width", vw);
  clone.setAttribute("height", vh);
  clone.setAttribute("viewBox", `${vx} ${vy} ${vw} ${vh}`);

  // Remove all elements with data-ui attribute (snap guides, selection handles, grid, etc.)
  clone.querySelectorAll("[data-ui]").forEach((el) => {
    // Keep background elements if includeBg is true
    if (el.getAttribute("data-ui") === "background" && includeBg) return;
    el.remove();
  });

  // Remove event handler attributes
  clone.querySelectorAll("*").forEach((el) => {
    const attrs = [...el.attributes];
    attrs.forEach((a) => {
      if (a.name.startsWith("on") || a.name === "style" && a.value.includes("cursor")) return;
    });
  });

  const w = vw;
  const h = vh;

  if (addTitleBlock || addLegend) {
    if (addTitleBlock) {
      const tg = document.createElementNS("http://www.w3.org/2000/svg", "g");
      tg.setAttribute("transform", `translate(${vx}, ${vy + vh - titleH})`);
      // Background bar
      const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      bg.setAttribute("width", w); bg.setAttribute("height", String(titleH));
      bg.setAttribute("fill", "#014336"); bg.setAttribute("opacity", "0.95");
      tg.appendChild(bg);

      // Logo image (if available)
      if (logoDataUrl) {
        const logoImg = document.createElementNS("http://www.w3.org/2000/svg", "image");
        logoImg.setAttributeNS("http://www.w3.org/1999/xlink", "href", logoDataUrl);
        logoImg.setAttribute("x", "12"); logoImg.setAttribute("y", "8");
        logoImg.setAttribute("width", "44"); logoImg.setAttribute("height", "44");
        logoImg.setAttribute("opacity", "0.9");
        tg.appendChild(logoImg);
      }

      // Title
      const titleX = logoDataUrl ? 64 : 20;
      const title = document.createElementNS("http://www.w3.org/2000/svg", "text");
      title.setAttribute("x", titleX); title.setAttribute("y", "22");
      title.setAttribute("fill", "white"); title.setAttribute("font-size", "14");
      title.setAttribute("font-weight", "700"); title.setAttribute("font-family", "'Inter',sans-serif");
      title.textContent = `GREENVISION — ${projectName || "Terreinplan"}`;
      tg.appendChild(title);
      // Subtitle
      const sub = document.createElementNS("http://www.w3.org/2000/svg", "text");
      sub.setAttribute("x", titleX); sub.setAttribute("y", "42");
      sub.setAttribute("fill", "rgba(255,255,255,0.7)"); sub.setAttribute("font-size", "10");
      sub.setAttribute("font-family", "'Inter',sans-serif");
      sub.textContent = `${projectAddress || ""} — ${new Date().toLocaleDateString("nl-NL")}`;
      tg.appendChild(sub);

      // KPI strip
      const kpiItems = [
        ["BVO", kpis.bvo > 0 ? `${(kpis.bvo/1000).toFixed(1)}k m²` : "—"],
        ["Kavel", kpis.kavelArea > 0 ? `${(kpis.kavelArea/1000).toFixed(1)}k m²` : "—"],
        ["Bebouwing", `${kpis.bebouwingsPct}%`],
        ["P auto", kpis.pCar || 0],
        ["P truck", kpis.pTruck || 0],
        ["Docks", kpis.docks || 0],
      ];
      kpiItems.forEach(([label, val], i) => {
        const kx = w - 20 - (kpiItems.length - i) * 100;
        const kl = document.createElementNS("http://www.w3.org/2000/svg", "text");
        kl.setAttribute("x", kx); kl.setAttribute("y", "18");
        kl.setAttribute("fill", "rgba(255,255,255,0.5)"); kl.setAttribute("font-size", "8");
        kl.setAttribute("font-family", "'Inter',sans-serif");
        kl.textContent = label;
        tg.appendChild(kl);
        const kv = document.createElementNS("http://www.w3.org/2000/svg", "text");
        kv.setAttribute("x", kx); kv.setAttribute("y", "34");
        kv.setAttribute("fill", "white"); kv.setAttribute("font-size", "12");
        kv.setAttribute("font-weight", "700"); kv.setAttribute("font-family", "'Inter',sans-serif");
        kv.textContent = String(val);
        tg.appendChild(kv);
      });

      clone.appendChild(tg);
    }

    if (addLegend) {
      // Collect unique block categories with area totals
      const cats = new Map();
      (blocks || []).forEach((b) => {
        const t = getT(b.type);
        if (!t) return;
        let area = 0;
        if (b.polygon && b.polygon.length >= 3) {
          for (let i = 0; i < b.polygon.length; i++) {
            const j = (i+1) % b.polygon.length;
            area += b.polygon[i][0]*b.polygon[j][1] - b.polygon[j][0]*b.polygon[i][1];
          }
          area = Math.abs(area) / 2;
        } else {
          area = b.w * b.h;
        }
        if (!cats.has(t.id)) cats.set(t.id, { label: t.label, cat: t.cat, icon: t.icon, area: 0, count: 0 });
        const entry = cats.get(t.id);
        entry.area += area;
        entry.count += 1;
      });
      if (cats.size > 0) {
        const lg = document.createElementNS("http://www.w3.org/2000/svg", "g");
        const lx = vx + w - 170, ly = vy + 20;
        // Background
        const lbg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        lbg.setAttribute("x", lx - 10); lbg.setAttribute("y", ly - 5);
        lbg.setAttribute("width", "170"); lbg.setAttribute("height", String(cats.size * 20 + 25));
        lbg.setAttribute("rx", "6"); lbg.setAttribute("fill", "white");
        lbg.setAttribute("fill-opacity", "0.92"); lbg.setAttribute("stroke", "#DCE4E0");
        lg.appendChild(lbg);
        // Title
        const lt = document.createElementNS("http://www.w3.org/2000/svg", "text");
        lt.setAttribute("x", lx); lt.setAttribute("y", String(ly + 10));
        lt.setAttribute("fill", "#014336"); lt.setAttribute("font-size", "9");
        lt.setAttribute("font-weight", "700"); lt.setAttribute("font-family", "'Inter',sans-serif");
        lt.textContent = "LEGENDA";
        lg.appendChild(lt);
        let li = 0;
        cats.forEach(({ label, cat, area, count }) => {
          const lc = LC[cat] || LC.terrein;
          const ry = ly + 22 + li * 20;
          const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
          rect.setAttribute("x", lx); rect.setAttribute("y", String(ry));
          rect.setAttribute("width", "12"); rect.setAttribute("height", "12");
          rect.setAttribute("rx", "2"); rect.setAttribute("fill", lc.f);
          rect.setAttribute("stroke", lc.s); rect.setAttribute("stroke-width", "1");
          lg.appendChild(rect);
          const txt = document.createElementNS("http://www.w3.org/2000/svg", "text");
          txt.setAttribute("x", String(lx + 18)); txt.setAttribute("y", String(ry + 9));
          txt.setAttribute("fill", "#1A1A1A"); txt.setAttribute("font-size", "9");
          txt.setAttribute("font-family", "'Inter',sans-serif");
          txt.textContent = `${label}  ${area >= 1000 ? (area/1000).toFixed(1)+"k" : area.toFixed(0)} m²  (${count})`;
          lg.appendChild(txt);
          li++;
        });
        clone.appendChild(lg);
      }
    }
  }

  // ---- Scale bar ----
  if (sc > 0) {
    const exportW = vw;
    // Pick a "nice" distance that fills 10-25% of width
    const candidates = [5, 10, 20, 50, 100, 200, 500];
    let chosenDist = 50;
    for (const d of candidates) {
      const barPx = d * sc;
      const pct = barPx / exportW;
      if (pct >= 0.08 && pct <= 0.3) { chosenDist = d; break; }
    }
    const barW = chosenDist * sc;
    const barH = 6;
    const bx = vx + 20;
    const by = addTitleBlock ? (vy + vh - titleH - 30) : (vy + vh - 30);

    const sg = document.createElementNS("http://www.w3.org/2000/svg", "g");
    // White outline for contrast
    const bgLine = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    bgLine.setAttribute("x", bx - 1); bgLine.setAttribute("y", by - 1);
    bgLine.setAttribute("width", barW + 2); bgLine.setAttribute("height", barH + 2);
    bgLine.setAttribute("fill", "white"); bgLine.setAttribute("fill-opacity", "0.8");
    bgLine.setAttribute("rx", "1");
    sg.appendChild(bgLine);
    // Black bar
    const bar = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    bar.setAttribute("x", bx); bar.setAttribute("y", by);
    bar.setAttribute("width", barW); bar.setAttribute("height", barH);
    bar.setAttribute("fill", "#1A1A1A");
    sg.appendChild(bar);
    // End ticks
    [bx, bx + barW].forEach((tx) => {
      const tick = document.createElementNS("http://www.w3.org/2000/svg", "line");
      tick.setAttribute("x1", tx); tick.setAttribute("y1", by - 4);
      tick.setAttribute("x2", tx); tick.setAttribute("y2", by + barH + 4);
      tick.setAttribute("stroke", "#1A1A1A"); tick.setAttribute("stroke-width", "2");
      sg.appendChild(tick);
    });
    // Label
    const lbl = document.createElementNS("http://www.w3.org/2000/svg", "text");
    lbl.setAttribute("x", bx + barW / 2); lbl.setAttribute("y", by - 8);
    lbl.setAttribute("text-anchor", "middle");
    lbl.setAttribute("fill", "#1A1A1A"); lbl.setAttribute("font-size", "11");
    lbl.setAttribute("font-weight", "700"); lbl.setAttribute("font-family", "'Inter',sans-serif");
    lbl.textContent = `${chosenDist} m`;
    sg.appendChild(lbl);
    // White text shadow
    const lblBg = lbl.cloneNode(true);
    lblBg.setAttribute("stroke", "white"); lblBg.setAttribute("stroke-width", "3");
    lblBg.setAttribute("fill", "white"); lblBg.setAttribute("paint-order", "stroke");
    sg.insertBefore(lblBg, lbl);

    clone.appendChild(sg);
  }

  // Inline all external images as base64 data URLs
  await inlineImages(clone);

  return clone;
}

// ============================================================================
// SMALL COMPONENTS
// ============================================================================
const Inp = ({ label, value, onChange, step = 1, unit }) => (
  <div style={{ display:"flex", flexDirection:"column", gap:1 }}>
    <label style={{ fontSize:9, color:C.textMut, fontWeight:500 }}>{label}</label>
    <div style={{ display:"flex", alignItems:"center", gap:3 }}>
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)} step={step}
        style={{ width:"100%", background:C.surfAlt, color:C.text, border:`1px solid ${C.border}`,
          padding:"4px 6px", fontSize:11, borderRadius:5, fontFamily:"inherit", boxSizing:"border-box", outline:"none" }}
        onFocus={(e) => (e.target.style.borderColor = C.accent)}
        onBlur={(e) => (e.target.style.borderColor = C.border)} />
      {unit && <span style={{ fontSize:8, color:C.textMut }}>{unit}</span>}
    </div>
  </div>
);

const Btn = ({ children, onClick, primary, danger, small, disabled, style: sx }) => (
  <button onClick={onClick} disabled={disabled} style={{
    padding: small ? "3px 7px" : "5px 11px", fontSize: small ? 9 : 10, fontWeight:600,
    border: danger ? "1px solid #FECACA" : primary ? "none" : `1px solid ${C.border}`,
    borderRadius:5, cursor: disabled ? "default" : "pointer", fontFamily:"inherit", opacity: disabled ? 0.4 : 1,
    background: danger ? "#FEF2F2" : primary ? C.primary : C.surfAlt,
    color: danger ? C.err : primary ? "white" : C.text, ...sx,
  }}>{children}</button>
);

const TBtn = ({ icon, label, active, onClick }) => (
  <button onClick={onClick} title={label} style={{
    padding:"5px 9px", fontSize:12, border:`1px solid ${active ? C.accentMid : C.border}`,
    borderRadius:5, cursor:"pointer", background: active ? C.accentDim : "white",
    color: active ? C.primary : C.textSec, fontFamily:"inherit", lineHeight:1,
  }}>{icon}</button>
);

// ============================================================================
// BLOCK SHAPE SVG
// ============================================================================
function BlockShape({ b, sc, selected, multiSel, onMD, onResizeStart, layerVis, editingEdge, onEdgeResize, onEditEdge }) {
  const t = getT(b.type);
  if (!t || !layerVis) return null;
  const lc = getLC(t.cat, b.type);
  const isV = t.cat === "voertuigen";
  const isSel = selected || multiSel;
  const hasPoly = b.polygon && b.polygon.length >= 3;

  // For polygon blocks, compute bounding box for labels/selection
  if (hasPoly) {
    const pts = b.polygon.map(p => [p[0]*sc, p[1]*sc]);
    const svgPts = pts.map(p => p.join(",")).join(" ");
    const cx = pts.reduce((s,p) => s+p[0], 0) / pts.length;
    const cy = pts.reduce((s,p) => s+p[1], 0) / pts.length;
    let area = 0;
    for (let i = 0; i < b.polygon.length; i++) {
      const j = (i+1) % b.polygon.length;
      area += b.polygon[i][0]*b.polygon[j][1] - b.polygon[j][0]*b.polygon[i][1];
    }
    area = Math.abs(area) / 2;
    const isLaadplein = b.type === "laadplein";

    return (
      <g data-block-id={b.id} onMouseDown={(e) => { e.stopPropagation(); onMD(b.id, e); }}
        style={{ cursor: b.locked ? "not-allowed" : "move" }}>
        {isLaadplein && (
          <defs>
            <pattern id={`lp-stripe-${b.id}`} width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="8" stroke={lc.s} strokeWidth="1.5" opacity="0.3" />
            </pattern>
          </defs>
        )}
        <polygon points={svgPts} fill="white" opacity={0.85} />
        <polygon points={svgPts} fill={lc.f} opacity={b.opacity ?? 1.0}
          stroke={selected ? C.accent : multiSel ? "#3B82F6" : lc.s}
          strokeWidth={isSel ? 3 : 2} />
        {isLaadplein && <polygon points={svgPts} fill={`url(#lp-stripe-${b.id})`} opacity={b.opacity ?? 1.0} />}
        <text x={cx} y={cy-6} textAnchor="middle" dominantBaseline="middle"
          fill="#ffffff" fontSize={11} fontWeight="700" style={{ pointerEvents:"none", textShadow:"0 1px 3px rgba(0,0,0,0.5)" }}>
          {t.icon} {t.label.toUpperCase()}
        </text>
        <text x={cx} y={cy+8} textAnchor="middle" dominantBaseline="middle"
          fill="rgba(255,255,255,0.85)" fontSize={9} style={{ pointerEvents:"none", textShadow:"0 1px 2px rgba(0,0,0,0.4)" }}>
          {area.toFixed(0)} m²{b.params?.capacity ? ` · ${b.params.capacity} pts` : ""}
        </text>
        {/* Side lengths for polygon — click to edit */}
        {isSel && b.polygon.map((p, i) => {
          const next = b.polygon[(i+1) % b.polygon.length];
          const mx = (p[0]+next[0])/2, my = (p[1]+next[1])/2;
          const dist = Math.sqrt((next[0]-p[0])**2 + (next[1]-p[1])**2);
          const isEditing = editingEdge && editingEdge.blockId === b.id && editingEdge.edgeIdx === i;
          return (
            <g key={`len${i}`} data-ui="selection" style={{ cursor:"pointer" }}
              onClick={(e) => { e.stopPropagation(); onEditEdge({ blockId: b.id, edgeIdx: i }); }}>
              <rect x={mx*sc-20} y={my*sc-9} width={40} height={18} rx={3} fill={C.primary} opacity={0.9} />
              {isEditing ? (
                <foreignObject x={mx*sc-20} y={my*sc-9} width={40} height={18}>
                  <input
                    xmlns="http://www.w3.org/1999/xhtml"
                    autoFocus
                    defaultValue={dist.toFixed(1)}
                    style={{ width:"100%", height:"100%", background:"white", color:C.primary, border:"none",
                      borderRadius:3, textAlign:"center", fontSize:9, fontWeight:600, padding:0, outline:"none" }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.target.blur();
                      if (e.key === "Escape") onEditEdge(null);
                    }}
                    onBlur={(e) => {
                      const newDist = parseFloat(e.target.value.replace(",", "."));
                      onEditEdge(null);
                      if (isNaN(newDist) || newDist <= 0 || Math.abs(newDist - dist) < 0.01) return;
                      const midX = (p[0]+next[0])/2, midY = (p[1]+next[1])/2;
                      const edgeDir = [next[0]-p[0], next[1]-p[1]];
                      const len = Math.hypot(edgeDir[0], edgeDir[1]);
                      const dir = [edgeDir[0]/len, edgeDir[1]/len];
                      const half = newDist / 2;
                      const np0 = [sg(midX - dir[0]*half), sg(midY - dir[1]*half)];
                      const np1 = [sg(midX + dir[0]*half), sg(midY + dir[1]*half)];
                      onEdgeResize(b.id, i, np0, np1);
                    }}
                  />
                </foreignObject>
              ) : (
                <text x={mx*sc} y={my*sc+1} textAnchor="middle" dominantBaseline="middle"
                  fill="white" fontSize={8} fontWeight="600" style={{ pointerEvents:"none" }}>{dist.toFixed(1)}m</text>
              )}
            </g>
          );
        })}
        {selected && (
          <g data-ui="selection">
            {b.polygon.map((p, i) => (
              <circle key={i} cx={p[0]*sc} cy={p[1]*sc} r={6} fill={C.accent} stroke="white" strokeWidth={2}
                style={{ cursor:"grab" }}
                onMouseDown={(e) => {
                  e.stopPropagation(); e.preventDefault();
                  window.__polyDrag = { blockId:b.id, vertexIdx:i, mx:e.clientX, my:e.clientY, ox:p[0], oy:p[1] };
                }}
                onContextMenu={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  if (b.polygon.length > 3) {
                    const newPoly = b.polygon.filter((_, j) => j !== i);
                    const xs = newPoly.map(p=>p[0]), ys = newPoly.map(p=>p[1]);
                    window.__polyRemove = { blockId:b.id, newPoly, minX:Math.min(...xs), minY:Math.min(...ys),
                      maxX:Math.max(...xs), maxY:Math.max(...ys) };
                  }
                }} />
            ))}
            {/* Midpoint circles to add new vertices — hidden on short edges */}
            {b.polygon.map((p, i) => {
              const next = b.polygon[(i+1) % b.polygon.length];
              const edgeLen = Math.hypot(next[0]-p[0], next[1]-p[1]);
              if (edgeLen < 3) return null;
              const mx = (p[0]+next[0])/2, my = (p[1]+next[1])/2;
              return (
                <circle key={`mid${i}`} cx={mx*sc} cy={my*sc} r={3}
                  fill={C.accent} fillOpacity={0.6} stroke={C.accentMid} strokeWidth={1} strokeDasharray="2,2"
                  style={{ cursor:"pointer" }}
                  onMouseDown={(e) => {
                    e.stopPropagation(); e.preventDefault();
                    window.__polyAdd = { blockId:b.id, afterIdx:i, mx, my };
                  }} />
              );
            })}
          </g>
        )}
      </g>
    );
  }

  // Standard rect-based block rendering
  const x = b.x*sc, y = b.y*sc, w = b.w*sc, h = b.h*sc;
  const cx = x+w/2, cy = y+h/2, rot = b.rotation || 0;

  return (
    <g data-block-id={b.id} transform={rot ? `rotate(${rot},${cx},${cy})` : undefined}
      onMouseDown={(e) => { e.stopPropagation(); onMD(b.id, e); }}
      style={{ cursor: b.locked ? "not-allowed" : "move" }}>

      {/* White backing for contrast on aerial photos */}
      <rect x={x} y={y} width={w} height={h} fill="white" opacity={0.85}
        rx={isV ? w*0.12 : 2} />
      <rect x={x} y={y} width={w} height={h} fill={lc.f} opacity={b.opacity ?? 1.0}
        stroke={selected ? C.accent : multiSel ? "#3B82F6" : lc.s}
        strokeWidth={isSel ? 3 : isV ? 2 : 1.5} rx={isV ? w*0.12 : 2} />

      {/* Warehouse stramien */}
      {b.type === "warehouse" && b.params?.baysX > 1 && (
        <g opacity={0.35}>
          {Array.from({ length: b.params.baysX - 1 }).map((_, i) => (
            <line key={`x${i}`} x1={x+((i+1)*w)/b.params.baysX} y1={y}
              x2={x+((i+1)*w)/b.params.baysX} y2={y+h}
              stroke={C.primary} strokeWidth={0.6} strokeDasharray="4,3" />
          ))}
          {Array.from({ length: (b.params.baysY || 1) - 1 }).map((_, i) => (
            <line key={`y${i}`} x1={x} y1={y+((i+1)*h)/(b.params.baysY||1)}
              x2={x+w} y2={y+((i+1)*h)/(b.params.baysY||1)}
              stroke={C.primary} strokeWidth={0.6} strokeDasharray="4,3" />
          ))}
          {Array.from({ length: b.params.baysX + 1 }).map((_, ix) =>
            Array.from({ length: (b.params.baysY || 1) + 1 }).map((_, iy) => (
              <circle key={`c${ix}${iy}`} cx={x+(ix*w)/b.params.baysX}
                cy={y+(iy*h)/(b.params.baysY||1)} r={2} fill={C.primary} opacity={0.45} />
            ))
          )}
        </g>
      )}

      {/* Docks */}
      {b.type === "docks" && b.params?.count > 0 &&
        Array.from({ length: b.params.count }).map((_, i) => {
          const dh = h / b.params.count;
          return <rect key={i} x={x+2} y={y+i*dh+1} width={w-4} height={dh-2}
            fill="none" stroke={lc.s} strokeWidth={0.7} rx={1} />;
        })
      }

      {/* Parking lines — dual row with road */}
      {(b.type === "parking_car" || b.type === "parking_truck") && (() => {
        const sw = (b.params?.sw || 2.5) * sc;
        const sl = (b.params?.sl || 5.0) * sc;
        const roadW = (b.params?.roadW || 6.0) * sc;
        const rows = b.params?.rows || 2;
        const slotsPerRow = Math.max(1, Math.floor(w / sw));
        const totalSlots = slotsPerRow * rows;
        const topRowH = sl;
        const bottomRowY = rows >= 2 ? y + h - sl : y;
        return (
          <g opacity={0.35}>
            {/* Top row slots */}
            {Array.from({ length: Math.min(slotsPerRow, 60) }).map((_, i) => (
              <rect key={`t${i}`} x={x + i * sw + 0.5} y={y + 0.5} width={sw - 1} height={topRowH - 1}
                fill="none" stroke={lc.s} strokeWidth={0.5} />
            ))}
            {/* Road stripe in middle */}
            {rows >= 2 && (
              <g>
                <line x1={x} y1={y + topRowH + roadW/2} x2={x + w} y2={y + topRowH + roadW/2}
                  stroke={lc.s} strokeWidth={1} strokeDasharray="6,4" opacity={0.6} />
              </g>
            )}
            {/* Bottom row slots */}
            {rows >= 2 && Array.from({ length: Math.min(slotsPerRow, 60) }).map((_, i) => (
              <rect key={`b${i}`} x={x + i * sw + 0.5} y={bottomRowY + 0.5} width={sw - 1} height={sl - 1}
                fill="none" stroke={lc.s} strokeWidth={0.5} />
            ))}
            {/* Capacity label */}
            {w > 40 && (
              <text x={cx} y={rows >= 2 ? y + topRowH + roadW/2 : cy} textAnchor="middle" dominantBaseline="middle"
                fill={lc.s} fontSize={Math.min(9, w/8)} fontWeight="700" opacity={0.8}>
                {totalSlots} pp
              </text>
            )}
          </g>
        );
      })()}

      {/* Vehicle details */}
      {isV && (
        <g opacity={0.5}>
          {b.type === "car" && <>
            <rect x={x+w*0.15} y={y+h*0.12} width={w*0.7} height={h*0.22}
              rx={w*0.08} fill="none" stroke={lc.s} strokeWidth={0.7} />
            <rect x={x+w*0.15} y={y+h*0.6} width={w*0.7} height={h*0.22}
              rx={w*0.08} fill="none" stroke={lc.s} strokeWidth={0.7} />
          </>}
          {(b.type === "truck" || b.type === "trailer") && <>
            <rect x={x+1} y={y+1} width={w-2} height={h*0.18} rx={2}
              fill={lc.s} fillOpacity={0.25} />
            <line x1={x} y1={y+h*0.22} x2={x+w} y2={y+h*0.22}
              stroke={lc.s} strokeWidth={0.8} />
          </>}
        </g>
      )}

      {/* Turning radius */}
      {isV && b.params?.showTurn && b.params?.turnR > 0 && (
        <circle cx={cx} cy={cy} r={b.params.turnR*sc}
          fill="none" stroke={C.warn} strokeWidth={1.5} strokeDasharray="6,4" opacity={0.5} />
      )}

      {/* Labels */}
      {w > 20 && h > 12 && !isV && (
        <g style={{ pointerEvents:"none" }}>
          <text x={cx} y={cy-4} textAnchor="middle" dominantBaseline="middle"
            fill={lc.tx || "#ffffff"} fontSize={Math.min(12, w/6)} fontWeight="700"
            fontFamily="'Inter',sans-serif"
            style={{ textShadow:"0 1px 3px rgba(0,0,0,0.4)" }}>{t?.label?.toUpperCase()}</text>
          {b.w*b.h > 100 && (
            <text x={cx} y={cy+10} textAnchor="middle" dominantBaseline="middle"
              fill="rgba(255,255,255,0.85)" fontSize={Math.min(10, w/8)} fontFamily="'Inter',sans-serif"
              style={{ textShadow:"0 1px 2px rgba(0,0,0,0.3)" }}>
              {b.w*b.h >= 1000 ? `${(b.w*b.h/1000).toFixed(1)}k` : (b.w*b.h).toFixed(0)} m²
            </text>
          )}
        </g>
      )}
      {isV && w > 8 && (
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
          fill={lc.tx || lc.s} fontSize={Math.min(10, w/2)} fontWeight="700"
          style={{ pointerEvents:"none", textShadow:"0 1px 2px rgba(0,0,0,0.3)" }}>{t.icon}</text>
      )}

      {/* Selection UI */}
      {selected && (
        <g data-ui="selection">
          <rect x={x+w/2-18} y={y-18} width={36} height={14} rx={3} fill={C.primary} />
          <text x={x+w/2} y={y-9.5} textAnchor="middle" dominantBaseline="middle"
            fill="white" fontSize={8} fontWeight="600" fontFamily="'Inter',sans-serif">
            {b.w.toFixed(1)}m</text>
          <rect x={x-36} y={y+h/2-7} width={32} height={14} rx={3} fill={C.primary} />
          <text x={x-20} y={y+h/2+1} textAnchor="middle" dominantBaseline="middle"
            fill="white" fontSize={8} fontWeight="600" fontFamily="'Inter',sans-serif">
            {b.h.toFixed(1)}m</text>
          <rect x={x+w-5} y={y+h-5} width={10} height={10} fill="white"
            stroke={C.accent} strokeWidth={2} rx={2} style={{ cursor:"nwse-resize" }}
            onMouseDown={(e) => {
              e.stopPropagation(); e.preventDefault();
              if (onResizeStart) onResizeStart(b.id, e);
            }} />
          {b.locked && <text x={x+8} y={y+14} fontSize={10}>🔒</text>}
          {/* Midpoint handles on rect edges for vertex insertion */}
          {!hasPoly && (() => {
            const midpoints = [
              { mx: b.x + b.w/2, my: b.y, afterIdx: 0 },
              { mx: b.x + b.w, my: b.y + b.h/2, afterIdx: 1 },
              { mx: b.x + b.w/2, my: b.y + b.h, afterIdx: 2 },
              { mx: b.x, my: b.y + b.h/2, afterIdx: 3 },
            ];
            return midpoints.map((mp, i) => (
              <circle key={`rmid${i}`} cx={mp.mx * sc} cy={mp.my * sc} r={3}
                fill={C.accent} fillOpacity={0.6} stroke={C.accentMid} strokeWidth={1} strokeDasharray="2,2"
                style={{ cursor:"crosshair" }}
                onMouseDown={(e) => {
                  e.stopPropagation(); e.preventDefault();
                  const corners = [
                    [b.x, b.y], [b.x+b.w, b.y], [b.x+b.w, b.y+b.h], [b.x, b.y+b.h]
                  ];
                  const newPoly = [...corners];
                  newPoly.splice(mp.afterIdx + 1, 0, [mp.mx, mp.my]);
                  window.__rectToPolyAdd = { blockId: b.id, newPoly };
                }} />
            ));
          })()}
        </g>
      )}
    </g>
  );
}

// ============================================================================
// BAG OVERLAY
// ============================================================================
function BAGOverlay({ panden, rdCenter, viewport, sc }) {
  if (!panden?.length || !rdCenter) return null;
  const halfV = viewport / 2;
  const toLocal = (rdx, rdy) => ({
    x: (rdx - rdCenter.x + halfV),
    y: (viewport - (rdy - rdCenter.y + halfV)),
  });
  return (
    <g opacity={0.7}>
      {panden.map((p) => {
        const pts = p.coords.map((c) => toLocal(c.x, c.y));
        const svgPts = pts.map((pt) => `${pt.x*sc},${pt.y*sc}`).join(" ");
        return <polygon key={p.id} points={svgPts} fill={C.bag} stroke={C.bagS}
          strokeWidth={1} opacity={0.6} />;
      })}
    </g>
  );
}

// ============================================================================
// KAVEL DISPLAY + EDITOR
// ============================================================================
function KavelDisplay({ kavel, sc, editMode, onAddPoint, onRemovePoint }) {
  if (!kavel || kavel.length < 2) return null;
  const pts = kavel.map((p) => `${p[0]*sc},${p[1]*sc}`).join(" ");
  // Compute centroid for label
  const cx = kavel.reduce((s, p) => s + p[0], 0) / kavel.length;
  const cy = kavel.reduce((s, p) => s + p[1], 0) / kavel.length;
  return (
    <g>
      <polygon points={pts} fill="rgba(0,200,83,0.08)"
        stroke="#02714D"
        strokeWidth={3}
        strokeDasharray={editMode ? "" : "0"} />
      {/* Side lengths */}
      {kavel.length >= 3 && kavel.map((p, i) => {
        const next = kavel[(i+1) % kavel.length];
        const mx = (p[0]+next[0]) / 2, my = (p[1]+next[1]) / 2;
        const dist = Math.sqrt((next[0]-p[0])**2 + (next[1]-p[1])**2).toFixed(1);
        const angle = Math.atan2((next[1]-p[1])*sc, (next[0]-p[0])*sc) * 180 / Math.PI;
        return (
          <g key={`len${i}`}>
            <rect x={mx*sc-18} y={my*sc-8} width={36} height={14} rx={3}
              fill="white" stroke="#02714D" strokeWidth={0.8} opacity={0.9} />
            <text x={mx*sc} y={my*sc+3} textAnchor="middle"
              fill="#02714D" fontSize={8} fontWeight="700" style={{ userSelect:"none" }}>
              {dist}m
            </text>
          </g>
        );
      })}
      {/* KAVEL label */}
      {kavel.length >= 3 && !editMode && (
        <text x={cx*sc} y={cy*sc} textAnchor="middle" dominantBaseline="central"
          fill="#02714D" fontSize={11} fontWeight="800" opacity={0.5}
          style={{ userSelect:"none", letterSpacing:"2px" }}>KAVEL</text>
      )}
      {kavel.map((p, i) => (
        <g key={i}>
          <circle cx={p[0]*sc} cy={p[1]*sc} r={editMode ? 7 : 5}
            fill={editMode ? C.accent : "white"}
            stroke={editMode ? "white" : "#02714D"} strokeWidth={2}
            style={{ cursor: editMode ? "grab" : "default" }}
            onMouseDown={editMode ? (e) => {
              e.stopPropagation();
              window.__kavelDrag = { idx:i, mx:e.clientX, my:e.clientY, ox:p[0], oy:p[1] };
            } : undefined}
            onContextMenu={editMode ? (e) => {
              e.preventDefault(); e.stopPropagation(); onRemovePoint(i);
            } : undefined} />
          {editMode && (
            <text x={p[0]*sc} y={p[1]*sc-12} textAnchor="middle"
              fill={C.accent} fontSize={8} fontWeight="700">{i+1}</text>
          )}
        </g>
      ))}
      {editMode && kavel.map((p, i) => {
        const next = kavel[(i+1) % kavel.length];
        const edgeLen = Math.hypot(next[0]-p[0], next[1]-p[1]);
        if (edgeLen < 3) return null;
        const mx = (p[0]+next[0]) / 2, my = (p[1]+next[1]) / 2;
        return (
          <circle key={`m${i}`} cx={mx*sc} cy={my*sc} r={3}
            fill={C.accent} fillOpacity={0.6} stroke={C.accentMid} strokeWidth={1} strokeDasharray="2,2"
            style={{ cursor:"pointer" }}
            onClick={(e) => { e.stopPropagation(); onAddPoint(i+1, mx, my); }} />
        );
      })}
    </g>
  );
}

// ============================================================================
// CONTEXT MENU
// ============================================================================
function CtxMenu({ x, y, items, onClose }) {
  useEffect(() => {
    const h = () => onClose();
    window.addEventListener("click", h);
    return () => window.removeEventListener("click", h);
  }, [onClose]);
  return (
    <div style={{ position:"fixed", left:x, top:y, background:"white",
      border:`1px solid ${C.border}`, borderRadius:8,
      boxShadow:"0 8px 30px rgba(0,0,0,0.12)", padding:"4px 0", zIndex:999, minWidth:180 }}
      onClick={(e) => e.stopPropagation()}>
      {items.map((it, i) => it.div ? (
        <div key={i} style={{ height:1, background:C.border, margin:"4px 0" }} />
      ) : (
        <button key={i} onClick={() => { it.fn(); onClose(); }} disabled={it.off}
          style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12,
            width:"100%", padding:"7px 14px", border:"none",
            cursor: it.off ? "default" : "pointer",
            background:"transparent", fontSize:11, fontFamily:"inherit",
            color: it.off ? C.textMut : it.d ? C.err : C.text,
            opacity: it.off ? 0.5 : 1 }}
          onMouseEnter={(e) => { if (!it.off) e.currentTarget.style.background = C.surfAlt; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
          <span>{it.ic} {it.l}</span>
          {it.sc && <span style={{ fontSize:9, color:C.textMut }}>{it.sc}</span>}
        </button>
      ))}
    </div>
  );
}

// ============================================================================
// MAIN EDITOR COMPONENT
// ============================================================================
const DEFAULT_KAVEL = [[10,10],[10,250],[220,250],[240,230],[230,50],[200,10]];

export default function SitePlannerEditor() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // ---- Mobile state ----
  const [mobileTab, setMobileTab] = useState(null); // null | "blocks" | "map" | "tools" | "props" | "more"
  const touchStartRef = useRef(null);
  const pinchStartRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);

  // ---- Supabase state ----
  const [project, setProject] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [actSc, setActSc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);

  // ---- Editor state ----
  const [h, hD] = useReducer(hR, { past:[], now:[], future:[] });
  const blocks = h.now;
  const commit = (d, label) => hD({ type:"P", d: typeof d === "function" ? d(blocks) : d, label });
  const silent = (d) => hD({ type:"S", d });
  const dragStartRef = useRef(null);
  const [imgDragMode, setImgDragMode] = useState(false);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [altHeld, setAltHeld] = useState(false);

  const [lines, setLines] = useState([]);
  const [sel, setSel] = useState(null);
  const [multiSel, setMultiSel] = useState([]);
  const [selLine, setSelLine] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x:50, y:30 });
  const [isPan, setIsPan] = useState(false);
  const [panSt, setPanSt] = useState(null);
  const [drag, setDrag] = useState(null);
  const [snapG, setSnapG] = useState([]);
  const [tool, setTool] = useState("select");
  const [lineType, setLineType] = useState("hekwerk");
  const [linePts, setLinePts] = useState([]);
  const [mPts, setMPts] = useState({ a:null, b:null });
  const [kavelEdit, setKavelEdit] = useState(false);
  const [kavelDrawPts, setKavelDrawPts] = useState([]);
  const [freeformDrawPts, setFreeformDrawPts] = useState([]);
  const [freeformType, setFreeformType] = useState(null);
  const [ctxMenu, setCtxMenu] = useState(null);
  const [leftTab, setLeftTab] = useState("blocks");
  const [layers, setLayers] = useState({ gebouw:true, logistiek:true, parking:true, terrein:true, voertuigen:true, lijnen:true, bag:true });
  const [mapCfg, setMapCfg] = useState({ rdX:0, rdY:0, viewport:300, layer:"luchtfoto", opacity:0.6, imgData:null, imgUrl:null, imgW:300, rotation:0, kadasterOverlay:false, imgX:0, imgY:0, imgRotation:0, imgOpacity:0.6, imgScale:1.0 });
  const [searchQ, setSearchQ] = useState("");
  const [searchRes, setSearchRes] = useState([]);
  const [searching, setSearching] = useState(false);
  const [bagPanden, setBagPanden] = useState([]);
  const [bagLoading, setBagLoading] = useState(false);
  const [aiRenderOpen, setAiRenderOpen] = useState(false);
  const [aiRenderCamera, setAiRenderCamera] = useState("isometric");
  const [aiRenderStyle, setAiRenderStyle] = useState("photorealistic");
  const [aiRenderModel, setAiRenderModel] = useState("google/gemini-3.1-flash-image-preview");
  const [aiRendering, setAiRendering] = useState(false);
  const [aiRenderResult, setAiRenderResult] = useState(null);
  const [aiRenderError, setAiRenderError] = useState(null);
  const [pendingBlock, setPendingBlock] = useState(null);
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [renderHistory, setRenderHistory] = useState([]);
  const [historyPreview, setHistoryPreview] = useState(null);
  const [renderStatus, setRenderStatus] = useState(null); // { phase: 'queued'|'preparing'|'generating'|'fallback'|'finalizing', progress: 0-100, message: '' }
  const [compareSelection, setCompareSelection] = useState([]); // array of render history indices (max 2)
  const [openaiSize, setOpenaiSize] = useState("1536x1024");
  const [openaiQuality, setOpenaiQuality] = useState("high");
  const [openaiHealth, setOpenaiHealth] = useState(null); // { ok, message, ... }
  const [testingOpenai, setTestingOpenai] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [show3DView, setShow3DView] = useState(false);
  const [screenshot3D, setScreenshot3D] = useState(null);
  const svgRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const [editingEdge, setEditingEdge] = useState(null); // { blockId, edgeIdx }
  const idC = useRef(500);
  const sc = 4 * zoom;

  const normalizeBlockIds = useCallback((input = []) => {
    const seen = new Set();
    return input.map((block) => {
      const rawId = typeof block?.id === "string" ? block.id : "";
      const parsed = /^b(\d+)$/.exec(rawId);
      if (parsed) idC.current = Math.max(idC.current, Number(parsed[1]));

      let nextId = rawId;
      if (!nextId || seen.has(nextId)) {
        nextId = `b${++idC.current}`;
      }

      seen.add(nextId);
      return nextId === rawId ? block : { ...block, id: nextId };
    });
  }, []);

  // ---- Load project + scenarios from Supabase ----
  useEffect(() => {
    if (!projectId) return;
    const load = async () => {
      setLoading(true);
      // Load project
      const { data: proj } = await supabase
        .from("site_planner_projects")
        .select("*")
        .eq("id", projectId)
        .single();
      if (proj) {
        setProject(proj);
        const mc = proj.map_config || {};
        setMapCfg((c) => ({
          ...c,
          rdX: mc.rdX || proj.rd_x || 0,
          rdY: mc.rdY || proj.rd_y || 0,
          viewport: mc.viewport || 300,
          layer: mc.layer || "luchtfoto",
          opacity: mc.opacity ?? 0.6,
          rotation: mc.rotation || 0,
          kadasterOverlay: mc.kadasterOverlay || false,
          imgUrl: mc.imgUrl || null,
          imgW: mc.imgW || 300,
          imgX: mc.imgX || 0,
          imgY: mc.imgY || 0,
          imgRotation: mc.imgRotation || 0,
          imgOpacity: mc.imgOpacity ?? 0.6,
          imgScale: mc.imgScale ?? 1.0,
        }));
      }

      // Load scenarios
      const { data: scens } = await supabase
        .from("site_planner_scenarios")
        .select("*")
        .eq("project_id", projectId)
        .order("created_at");

      if (scens && scens.length > 0) {
        const normalizedScens = scens.map((scn) => ({
          ...scn,
          blocks: normalizeBlockIds(scn.blocks || []),
        }));
        setScenarios(normalizedScens);
        setActSc(normalizedScens[0].id);
        hD({ type: "INIT", d: normalizedScens[0].blocks || [] });
        setLines(normalizedScens[0].lines || []);
      } else {
        // Create first scenario
        const { data: newSc } = await supabase
          .from("site_planner_scenarios")
          .insert({ project_id: projectId, name: "Scenario A", blocks: [], lines: [] })
          .select()
          .single();
        if (newSc) {
          setScenarios([newSc]);
          setActSc(newSc.id);
          hD({ type: "INIT", d: [] });
        }
      }
      setLoading(false);
    };
    load();
  }, [projectId, normalizeBlockIds]);

  // ---- Auto-save to Supabase (debounced) ----
  const autoSave = useCallback(() => {
    if (!actSc) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      const kpisData = calcKpis(blocks, kavel);
      await supabase
        .from("site_planner_scenarios")
        .update({ blocks, lines, kpis: kpisData, updated_at: new Date().toISOString() })
        .eq("id", actSc);
      // Save kavel + map_config to project
      if (project) {
        const { imgData, ...mcSave } = mapCfg;
        await supabase
          .from("site_planner_projects")
          .update({ kavel_boundary: kavel, map_config: mcSave, updated_at: new Date().toISOString() })
          .eq("id", project.id);
      }
      setSaving(false);
    }, 2000);
  }, [actSc, blocks, lines, project, mapCfg]);

  useEffect(() => { autoSave(); }, [blocks, lines, mapCfg, autoSave]);

  // ---- Scroll zoom via non-passive wheel listener (retry until ref available) ----
  useEffect(() => {
    let cancelled = false;
    let boundEl = null;
    let boundHandler = null;
    const attach = () => {
      const el = canvasContainerRef.current;
      if (!el) { if (!cancelled) requestAnimationFrame(attach); return; }
      const handler = (e) => {
        e.preventDefault();
        setZoom((z) => {
          const factor = e.deltaY > 0 ? 0.88 : 1.14;
          const next = z * factor;
          return Math.max(0.15, Math.min(8, next));
        });
      };
      el.addEventListener("wheel", handler, { passive: false });
      boundEl = el;
      boundHandler = handler;
    };
    attach();
    return () => {
      cancelled = true;
      if (boundEl && boundHandler) boundEl.removeEventListener("wheel", boundHandler);
    };
  }, []);

  // ---- Switch scenario ----
  const switchScenario = useCallback((scId) => {
    const scn = scenarios.find((s) => s.id === scId);
    if (scn) {
      const normalizedBlocks = normalizeBlockIds(scn.blocks || []);
      const hadDuplicateIds = normalizedBlocks.some((b, i) => b.id !== (scn.blocks?.[i]?.id));
      setActSc(scId);
      hD({ type: "INIT", d: normalizedBlocks });
      setLines(scn.lines || []);
      setSel(null);
      setMultiSel([]);
      if (hadDuplicateIds) {
        setScenarios((prev) => prev.map((s) => s.id === scId ? { ...s, blocks: normalizedBlocks } : s));
      }
    }
  }, [scenarios, normalizeBlockIds]);

  // ---- Scenario helpers ----
  const scenario = scenarios.find((s) => s.id === actSc);
  const kavel = project?.kavel_boundary || DEFAULT_KAVEL;

  const setKavel = useCallback((fn) => {
    const newKavel = typeof fn === "function" ? fn(kavel) : fn;
    setProject((p) => p ? { ...p, kavel_boundary: newKavel } : p);
  }, [kavel]);

  const addScenario = async () => {
    const name = `Scenario ${String.fromCharCode(65 + scenarios.length)}`;
    const { data } = await supabase
      .from("site_planner_scenarios")
      .insert({ project_id: projectId, name, blocks: [], lines: [] })
      .select()
      .single();
    if (data) {
      setScenarios((p) => [...p, data]);
      switchScenario(data.id);
    }
  };

  const dupScenario = async () => {
    if (!scenario) return;
    const name = `${scenario.name} (kopie)`;
    const newBlocks = blocks.map((b) => ({ ...b, id: `b${++idC.current}`, params: { ...b.params } }));
    const { data } = await supabase
      .from("site_planner_scenarios")
      .insert({ project_id: projectId, name, blocks: newBlocks, lines: [...lines] })
      .select()
      .single();
    if (data) {
      setScenarios((p) => [...p, data]);
      switchScenario(data.id);
    }
  };

  // ---- KPI calculation ----
  function calcKpis(blks, kv) {
    const bt = (t) => blks.filter((b) => b.type === t);
    const sa = (a) => a.reduce((s, b) => s + b.w * b.h, 0);
    const sp = (a, p) => a.reduce((s, b) => s + (b.params?.[p] || 0), 0);
    const wh = sa(bt("warehouse")), exp = sa(bt("expedition")), off = sa(bt("office"));
    let ka = 0;
    if (kv?.length >= 3) {
      for (let i = 0; i < kv.length; i++) {
        const j = (i+1) % kv.length;
        ka += kv[i][0]*kv[j][1] - kv[j][0]*kv[i][1];
      }
      ka = Math.abs(ka) / 2;
    }
    return {
      wh, exp, off, bvo: wh+exp+off,
      pCar: sp(bt("parking_car"), "spaces"),
      pTruck: sp(bt("parking_truck"), "spaces"),
      docks: sp(bt("docks"), "count"),
      green: sa(bt("green")),
      kavelArea: ka,
      bebouwingsPct: ka > 0 ? ((wh+exp+off)/ka*100).toFixed(1) : 0,
    };
  }

  const kpis = useMemo(() => calcKpis(blocks, kavel), [blocks, kavel]);

  // ---- SVG coords ----
  const toSvg = useCallback((e) => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r) return { x:0, y:0 };
    return { x: sg((e.clientX-r.left-pan.x)/sc), y: sg((e.clientY-r.top-pan.y)/sc) };
  }, [pan, sc]);

  // ---- Search & BAG ----
  const doSearch = async () => {
    if (!searchQ.trim()) return;
    setSearching(true);
    setSearchRes(await geocode(searchQ));
    setSearching(false);
  };
  const pickLoc = (loc) => {
    setMapCfg((c) => ({ ...c, rdX: loc.rdX, rdY: loc.rdY }));
    setSearchRes([]);
    setSearchQ(loc.label);
    // Save to project
    if (project) {
      const { imgData, ...mcSave } = { ...mapCfg, rdX: loc.rdX, rdY: loc.rdY };
      supabase.from("site_planner_projects")
        .update({ rd_x: loc.rdX, rd_y: loc.rdY, address: loc.label, map_config: mcSave })
        .eq("id", project.id);
    }
  };
  const loadBAG = async () => {
    if (!mapCfg.rdX) return;
    setBagLoading(true);
    setBagPanden(await fetchBAGPanden(mapCfg.rdX, mapCfg.rdY, mapCfg.viewport/2));
    setBagLoading(false);
  };

  // ---- Drag / Pan / Resize handlers ----
  const handleMD = useCallback((id, e) => {
    if (pendingBlock) return; // Don't select blocks during placement mode
    if (tool !== "select" || spaceHeld) return;
    e.stopPropagation();
    const b = blocks.find((x) => x.id === id);
    if (!b || b.locked) { setSel(id); setMultiSel([]); return; }
    if (e.shiftKey) {
      // Add current sel to multiSel if not already there, then toggle clicked block
      setMultiSel((ms) => {
        let next = [...ms];
        if (sel && !next.includes(sel)) next.push(sel);
        if (next.includes(id)) next = next.filter((x) => x !== id);
        else next.push(id);
        return next;
      });
      setSel(null);
      return;
    }
    setSel(id); setMultiSel([]);
    dragStartRef.current = blocks;
    setDrag({ id, mx:e.clientX, my:e.clientY, bx:b.x, by:b.y });
  }, [blocks, tool, multiSel, sel, spaceHeld]);

  const handleMM = useCallback((e) => {
    if (drag) {
      const dx = (e.clientX-drag.mx)/sc, dy = (e.clientY-drag.my)/sc;
      const mv = blocks.find((b) => b.id === drag.id);
      if (!mv) return;
      const tent = { ...mv, x: sg(drag.bx+dx), y: sg(drag.by+dy) };
      if (e.altKey) {
        setSnapG([]);
        const deltaX = tent.x - mv.x, deltaY = tent.y - mv.y;
        silent((p) => p.map((b) => {
          if (b.id !== drag.id) return b;
          const update = { ...b, x: tent.x, y: tent.y };
          if (b.polygon) {
            update.polygon = b.polygon.map(pt => [pt[0] + deltaX, pt[1] + deltaY]);
          }
          return update;
        }));
        return;
      }
      const { x, y, guides } = calcSnap(tent, blocks);
      setSnapG(guides);
      const deltaX = x - mv.x, deltaY = y - mv.y;
      silent((p) => p.map((b) => {
        if (b.id !== drag.id) return b;
        const update = { ...b, x, y };
        if (b.polygon) {
          update.polygon = b.polygon.map(pt => [pt[0] + deltaX, pt[1] + deltaY]);
        }
        return update;
      }));
    } else if (window.__rsz) {
      const ri = window.__rsz;
      silent((p) => p.map((b) => b.id===ri.id ? {
        ...b,
        w: Math.max(1, sg(ri.bw+(e.clientX-ri.mx)/sc)),
        h: Math.max(1, sg(ri.bh+(e.clientY-ri.my)/sc)),
      } : b));
    } else if (window.__polyDrag) {
      const pd = window.__polyDrag;
      const rawX = sg(pd.ox+(e.clientX-pd.mx)/sc);
      const rawY = sg(pd.oy+(e.clientY-pd.my)/sc);
      let nx, ny;
      if (e.altKey) {
        nx = rawX; ny = rawY; setSnapG([]);
      } else {
        const snap = snapVertex(rawX, rawY, pd.blockId, blocks, kavel);
        nx = snap.x; ny = snap.y;
        if (snap.snapped) setSnapG(snap.guides); else setSnapG([]);
      }
      silent((p) => p.map((b) => {
        if (b.id !== pd.blockId) return b;
        const newPoly = b.polygon.map((pt, i) => i === pd.vertexIdx ? [nx, ny] : pt);
        const xs = newPoly.map(p=>p[0]), ys = newPoly.map(p=>p[1]);
        return { ...b, polygon: newPoly, x:Math.min(...xs), y:Math.min(...ys), w:Math.max(...xs)-Math.min(...xs), h:Math.max(...ys)-Math.min(...ys) };
      }));
    } else if (window.__imgDrag) {
      const id = window.__imgDrag;
      const nx = id.ox + (e.clientX - id.mx) / sc;
      const ny = id.oy + (e.clientY - id.my) / sc;
      setMapCfg((c) => ({ ...c, imgX: Math.round(nx), imgY: Math.round(ny) }));
    } else if (window.__kavelDrag) {
      const kd = window.__kavelDrag;
      const rawX = sg(kd.ox+(e.clientX-kd.mx)/sc);
      const rawY = sg(kd.oy+(e.clientY-kd.my)/sc);
      let nx, ny;
      if (e.altKey) {
        nx = rawX; ny = rawY; setSnapG([]);
      } else {
        const snap = snapVertex(rawX, rawY, null, blocks, null);
        nx = snap.x; ny = snap.y;
        if (snap.snapped) setSnapG(snap.guides); else setSnapG([]);
      }
      setKavel((k) => { const n = [...k]; n[kd.idx] = [nx, ny]; return n; });
    } else if (isPan && panSt) {
      setPan((p) => ({ x: p.x+e.clientX-panSt.x, y: p.y+e.clientY-panSt.y }));
      setPanSt({ x: e.clientX, y: e.clientY });
    }
  }, [drag, isPan, panSt, sc, blocks]);

  const handleMU = useCallback(() => {
    if (drag) {
      const base = dragStartRef.current;
      if (base) { hD({ type:"PB", base, d: blocks, label:"Blok verplaatst" }); }
      else { commit(blocks, "Blok verplaatst"); }
      dragStartRef.current = null;
      setSnapG([]);
    }
    if (window.__rsz) {
      const base = dragStartRef.current;
      if (base) { hD({ type:"PB", base, d: blocks, label:"Formaat gewijzigd" }); }
      else { commit(blocks, "Formaat gewijzigd"); }
      dragStartRef.current = null;
      window.__rsz = null;
    }
    if (window.__polyDrag) {
      commit(blocks, "Hoekpunt verplaatst");
      window.__polyDrag = null;
    }
    if (window.__polyRemove) {
      const pr = window.__polyRemove;
      commit((p) => p.map((b) => b.id === pr.blockId ? {
        ...b, polygon: pr.newPoly, x:pr.minX, y:pr.minY, w:pr.maxX-pr.minX, h:pr.maxY-pr.minY
      } : b), "Hoekpunt verwijderd");
      window.__polyRemove = null;
    }
    if (window.__polyAdd) {
      const pa = window.__polyAdd;
      commit((p) => p.map((b) => {
        if (b.id !== pa.blockId) return b;
        const newPoly = [...b.polygon];
        newPoly.splice(pa.afterIdx + 1, 0, [pa.mx, pa.my]);
        const xs = newPoly.map(p=>p[0]), ys = newPoly.map(p=>p[1]);
        return { ...b, polygon: newPoly, x:Math.min(...xs), y:Math.min(...ys), w:Math.max(...xs)-Math.min(...xs), h:Math.max(...ys)-Math.min(...ys) };
      }), "Hoekpunt toegevoegd");
      window.__polyAdd = null;
    }
    if (window.__rectToPolyAdd) {
      const rp = window.__rectToPolyAdd;
      commit((p) => p.map((b) => {
        if (b.id !== rp.blockId) return b;
        const xs = rp.newPoly.map(p=>p[0]), ys = rp.newPoly.map(p=>p[1]);
        return { ...b, polygon: rp.newPoly, x:Math.min(...xs), y:Math.min(...ys), w:Math.max(...xs)-Math.min(...xs), h:Math.max(...ys)-Math.min(...ys) };
      }), "Omgezet + hoekpunt");
      window.__rectToPolyAdd = null;
    }
    if (window.__imgDrag) window.__imgDrag = null;
    if (window.__kavelDrag) { commit(blocks, "Kavel verplaatst"); window.__kavelDrag = null; }
    setDrag(null); setIsPan(false); setPanSt(null);
  }, [drag, blocks]);

  // ---- Touch Handlers (mobile) ----
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      // Pinch-to-zoom start
      const d = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      pinchStartRef.current = { dist: d, zoom };
      if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
      return;
    }
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
    longPressFiredRef.current = false;

    // Long press → context menu
    longPressTimerRef.current = setTimeout(() => {
      if (!touchStartRef.current) return;
      longPressFiredRef.current = true;
      const p = toSvg({ clientX: touchStartRef.current.x, clientY: touchStartRef.current.y });
      const hit = blocks.find((b) => p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h);
      if (hit) setSel(hit.id);
      setCtxMenu({ x: touchStartRef.current.x, y: touchStartRef.current.y, bid: hit?.id || null });
    }, 500);

    // Start pan (or drag if on block)
    const target = e.target;
    if (!target.closest('[data-block-id]')) {
      setIsPan(true);
      setPanSt({ x: t.clientX, y: t.clientY });
    }
  }, [zoom, blocks, toSvg]);

  const handleTouchMove = useCallback((e) => {
    // Pinch-to-zoom
    if (e.touches.length === 2 && pinchStartRef.current) {
      e.preventDefault();
      const d = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const ratio = d / pinchStartRef.current.dist;
      setZoom(Math.max(0.15, Math.min(8, pinchStartRef.current.zoom * ratio)));
      return;
    }
    if (e.touches.length !== 1) return;
    const t = e.touches[0];

    // Cancel long press on move
    if (touchStartRef.current && longPressTimerRef.current) {
      const dx = Math.abs(t.clientX - touchStartRef.current.x);
      const dy = Math.abs(t.clientY - touchStartRef.current.y);
      if (dx > 8 || dy > 8) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    }

    // Pan
    if (isPan && panSt) {
      setPan((p) => ({ x: p.x + t.clientX - panSt.x, y: p.y + t.clientY - panSt.y }));
      setPanSt({ x: t.clientX, y: t.clientY });
    }

    // Forward to handleMM for drag operations
    handleMM({ clientX: t.clientX, clientY: t.clientY, altKey: false, target: e.target });
  }, [isPan, panSt, handleMM]);

  const handleClickRef = useRef(null);

  const handleTouchEnd = useCallback((e) => {
    pinchStartRef.current = null;
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    // Tap → click
    if (touchStartRef.current && !longPressFiredRef.current && e.changedTouches.length === 1) {
      const t = e.changedTouches[0];
      const dx = Math.abs(t.clientX - touchStartRef.current.x);
      const dy = Math.abs(t.clientY - touchStartRef.current.y);
      const dt = Date.now() - touchStartRef.current.time;
      if (dx < 10 && dy < 10 && dt < 300) {
        // Simulate click via ref to avoid TDZ
        handleClickRef.current?.({ clientX: t.clientX, clientY: t.clientY, target: e.target, stopPropagation: () => {}, preventDefault: () => {} });
      }
    }

    touchStartRef.current = null;
    handleMU();
  }, [handleMU]);

  const handleCDown = (e) => {
    if (e.button === 2) return;
    if (pendingBlock) return; // Let handleClick handle placement
    setCtxMenu(null);
    // Middle mouse button or spacebar held = always pan
    if (e.button === 1 || spaceHeld) {
      e.preventDefault();
      setIsPan(true); setPanSt({ x:e.clientX, y:e.clientY });
      return;
    }
    if (tool === "select") {
      // Only clear selection if clicking empty canvas, not on a block
      if (!e.target.closest('[data-block-id]')) {
        setSel(null); setSelLine(null); setMultiSel([]);
      }
      setIsPan(true); setPanSt({ x:e.clientX, y:e.clientY });
    }
  };

  const handleClick = (e) => {
    const p = toSvg(e);

    // Click-to-place: place pending block at click position
    if (pendingBlock) {
      const t = getT(pendingBlock.type);
      if (t) {
        const id = `b${++idC.current}`;
        commit((prev) => [...prev, {
          id, type: pendingBlock.type,
          x: sg(p.x - t.dw / 2), y: sg(p.y - t.dh / 2),
          w: t.dw, h: t.dh, rotation: 0, locked: false,
          params: { ...t.params }
        }], `${t.label} geplaatst`);
        setSel(id); setMultiSel([]);
      }
      setPendingBlock(null);
      return;
    }

    if (tool === "measure") {
      if (!mPts.a) setMPts({ a:p, b:null });
      else if (!mPts.b) setMPts((m) => ({ ...m, b: p }));
      else setMPts({ a:p, b:null });
    } else if (tool === "line") {
      setLinePts((pr) => [...pr, p]);
    } else if (tool === "kavel") {
      if (kavelDrawPts.length >= 3) {
        const f = kavelDrawPts[0];
        const dist = Math.hypot((p.x - f.x) * sc, (p.y - f.y) * sc);
        if (dist < 18) { finishKavel(); return; }
      }
      // Snap new kavel point to existing geometry (Alt = no snap)
      if (e.altKey) {
        setKavelDrawPts((pr) => [...pr, { x: sg(p.x), y: sg(p.y) }]);
      } else {
        const snap = snapVertex(p.x, p.y, null, blocks, null);
        setKavelDrawPts((pr) => [...pr, { x: snap.x, y: snap.y }]);
      }
    } else if (tool === "freeform") {
      if (freeformDrawPts.length >= 3) {
        const f = freeformDrawPts[0];
        const dist = Math.hypot((p.x - f.x) * sc, (p.y - f.y) * sc);
        if (dist < 18) { finishFreeform(); return; }
      }
      // Snap new freeform point to existing geometry (Alt = no snap)
      if (e.altKey) {
        setFreeformDrawPts((pr) => [...pr, { x: sg(p.x), y: sg(p.y) }]);
      } else {
        const snap = snapVertex(p.x, p.y, null, blocks, kavel);
        setFreeformDrawPts((pr) => [...pr, { x: snap.x, y: snap.y }]);
      }
    }
  };
  handleClickRef.current = handleClick;

  const handleDblClick = (e) => {
    if (tool === "kavel" && kavelDrawPts.length >= 3) { finishKavel(); }
    else if (tool === "freeform" && freeformDrawPts.length >= 3) { finishFreeform(); }
    else if (tool === "line" && linePts.length >= 2) { finishLine(); }
  };

  const handleCtx = (e) => {
    e.preventDefault();
    const p = toSvg(e);
    const hit = blocks.find((b) => p.x>=b.x && p.x<=b.x+b.w && p.y>=b.y && p.y<=b.y+b.h);
    if (hit) setSel(hit.id);
    setCtxMenu({ x:e.clientX, y:e.clientY, bid: hit?.id || null });
  };

  const finishLine = () => {
    if (linePts.length >= 2) {
      setLines((p) => [...p, { id:`ln${++idC.current}`, lineType, points:[...linePts] }]);
    }
    setLinePts([]);
  };

  const finishKavel = () => {
    if (kavelDrawPts.length >= 3) {
      setKavel(kavelDrawPts.map((p) => [p.x, p.y]));
    }
    setKavelDrawPts([]); setTool("select");
  };

  const finishFreeform = () => {
    if (freeformDrawPts.length >= 3 && freeformType) {
      const id = `b${++idC.current}`;
      const t = getT(freeformType);
      const polygon = freeformDrawPts.map((p) => [p.x, p.y]);
      const xs = polygon.map(p => p[0]), ys = polygon.map(p => p[1]);
      const minX = Math.min(...xs), minY = Math.min(...ys);
      const maxX = Math.max(...xs), maxY = Math.max(...ys);
      commit((p) => [...p, {
        id, type:freeformType, x:minX, y:minY, w:maxX-minX, h:maxY-minY,
        rotation:0, locked:false, polygon, params:{ ...(t?.params || {}) }
      }], `${t?.label || "Blok"} (polygoon) toegevoegd`);
      setSel(id); setMultiSel([]);
    }
    setFreeformDrawPts([]); setFreeformType(null); setTool("select");
  };

  const convertToPolygon = (bid) => {
    commit((p) => p.map((b) => {
      if (b.id !== bid || b.polygon) return b;
      return { ...b, polygon: [[b.x,b.y],[b.x+b.w,b.y],[b.x+b.w,b.y+b.h],[b.x,b.y+b.h]] };
    }), "Omgezet naar vrije vorm");
  };

  const mergeBlocks = () => {
    if (multiSel.length < 2) return;
    const sels = blocks.filter((b) => multiSel.includes(b.id));
    const types = [...new Set(sels.map(b => b.type))];
    if (types.length !== 1) return;
    const type = types[0];
    const t = getT(type);
    // Collect all vertices
    const allPts = [];
    sels.forEach(b => {
      if (b.polygon) { b.polygon.forEach(p => allPts.push(p)); }
      else { allPts.push([b.x,b.y],[b.x+b.w,b.y],[b.x+b.w,b.y+b.h],[b.x,b.y+b.h]); }
    });
    // Convex hull
    const hull = convexHull(allPts);
    const xs = hull.map(p=>p[0]), ys = hull.map(p=>p[1]);
    const id = `b${++idC.current}`;
    commit((p) => [
      ...p.filter(b => !multiSel.includes(b.id)),
      { id, type, x:Math.min(...xs), y:Math.min(...ys), w:Math.max(...xs)-Math.min(...xs), h:Math.max(...ys)-Math.min(...ys),
        rotation:0, locked:false, polygon:hull, params:{ ...(t?.params || {}), ...(sels[0].params || {}) } }
    ], "Blokken samengevoegd");
    setSel(id); setMultiSel([]);
  };

  const startFreeform = (typeId) => {
    setFreeformType(typeId);
    setFreeformDrawPts([]);
    setTool("freeform");
  };

  // ---- Block operations ----
  const addBlock = (tid) => {
    const t = getT(tid); if (!t) return;
    // Enter placement mode — next click on canvas places the block
    setPendingBlock({ type: tid });
    setTool("select");
    setSel(null); setMultiSel([]);
  };
  const delBlock = () => { if (sel) { const b = blocks.find(x=>x.id===sel); commit((p) => p.filter((b) => b.id!==sel), `${getT(b?.type)?.label||"Blok"} verwijderd`); setSel(null); } };
  const dupBlock = () => {
    const b = blocks.find((x) => x.id===sel); if (!b) return;
    const id = `b${++idC.current}`;
    commit((p) => [...p, { ...b, id, x:b.x+3, y:b.y+3, params:{...b.params} }], `${getT(b.type)?.label||"Blok"} gedupliceerd`);
    setSel(id); setMultiSel([]);
  };
  const rotBlock = () => commit((p) => p.map((b) => b.id===sel ? { ...b, rotation:((b.rotation||0)+90)%360 } : b), "Geroteerd");
  const setBlockRotation = (deg) => commit((p) => p.map((b) => b.id===sel ? { ...b, rotation: Math.round(deg) % 360 } : b), "Rotatie aangepast");
  const lockBlock = () => commit((p) => p.map((b) => b.id===sel ? { ...b, locked:!b.locked } : b), "Lock gewijzigd");
  const toggleTurn = () => commit((p) => p.map((b) => b.id===sel ? { ...b, params:{...b.params, showTurn:b.params?.showTurn?0:1} } : b), "Draaistraal toggle");
  const updateP = (k, v) => commit((p) => p.map((b) => b.id===sel ? { ...b, params:{...b.params, [k]:parseFloat(v)||0} } : b), "Parameter gewijzigd");
  const updateB = (k, v) => commit((p) => p.map((b) => {
    if (b.id !== sel) return b;
    const nv = parseFloat(v) || 0;
    const upd = { ...b, [k]: nv };
    if (b.polygon && b.polygon.length >= 3) {
      if (k === "x") {
        const delta = nv - b.x;
        upd.polygon = b.polygon.map(pt => [pt[0] + delta, pt[1]]);
      } else if (k === "y") {
        const delta = nv - b.y;
        upd.polygon = b.polygon.map(pt => [pt[0], pt[1] + delta]);
      } else if (k === "w" && b.w > 0) {
        const scale = nv / b.w;
        upd.polygon = b.polygon.map(pt => [b.x + (pt[0] - b.x) * scale, pt[1]]);
      } else if (k === "h" && b.h > 0) {
        const scale = nv / b.h;
        upd.polygon = b.polygon.map(pt => [pt[0], b.y + (pt[1] - b.y) * scale]);
      }
    }
    return upd;
  }), "Afmeting gewijzigd");

  const alignBlocks = (dir) => {
    if (multiSel.length < 2) return;
    const sels = blocks.filter((b) => multiSel.includes(b.id));
    commit((p) => {
      let n = [...p];
      if (dir==="left") { const m=Math.min(...sels.map(b=>b.x)); n=n.map(b=>multiSel.includes(b.id)?{...b,x:m}:b); }
      if (dir==="right") { const m=Math.max(...sels.map(b=>b.x+b.w)); n=n.map(b=>multiSel.includes(b.id)?{...b,x:m-b.w}:b); }
      if (dir==="top") { const m=Math.min(...sels.map(b=>b.y)); n=n.map(b=>multiSel.includes(b.id)?{...b,y:m}:b); }
      if (dir==="bottom") { const m=Math.max(...sels.map(b=>b.y+b.h)); n=n.map(b=>multiSel.includes(b.id)?{...b,y:m-b.h}:b); }
      return n;
    }, `Blokken uitgelijnd (${dir})`);
  };

  // ---- Exports ----
  const [exportOpen, setExportOpen] = useState(false);
  const [exportBg, setExportBg] = useState(true);
  const exportRef = useRef(null);

  // Close export dropdown on outside click
  useEffect(() => {
    if (!exportOpen) return;
    const h = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [exportOpen]);

  const exportDXF = () => { dl(genDXF(blocks, lines, kavel), `${project?.name || "terreinplan"}.dxf`, "application/dxf"); setExportOpen(false); };

  // Helper: load logo as data URL for SVG exports
  const getLogoDataUrl = () => new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      c.getContext("2d").drawImage(img, 0, 0);
      resolve(c.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = greenvisionLogoPng;
  });

  const exportSVG = async () => {
    if (!svgRef.current) return;
    setExportOpen(false);
    const logoDataUrl = await getLogoDataUrl();
    const clean = await cleanSvgForExport(svgRef.current, {
      addTitleBlock: true, addLegend: true,
      projectName: project?.name, projectAddress: project?.address,
      kpis, blocks, kavel, logoDataUrl, sc, pan, includeBg: exportBg,
    });
    clean.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clean.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
    dl(new XMLSerializer().serializeToString(clean), `${project?.name || "terreinplan"}.svg`, "image/svg+xml");
  };

  const exportPNG = async () => {
    if (!svgRef.current) return;
    setExportOpen(false);
    try {
      const logoDataUrl = await getLogoDataUrl();
      const clean = await cleanSvgForExport(svgRef.current, {
        addTitleBlock: true, addLegend: true,
        projectName: project?.name, projectAddress: project?.address,
        kpis, blocks, kavel, logoDataUrl, sc, pan, includeBg: exportBg,
      });
      clean.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      clean.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
      const svgStr = new XMLSerializer().serializeToString(clean);
      const svgBlob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = () => {
        const scale = 3;
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth * scale;
        canvas.height = img.naturalHeight * scale;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#F7F9F8";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        canvas.toBlob((blob) => {
          if (!blob) return;
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `${project?.name || "terreinplan"}.png`;
          document.body.appendChild(a); a.click(); document.body.removeChild(a);
        }, "image/png");
      };
      img.src = url;
    } catch (err) { console.error("PNG export error:", err); }
  };

  const exportPDF = async () => {
    setExportOpen(false);
    try {
      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a3" });
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();
      const m = 12; // margin

      // Load logo for PDF header
      const logoDataUrl = await getLogoDataUrl();

      // Header bar
      doc.setFillColor(1, 67, 54);
      doc.rect(0, 0, pw, 22, "F");

      // Add logo to header
      let textStartX = m;
      if (logoDataUrl) {
        try { doc.addImage(logoDataUrl, "PNG", m, 2, 18, 18); textStartX = m + 22; } catch(e) {}
      }

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16); doc.setFont("helvetica", "bold");
      doc.text("GREENVISION — Terreinplan", textStartX, 14);
      doc.setFontSize(9); doc.setFont("helvetica", "normal");
      doc.setTextColor(200, 220, 210);
      doc.text(`${project?.name || ""} — ${project?.address || ""} — ${new Date().toLocaleDateString("nl-NL")}`, textStartX, 20);

      // KPI bar
      doc.setFillColor(247, 249, 248);
      doc.rect(0, 22, pw, 14, "F");
      doc.setDrawColor(220, 228, 224);
      doc.line(0, 36, pw, 36);
      const kpiItems = [
        ["Warehouse", kpis.wh > 0 ? `${(kpis.wh/1000).toFixed(1)}k m²` : "—"],
        ["Expeditie", kpis.exp > 0 ? `${kpis.exp.toFixed(0)} m²` : "—"],
        ["Docks", String(kpis.docks)], ["P auto", String(kpis.pCar)], ["P truck", String(kpis.pTruck)],
        ["Groen", kpis.green > 0 ? `${kpis.green.toFixed(0)} m²` : "—"],
        ["BVO", `${(kpis.bvo/1000).toFixed(1)}k m²`],
        ["Kavel", kpis.kavelArea > 0 ? `${(kpis.kavelArea/1000).toFixed(1)}k m²` : "—"],
        ["Bebouwing%", `${kpis.bebouwingsPct}%`],
      ];
      const kpiW = (pw - 2*m) / kpiItems.length;
      kpiItems.forEach(([label, val], i) => {
        const kx = m + i * kpiW;
        doc.setFontSize(7); doc.setTextColor(150, 160, 155); doc.setFont("helvetica", "normal");
        doc.text(label, kx, 28);
        doc.setFontSize(10); doc.setTextColor(1, 67, 54); doc.setFont("helvetica", "bold");
        doc.text(val, kx, 34);
      });

      // SVG as image
      const clean = await cleanSvgForExport(svgRef.current, { addLegend: true, blocks, kavel, sc, pan, includeBg: exportBg });
      clean.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      const svgStr = new XMLSerializer().serializeToString(clean);
      const svgBlob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
      const imgUrl = URL.createObjectURL(svgBlob);

      await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const scale = 2;
          canvas.width = img.naturalWidth * scale;
          canvas.height = img.naturalHeight * scale;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#F7F9F8";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.scale(scale, scale);
          ctx.drawImage(img, 0, 0);
          URL.revokeObjectURL(imgUrl);

          const imgData = canvas.toDataURL("image/png");
          const svgH = ph - 36 - m - 14; // space for SVG between KPI bar and footer
          const svgW = pw - 2*m;
          const ratio = Math.min(svgW / img.naturalWidth, svgH / img.naturalHeight);
          const drawW = img.naturalWidth * ratio;
          const drawH = img.naturalHeight * ratio;
          const ox = m + (svgW - drawW) / 2;
          doc.addImage(imgData, "PNG", ox, 38, drawW, drawH);

          // Footer
          doc.setFillColor(1, 67, 54);
          doc.rect(0, ph - 10, pw, 10, "F");
          doc.setFontSize(7); doc.setTextColor(200, 220, 210); doc.setFont("helvetica", "normal");
          doc.text("GreenVision Energy — Terreinplan", m, ph - 4);
          doc.text(new Date().toLocaleDateString("nl-NL"), pw - m - 25, ph - 4);

          // Scale bar in PDF
          if (sc > 0) {
            const pdfSc = (drawW / img.naturalWidth) * sc; // pixels-per-meter in PDF mm space
            const candidates = [5, 10, 20, 50, 100, 200, 500];
            let chosenDist = 50;
            for (const d of candidates) {
              const barMm = d * pdfSc;
              const pct = barMm / drawW;
              if (pct >= 0.08 && pct <= 0.3) { chosenDist = d; break; }
            }
            const barMm = chosenDist * pdfSc;
            const sbx = ox + 6;
            const sby = 38 + drawH - 10;
            // White background
            doc.setFillColor(255, 255, 255);
            doc.setGState(new doc.GState({ opacity: 0.8 }));
            doc.roundedRect(sbx - 2, sby - 6, barMm + 4, 9, 1, 1, "F");
            doc.setGState(new doc.GState({ opacity: 1 }));
            // Black bar
            doc.setFillColor(26, 26, 26);
            doc.rect(sbx, sby - 1, barMm, 2, "F");
            // End ticks
            doc.setDrawColor(26, 26, 26); doc.setLineWidth(0.5);
            doc.line(sbx, sby - 3, sbx, sby + 2);
            doc.line(sbx + barMm, sby - 3, sbx + barMm, sby + 2);
            // Label
            doc.setFontSize(7); doc.setTextColor(26, 26, 26); doc.setFont("helvetica", "bold");
            doc.text(`${chosenDist} m`, sbx + barMm / 2, sby - 3, { align: "center" });
          }

          doc.save(`${project?.name || "terreinplan"}.pdf`);
          resolve();
        };
        img.onerror = () => { URL.revokeObjectURL(imgUrl); resolve(); };
        img.src = imgUrl;
      });
    } catch (err) { console.error("PDF export error:", err); }
  };

  // ---- Keyboard ----
  useEffect(() => {
    const handler = (e) => {
      // Spacebar pan
      if (e.key === "Alt") { e.preventDefault(); setAltHeld(true); return; }
      if (e.key === " " && !e.target.closest("input")) { e.preventDefault(); setSpaceHeld(true); return; }
      if (e.target.closest("input")) return;
      if (e.key==="Delete"||e.key==="Backspace") { delBlock(); if(selLine){setLines(p=>p.filter(l=>l.id!==selLine));setSelLine(null);} }
      if (e.key==="d"&&(e.metaKey||e.ctrlKey)) { e.preventDefault(); dupBlock(); }
      if (e.key==="r"&&!e.metaKey) rotBlock();
      if (e.key==="l"&&!e.metaKey) lockBlock();
      if (e.key==="z"&&(e.metaKey||e.ctrlKey)&&!e.shiftKey) { e.preventDefault(); hD({type:"U"}); }
      if (e.key==="z"&&(e.metaKey||e.ctrlKey)&&e.shiftKey) { e.preventDefault(); hD({type:"R"}); }
      if (e.key==="Escape") { setTool("select"); finishLine(); setKavelDrawPts([]); setFreeformDrawPts([]); setFreeformType(null); setKavelEdit(false); setMPts({a:null,b:null}); setPendingBlock(null); }
      if (e.key==="Enter") { if(tool==="line")finishLine(); if(tool==="kavel")finishKavel(); if(tool==="freeform")finishFreeform(); }
      if (e.key==="m"&&!e.metaKey) setTool("measure");
      if (e.key==="v"&&!e.metaKey) setTool("select");
      if (e.key==="n"&&!e.metaKey) { setTool("line"); setLinePts([]); }
      if (e.key==="k"&&!e.metaKey) { setTool("kavel"); setKavelDrawPts([]); }
    };
    const upHandler = (e) => { if (e.key === " ") setSpaceHeld(false); if (e.key === "Alt") setAltHeld(false); };
    window.addEventListener("keydown", handler);
    window.addEventListener("keyup", upHandler);
    return () => { window.removeEventListener("keydown", handler); window.removeEventListener("keyup", upHandler); };
  }, [sel, selLine, blocks, tool, linePts, kavelDrawPts]);

  // ---- Grid lines ----
  const grid = useMemo(() => {
    const ls = [];
    const step = zoom > 1.5 ? 5 : zoom > 0.6 ? 10 : 25;
    for (let i = 0; i <= 300; i += step) {
      const m = i % (step*5) === 0;
      ls.push(<line key={`x${i}`} data-ui="grid" x1={i*sc} y1={0} x2={i*sc} y2={300*sc} stroke={m?C.gm:C.gl} strokeWidth={m?0.8:0.4} />);
      ls.push(<line key={`y${i}`} data-ui="grid" x1={0} y1={i*sc} x2={300*sc} y2={i*sc} stroke={m?C.gm:C.gl} strokeWidth={m?0.8:0.4} />);
      if (m) {
        ls.push(<text key={`lx${i}`} data-ui="grid" x={i*sc} y={-3} textAnchor="middle" fill={C.textMut} fontSize={8} fontFamily="'Inter',sans-serif">{i}m</text>);
        ls.push(<text key={`ly${i}`} data-ui="grid" x={-3} y={i*sc+3} textAnchor="end" fill={C.textMut} fontSize={8} fontFamily="'Inter',sans-serif">{i}m</text>);
      }
    }
    return ls;
  }, [sc, zoom]);

  const selB = blocks.find((b) => b.id === sel);
  const selT = selB ? getT(selB.type) : null;
  const cats = ["gebouw", "logistiek", "parking", "terrein", "voertuigen"];

  const ctxItems = ctxMenu ? [
    { ic:"⧉", l:"Dupliceer", sc:"⌘D", fn:()=>{if(ctxMenu.bid){setSel(ctxMenu.bid);setTimeout(dupBlock,0);}}, off:!ctxMenu.bid },
    { ic:"↻", l:"Roteer", sc:"R", fn:()=>{if(ctxMenu.bid){setSel(ctxMenu.bid);setTimeout(rotBlock,0);}}, off:!ctxMenu.bid },
    { ic:"🔒", l:"Lock", sc:"L", fn:()=>{if(ctxMenu.bid){setSel(ctxMenu.bid);setTimeout(lockBlock,0);}}, off:!ctxMenu.bid },
    { ic:"⬠", l:"Maak vrije vorm", fn:()=>{if(ctxMenu.bid){convertToPolygon(ctxMenu.bid);setSel(ctxMenu.bid);}},
      off:!ctxMenu.bid || !!blocks.find(b=>b.id===ctxMenu.bid)?.polygon },
    { div:true },
    ...(multiSel.length >= 2 ? [
      { ic:"⊕", l:"Samenvoegen", fn:()=>mergeBlocks(),
        off:[...new Set(blocks.filter(b=>multiSel.includes(b.id)).map(b=>b.type))].length !== 1 },
      { div:true },
    ] : []),
    { ic:"📏", l:"Meettool", sc:"M", fn:()=>setTool("measure") },
    { ic:"✏️", l:"Lijn tekenen", sc:"N", fn:()=>{setTool("line");setLinePts([]);} },
    { ic:"⬠", l:"Kavel tekenen", sc:"K", fn:()=>{setTool("kavel");setKavelDrawPts([]);} },
    { div:true },
    { ic:"✕", l:"Verwijder", sc:"Del", fn:()=>{if(ctxMenu.bid){setSel(ctxMenu.bid);setTimeout(delBlock,0);}}, off:!ctxMenu.bid, d:true },
  ] : [];

  // Auto-open properties sheet on mobile when block selected
  useEffect(() => {
    if (isMobile && sel && selB) {
      setMobileTab("props");
    }
  }, [sel, isMobile]);

  // ---- Loading state ----
  if (loading) {
    return (
      <div style={{ height:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.bg, fontFamily:"'Inter',sans-serif" }}>
        <div style={{ textAlign:"center" }}>
          <div style={{ fontSize:24, marginBottom:8 }}>🌿</div>
          <div style={{ color:C.textSec }}>Terreinplan laden...</div>
        </div>
      </div>
    );
  }

  // ---- PDOK WMS URL ----
  const wmsUrl = mapCfg.rdX > 0 ? pdokWmsUrl(mapCfg.layer, mapCfg.rdX, mapCfg.rdY, mapCfg.viewport) : null;
  const kadasterOverlayUrl = (mapCfg.rdX > 0 && mapCfg.layer === "luchtfoto" && mapCfg.kadasterOverlay)
    ? pdokWmsUrl("kadaster", mapCfg.rdX, mapCfg.rdY, mapCfg.viewport) : null;

  // ============================================================================
  // RENDER
  // ============================================================================
  // ---- MobileSheet component ----
  const MobileSheet = ({ open, onClose, title, children }) => {
    if (!open) return null;
    return (
      <div style={{ position:"fixed", inset:0, zIndex:900 }}>
        <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.3)" }} onClick={onClose} />
        <div style={{ position:"absolute", bottom:52, left:0, right:0, maxHeight:"60vh",
          background:C.surface, borderRadius:"16px 16px 0 0", boxShadow:"0 -8px 30px rgba(0,0,0,0.15)",
          display:"flex", flexDirection:"column", overflow:"hidden" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
            padding:"12px 16px", borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
            <span style={{ fontSize:13, fontWeight:700, color:C.primary }}>{title}</span>
            <button onClick={onClose} style={{ background:"none", border:"none", fontSize:18,
              cursor:"pointer", color:C.textMut, padding:4 }}>✕</button>
          </div>
          <div style={{ flex:1, overflow:"auto", padding:"8px 0" }}>{children}</div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ background:C.bg, color:C.text, height:"100vh", display:"flex", flexDirection:"column",
      fontFamily:"'Inter','system-ui',sans-serif", overflow:"hidden", userSelect:"none" }}>

      {/* === TOP BAR === */}
      {isMobile ? (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px",
          borderBottom:`1px solid ${C.border}`, background:C.surface, flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <button onClick={() => navigate("/crm/tekenaar")} style={{ background:"none", border:"none", cursor:"pointer", fontSize:20, color:C.textSec, padding:"8px", minWidth:44, minHeight:44, display:"flex", alignItems:"center", justifyContent:"center" }}>←</button>
            <span style={{ fontSize:14 }}>🌿</span>
            <div style={{ overflow:"hidden" }}>
              <div style={{ fontWeight:800, fontSize:11, color:C.primary, letterSpacing:1 }}>GREENVISION</div>
              {project && <div style={{ fontSize:9, color:C.textMut, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", maxWidth:140 }}>{project.name}</div>}
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
            {saving && <span style={{ fontSize:9, color:C.accent }}>💾</span>}
            {scenarios.length > 1 && (
              <select value={actSc || ""} onChange={(e) => switchScenario(e.target.value)}
                style={{ padding:"4px 6px", fontSize:10, border:`1px solid ${C.border}`, borderRadius:5,
                  background:C.surfAlt, color:C.text, fontFamily:"inherit" }}>
                {scenarios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
            <span style={{ fontSize:9, color:C.textMut, background:C.surfAlt, padding:"2px 5px", borderRadius:3 }}>
              {Math.round(zoom*100)}%
            </span>
          </div>
        </div>
      ) : (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"5px 12px",
          borderBottom:`1px solid ${C.border}`, background:C.surface, flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <button onClick={() => navigate("/crm/tekenaar")} style={{ background:"none", border:"none", cursor:"pointer", fontSize:16, color:C.textSec, padding:"4px" }}>←</button>
            <span style={{ fontSize:14 }}>🌿</span>
            <span style={{ fontWeight:800, fontSize:11, color:C.primary, letterSpacing:1.5 }}>GREENVISION</span>
            <span style={{ fontSize:10, color:C.textSec }}>Tekenaar</span>
            {project && <span style={{ fontSize:10, color:C.textMut, marginLeft:8 }}>— {project.name}</span>}
            <div style={{ width:1, height:18, background:C.border }} />
            <TBtn icon="↖" label="Selecteer (V)" active={tool==="select"} onClick={()=>setTool("select")} />
            <TBtn icon="📏" label="Meten (M)" active={tool==="measure"} onClick={()=>{setTool("measure");setMPts({a:null,b:null});}} />
            <TBtn icon="✏️" label="Lijn (N)" active={tool==="line"} onClick={()=>{setTool("line");setLinePts([]);}} />
            <TBtn icon="⬠" label="Kavel (K)" active={tool==="kavel"} onClick={()=>{setTool("kavel");setKavelDrawPts([]);}} />
            <TBtn icon="✎" label="Vrije vorm (⬠)" active={tool==="freeform"} onClick={()=>{
              if (tool==="freeform") { setTool("select"); setFreeformDrawPts([]); setFreeformType(null); }
              else { startFreeform("laadplein"); }
            }} />
            <div style={{ width:1, height:18, background:C.border }} />
            <TBtn icon="↩" label="Undo" onClick={()=>hD({type:"U"})} />
            <span style={{ fontSize:8, color:C.textMut, minWidth:30, textAlign:"center" }}>
              {h.past.length > 0 || h.future.length > 0 ? `↩${h.past.length} ↪${h.future.length}` : ""}
            </span>
            <TBtn icon="↪" label="Redo" onClick={()=>hD({type:"R"})} />
            <div style={{ width:1, height:18, background:C.border }} />
            {scenarios.map((s) => (
              <button key={s.id} onClick={() => switchScenario(s.id)}
                style={{ background: s.id===actSc ? C.accentDim : "transparent", color: s.id===actSc ? C.primary : C.textSec,
                  border:`1px solid ${s.id===actSc ? C.accentMid : C.border}`, padding:"3px 9px", fontSize:10,
                  cursor:"pointer", borderRadius:5, fontWeight: s.id===actSc ? 700 : 500, fontFamily:"inherit" }}>
                {s.name}
              </button>
            ))}
            <Btn onClick={addScenario} small>+</Btn>
            <Btn onClick={dupScenario} small>⧉</Btn>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
            {saving && <span style={{ fontSize:9, color:C.accent }}>💾 Opslaan...</span>}
            <div ref={exportRef} style={{ position:"relative" }}>
              <Btn onClick={() => setExportOpen(!exportOpen)} small style={{ fontWeight:700 }}>⬇ Exporteren</Btn>
              {exportOpen && (
                <div style={{ position:"absolute", top:"100%", right:0, marginTop:4, background:"white",
                  border:`1px solid ${C.border}`, borderRadius:8, boxShadow:"0 8px 30px rgba(0,0,0,0.15)",
                  padding:"4px 0", zIndex:999, minWidth:220, whiteSpace:"nowrap" }}>
                  <label style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", fontSize:11,
                    fontFamily:"inherit", color:C.text, cursor:"pointer", userSelect:"none" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = C.surfAlt; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                    <input type="checkbox" checked={exportBg} onChange={(e) => setExportBg(e.target.checked)}
                      style={{ accentColor: C.primary, width:14, height:14 }} />
                    <span>🗺 Achtergrond meenemen</span>
                  </label>
                  <div style={{ height:1, background:C.border, margin:"2px 0" }} />
                  {[
                    { icon:"📄", label:"PDF — Professioneel rapport", fn: exportPDF },
                    { icon:"📷", label:"PNG — Hoge resolutie afbeelding", fn: exportPNG },
                    { icon:"🖼", label:"SVG — Vector tekening", fn: exportSVG },
                    { icon:"📐", label:"DXF — AutoCAD bestand", fn: exportDXF },
                  ].map((item, i) => (
                    <button key={i} onClick={item.fn}
                      style={{ display:"flex", alignItems:"center", gap:8, width:"100%",
                        padding:"8px 14px", border:"none", cursor:"pointer", background:"transparent",
                        fontSize:11, fontFamily:"inherit", color:C.text, textAlign:"left" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = C.surfAlt; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                      <span style={{ fontSize:14 }}>{item.icon}</span>
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Btn onClick={() => setAiRenderOpen(true)} small style={{ background:"linear-gradient(135deg, #014336, #02714D)", color:"#fff", border:"none" }}>🎨 AI Render</Btn>
            <Btn onClick={() => setShow3DView(true)} small style={{ background:"linear-gradient(135deg, #1E40AF, #3B82F6)", color:"#fff", border:"none" }}>🎮 3D Preview</Btn>
            <span style={{ fontSize:9, color:C.textMut, background:C.surfAlt, padding:"2px 5px", borderRadius:3 }}>
              {Math.round(zoom*100)}%
            </span>
          </div>
        </div>
      )}

      {/* === MULTI-SELECT BAR === */}
      {multiSel.length >= 2 && (
        <div style={{ display:"flex", alignItems:"center", gap:6, padding:"3px 12px",
          background:C.accentDim, borderBottom:`1px solid ${C.accentMid}`, fontSize:10 }}>
          <span style={{ fontWeight:700, color:C.primary }}>{multiSel.length} geselecteerd</span>
          <Btn onClick={()=>alignBlocks("left")} small>⫷ Links</Btn>
          <Btn onClick={()=>alignBlocks("right")} small>⫸ Rechts</Btn>
          <Btn onClick={()=>alignBlocks("top")} small>⫠ Boven</Btn>
          <Btn onClick={()=>alignBlocks("bottom")} small>⫡ Onder</Btn>
          <div style={{ flex:1 }} />
          <Btn onClick={()=>setMultiSel([])} small>✕</Btn>
        </div>
      )}

      {/* === MAIN LAYOUT === */}
      <div style={{ display:"flex", flex:1, overflow:"hidden" }}>

        {/* LEFT SIDEBAR — hidden on mobile */}
        {!isMobile && <div style={{ width:200, background:C.surface, borderRight:`1px solid ${C.border}`, display:"flex", flexDirection:"column", flexShrink:0 }}>
          <div style={{ display:"flex", borderBottom:`1px solid ${C.border}` }}>
            {[["blocks","Blokken"],["map","Kaart"],["layers","Layers"],["history","Historie"]].map(([k,l]) => (
              <button key={k} onClick={()=>setLeftTab(k)} style={{
                flex:1, padding:"6px 2px", fontSize:9, fontWeight:600, border:"none", cursor:"pointer",
                background: leftTab===k ? C.surface : C.surfAlt, color: leftTab===k ? C.primary : C.textMut,
                borderBottom: leftTab===k ? `2px solid ${C.accent}` : "2px solid transparent", fontFamily:"inherit",
              }}>{l}</button>
            ))}
          </div>
          <div style={{ flex:1, overflow:"auto" }}>
            {/* BLOCKS TAB */}
            {leftTab === "blocks" && cats.map((cat) => (
              <div key={cat}>
                <div style={{ padding:"7px 10px 2px", fontSize:8, color:C.textMut, letterSpacing:1.5, fontWeight:700, textTransform:"uppercase" }}>{cat}</div>
                {BT.filter((t) => t.cat===cat).map((t) => (
                  <div key={t.id} style={{ display:"flex", alignItems:"center", borderBottom:`1px solid ${C.borderLt}` }}>
                    <button onClick={() => addBlock(t.id)} style={{
                      display:"flex", alignItems:"center", gap:7, padding:"4px 10px", flex:1,
                      background:"transparent", color:C.text, border:"none", cursor:"pointer",
                      fontSize:10, fontFamily:"inherit", textAlign:"left",
                    }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = C.surfAlt)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                      <span style={{ width:20, height:20, borderRadius:4, background:getLC(cat, t.id).f,
                        border:`1.5px solid ${getLC(cat, t.id).s}`, display:"flex", alignItems:"center",
                        justifyContent:"center", fontSize:10, flexShrink:0 }}>{t.icon}</span>
                      <div>
                        <div style={{ fontWeight:500 }}>{t.label}</div>
                        <div style={{ fontSize:8, color:C.textMut }}>{t.dw}×{t.dh}m</div>
                      </div>
                    </button>
                    <button onClick={() => startFreeform(t.id)} title={`${t.label} als vrije vorm tekenen`}
                      style={{ padding:"4px 6px", background:"transparent", border:"none", cursor:"pointer",
                        fontSize:11, color:C.textMut, flexShrink:0 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = C.accent; e.currentTarget.style.background = C.accentDim; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = C.textMut; e.currentTarget.style.background = "transparent"; }}>
                      ⬠
                    </button>
                  </div>
                ))}
              </div>
            ))}

            {/* MAP TAB */}
            {leftTab === "map" && (
              <div style={{ padding:10, display:"flex", flexDirection:"column", gap:8 }}>
                <div style={{ fontSize:9, color:C.textMut, fontWeight:700 }}>ADRES ZOEKEN</div>
                <div style={{ display:"flex", gap:3 }}>
                  <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                    onKeyDown={(e) => e.key==="Enter" && doSearch()}
                    placeholder="Adres, postcode..."
                    style={{ flex:1, padding:"5px 7px", fontSize:10, border:`1px solid ${C.border}`,
                      borderRadius:5, fontFamily:"inherit", outline:"none", background:C.surfAlt }} />
                  <Btn onClick={doSearch} primary small>{searching ? "…" : "🔍"}</Btn>
                </div>
                {searchRes.length > 0 && (
                  <div style={{ border:`1px solid ${C.border}`, borderRadius:5, overflow:"hidden", maxHeight:120, overflowY:"auto" }}>
                    {searchRes.map((r, i) => (
                      <button key={i} onClick={() => pickLoc(r)} style={{
                        display:"block", width:"100%", padding:"5px 7px", fontSize:10, textAlign:"left",
                        border:"none", borderBottom:`1px solid ${C.borderLt}`, cursor:"pointer",
                        background:"white", fontFamily:"inherit", color:C.text,
                      }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = C.surfAlt)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "white")}>
                        <div style={{ fontWeight:500 }}>{r.label}</div>
                        <div style={{ fontSize:8, color:C.textMut }}>RD: {r.rdX.toFixed(0)}, {r.rdY.toFixed(0)}</div>
                      </button>
                    ))}
                  </div>
                )}
                {mapCfg.rdX > 0 && (
                  <div style={{ padding:6, background:C.accentDim, borderRadius:5, fontSize:9, color:C.primary }}>
                    📍 RD: {mapCfg.rdX.toFixed(0)}, {mapCfg.rdY.toFixed(0)}
                  </div>
                )}

                <div style={{ fontSize:9, color:C.textMut, fontWeight:700, marginTop:4 }}>BAG PANDEN</div>
                <Btn onClick={loadBAG} primary small disabled={!mapCfg.rdX || bagLoading}>
                  {bagLoading ? "Laden..." : bagPanden.length > 0 ? `🏢 Herlaad (${bagPanden.length})` : "🏢 BAG panden laden"}
                </Btn>

                <div style={{ fontSize:9, color:C.textMut, fontWeight:700, marginTop:4 }}>KAARTBEREIK</div>
                <label style={{ fontSize:9, color:C.textSec }}>
                  Bereik: {mapCfg.viewport}m × {mapCfg.viewport}m
                </label>
                <input type="range" min={200} max={1000} step={50}
                  value={mapCfg.viewport}
                  onChange={(e) => setMapCfg((c) => ({ ...c, viewport:+e.target.value }))}
                  style={{ width:"100%", accentColor:C.accent }} />

                <div style={{ fontSize:9, color:C.textMut, fontWeight:700, marginTop:4 }}>KAARTROTATIE</div>
                <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                  <label style={{ fontSize:9, color:C.textSec, flex:1 }}>
                    Rotatie: {mapCfg.rotation || 0}°
                  </label>
                  <button onClick={() => setMapCfg((c) => ({ ...c, rotation:0 }))}
                    style={{ fontSize:8, padding:"2px 6px", borderRadius:3, border:`1px solid ${C.border}`,
                      background:C.surfAlt, color:C.textSec, cursor:"pointer", fontFamily:"inherit" }}>
                    Reset
                  </button>
                </div>
                <input type="range" min={0} max={359} step={1}
                  value={mapCfg.rotation || 0}
                  onChange={(e) => setMapCfg((c) => ({ ...c, rotation:+e.target.value }))}
                  style={{ width:"100%", accentColor:C.accent }} />

                <div style={{ fontSize:9, color:C.textMut, fontWeight:700, marginTop:4 }}>TRANSPARANTIE</div>
                <label style={{ fontSize:9, color:C.textSec }}>Dekking: {Math.round(mapCfg.opacity*100)}%</label>
                <input type="range" min={0} max={100} value={mapCfg.opacity*100}
                  onChange={(e) => setMapCfg((c) => ({ ...c, opacity:+e.target.value/100 }))}
                  style={{ width:"100%", accentColor:C.accent }} />

                <div style={{ fontSize:9, color:C.textMut, fontWeight:700, marginTop:4 }}>KAARTLAAG</div>
                <div style={{ display:"flex", gap:3 }}>
                  {["luchtfoto", "kadaster"].map((l) => (
                    <button key={l} onClick={() => setMapCfg((c) => ({ ...c, layer:l }))} style={{
                      flex:1, padding:"4px", fontSize:9, fontWeight:600, borderRadius:4, cursor:"pointer",
                      border: mapCfg.layer===l ? `2px solid ${C.accent}` : `1px solid ${C.border}`,
                      background: mapCfg.layer===l ? C.accentDim : "white", color:C.primary, fontFamily:"inherit",
                    }}>{l === "luchtfoto" ? "📷 Luchtfoto" : "📐 Kadaster"}</button>
                  ))}
                </div>
                {mapCfg.layer === "luchtfoto" && (
                  <label style={{ display:"flex", alignItems:"center", gap:4, fontSize:9, color:C.textSec, marginTop:4, cursor:"pointer" }}>
                    <input type="checkbox" checked={!!mapCfg.kadasterOverlay}
                      onChange={(e) => setMapCfg((c) => ({ ...c, kadasterOverlay: e.target.checked }))} />
                    📐 Kadaster overlay
                  </label>
                )}

                <div style={{ fontSize:9, color:C.textMut, fontWeight:700, marginTop:4 }}>ACHTERGROND OVERLAY</div>
                <label style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:10,
                  border:`2px dashed ${C.border}`, borderRadius:6, cursor:"pointer", fontSize:10, color:C.textSec }}>
                  📁 Upload afbeelding overlay
                  <input type="file" accept="image/*" style={{ display:"none" }} onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const safeName = f.name.replace(/[^a-zA-Z0-9._-]/g, '_');
                    const path = `tekenaar/${projectId}/${Date.now()}_${safeName}`;
                    const { error } = await supabase.storage.from("location-sketches").upload(path, f, { upsert: true });
                    if (error) { console.error("Upload failed", error); return; }
                    const { data: { publicUrl } } = supabase.storage.from("location-sketches").getPublicUrl(path);
                    setMapCfg((c) => ({ ...c, imgUrl: publicUrl, imgData: null }));
                  }} />
                </label>
                {(mapCfg.imgUrl || mapCfg.imgData) && (<>
                  <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:10, color: imgDragMode ? C.primary : C.textSec, cursor:"pointer", padding:"4px 0" }}>
                    <input type="checkbox" checked={imgDragMode} onChange={(e) => setImgDragMode(e.target.checked)} style={{ accentColor:C.accent }} />
                    🖐 Overlay verslepen
                  </label>
                  <Inp label="Breedte werkelijkheid (m)" value={mapCfg.imgW} onChange={(v) => setMapCfg((c) => ({ ...c, imgW:+v }))} step={10} />
                  <div style={{ display:"flex", gap:4 }}>
                    <Inp label="Positie X (m)" value={mapCfg.imgX||0} onChange={(v) => setMapCfg((c) => ({ ...c, imgX:+v }))} step={1} />
                    <Inp label="Positie Y (m)" value={mapCfg.imgY||0} onChange={(v) => setMapCfg((c) => ({ ...c, imgY:+v }))} step={1} />
                  </div>
                  <div style={{ fontSize:9, color:C.textSec, marginTop:2 }}>Schaal: {(mapCfg.imgScale??1).toFixed(2)}x</div>
                  <input type="range" min={0.1} max={5} step={0.05} value={mapCfg.imgScale??1}
                    onChange={(e) => setMapCfg((c) => ({ ...c, imgScale:+e.target.value }))}
                    style={{ width:"100%", accentColor:C.primary }} />
                  <div style={{ fontSize:9, color:C.textSec, marginTop:2 }}>Rotatie: {mapCfg.imgRotation||0}°</div>
                  <input type="range" min={0} max={359} step={1} value={mapCfg.imgRotation||0}
                    onChange={(e) => setMapCfg((c) => ({ ...c, imgRotation:+e.target.value }))}
                    style={{ width:"100%", accentColor:C.primary }} />
                  <div style={{ fontSize:9, color:C.textSec, marginTop:2 }}>Transparantie: {((mapCfg.imgOpacity??0.6)*100).toFixed(0)}%</div>
                  <input type="range" min={0} max={1} step={0.05} value={mapCfg.imgOpacity??0.6}
                    onChange={(e) => setMapCfg((c) => ({ ...c, imgOpacity:+e.target.value }))}
                    style={{ width:"100%", accentColor:C.primary }} />
                  <Btn small onClick={() => setMapCfg((c) => ({ ...c, imgUrl: null, imgData: null, imgX:0, imgY:0, imgRotation:0, imgOpacity:0.6, imgScale:1 }))}
                    style={{ background:"#dc2626", color:"#fff", fontSize:9, marginTop:2 }}>🗑 Verwijder overlay</Btn>
                </>)}

                <div style={{ fontSize:9, color:C.textMut, fontWeight:700, marginTop:4 }}>KAVEL</div>
                <div style={{ display:"flex", gap:3 }}>
                  <Btn onClick={() => setKavelEdit(!kavelEdit)} small style={{ flex:1,
                    background: kavelEdit ? C.accentDim : C.surfAlt, color: kavelEdit ? C.primary : C.text }}>
                    {kavelEdit ? "✓ Klaar" : "✎ Bewerk kavel"}
                  </Btn>
                  <Btn onClick={() => { setTool("kavel"); setKavelDrawPts([]); }} small>⬠ Nieuw</Btn>
                </div>
                {kavel.length >= 3 && (
                  <div style={{ fontSize:9, color:C.textSec }}>
                    Kaveloppervlakte: {kpis.kavelArea > 0 ? `${kpis.kavelArea.toFixed(0)} m²` : "—"}
                  </div>
                )}
              </div>
            )}

            {/* LAYERS TAB */}
            {leftTab === "layers" && (
              <div style={{ padding:10, display:"flex", flexDirection:"column", gap:4 }}>
                <div style={{ fontSize:9, color:C.textMut, fontWeight:700, marginBottom:2 }}>ZICHTBAARHEID</div>
                {Object.entries(layers).map(([k, v]) => (
                  <label key={k} style={{ display:"flex", alignItems:"center", gap:7, padding:"4px 3px", cursor:"pointer", fontSize:10 }}>
                    <input type="checkbox" checked={v} onChange={() => setLayers((l) => ({ ...l, [k]:!l[k] }))} style={{ accentColor:C.accent }} />
                    <span style={{ width:12, height:12, borderRadius:2, background:LC[k]?.f||C.bag, border:`1.5px solid ${LC[k]?.s||C.bagS}` }} />
                    <span style={{ fontWeight:500, textTransform:"capitalize", color: v ? C.text : C.textMut }}>{k}</span>
                  </label>
                ))}

                <div style={{ fontSize:9, color:C.textMut, fontWeight:700, marginTop:8 }}>KPI's</div>
                {[
                  ["Warehouse", kpis.wh>0 ? `${(kpis.wh/1000).toFixed(1)}k m²` : "—"],
                  ["Expeditie", kpis.exp>0 ? `${kpis.exp.toFixed(0)} m²` : "—"],
                  ["Kantoor", kpis.off>0 ? `${kpis.off.toFixed(0)} m²` : "—"],
                  ["Docks", `${kpis.docks}`],
                  ["P auto", `${kpis.pCar}`],
                  ["P truck", `${kpis.pTruck}`],
                  ["Groen", kpis.green>0 ? `${kpis.green.toFixed(0)} m²` : "—"],
                  ["Kavel", kpis.kavelArea>0 ? `${(kpis.kavelArea/1000).toFixed(1)}k m²` : "—"],
                  ["Bebouwing%", `${kpis.bebouwingsPct}%`],
                ].map(([l, v]) => (
                  <div key={l} style={{ display:"flex", justifyContent:"space-between", fontSize:10, padding:"2px 0", borderBottom:`1px solid ${C.borderLt}` }}>
                    <span style={{ color:C.textSec }}>{l}</span>
                    <span style={{ fontWeight:600 }}>{v}</span>
                  </div>
                ))}
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, padding:"6px 0",
                  borderTop:`2px solid ${C.primary}`, marginTop:4 }}>
                  <span style={{ fontWeight:800, color:C.primary }}>Totaal BVO</span>
                  <span style={{ fontWeight:800, color:C.primary }}>
                    {kpis.bvo > 0 ? `${(kpis.bvo/1000).toFixed(1)}k m²` : "—"}
                  </span>
                </div>

                {/* LEGENDA — oppervlaktes per type */}
                <div style={{ fontSize:9, color:C.textMut, fontWeight:700, marginTop:12 }}>LEGENDA</div>
                {(() => {
                  const legend = new Map();
                  blocks.forEach((b) => {
                    const t = getT(b.type);
                    if (!t) return;
                    let area = 0;
                    if (b.polygon && b.polygon.length >= 3) {
                      for (let i = 0; i < b.polygon.length; i++) {
                        const j = (i+1) % b.polygon.length;
                        area += b.polygon[i][0]*b.polygon[j][1] - b.polygon[j][0]*b.polygon[i][1];
                      }
                      area = Math.abs(area) / 2;
                    } else {
                      area = b.w * b.h;
                    }
                    if (!legend.has(t.id)) legend.set(t.id, { label: t.label, icon: t.icon, cat: t.cat, area: 0, count: 0 });
                    const e = legend.get(t.id);
                    e.area += area; e.count += 1;
                  });
                  return [...legend.values()].map(({ label, icon, cat, area, count }) => {
                    const lc = getLC(cat);
                    return (
                      <div key={label} style={{ display:"flex", alignItems:"center", gap:6, fontSize:10, padding:"3px 0", borderBottom:`1px solid ${C.borderLt}` }}>
                        <span style={{ width:14, height:14, borderRadius:2, background:lc.f, border:`1.5px solid ${lc.s}`,
                          display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, flexShrink:0 }}>{icon}</span>
                        <span style={{ flex:1, color:C.text, fontWeight:500 }}>{label}</span>
                        <span style={{ color:C.textSec, fontSize:9, fontWeight:600, whiteSpace:"nowrap" }}>
                          {area >= 1000 ? `${(area/1000).toFixed(1)}k` : area.toFixed(0)} m²
                        </span>
                        <span style={{ color:C.textMut, fontSize:8 }}>({count})</span>
                      </div>
                    );
                  });
                })()}
              </div>
            )}

            {/* HISTORY TAB */}
            {leftTab === "history" && (
              <div style={{ padding:10, display:"flex", flexDirection:"column", gap:2 }}>
                <div style={{ fontSize:9, color:C.textMut, fontWeight:700, marginBottom:4 }}>HISTORIE</div>
                <div style={{ fontSize:8, color:C.textMut, marginBottom:4 }}>Klik om naar een stap te springen</div>
                {(() => {
                  const allStates = [...h.past, { state: h.now, label: "Huidig" }, ...h.future];
                  const currentIdx = h.past.length;
                  return allStates.map((item, idx) => {
                    const label = item.label || `Actie ${idx+1}`;
                    const isCurrent = idx === currentIdx;
                    const isPast = idx < currentIdx;
                    const isFuture = idx > currentIdx;
                    return (
                      <button key={idx} onClick={() => hD({ type:"JUMP", idx })}
                        style={{
                          display:"flex", alignItems:"center", gap:6, padding:"4px 8px", width:"100%",
                          background: isCurrent ? C.accentDim : "transparent",
                          border: isCurrent ? `1px solid ${C.accentMid}` : `1px solid transparent`,
                          borderRadius:4, cursor:"pointer", fontSize:10, fontFamily:"inherit", textAlign:"left",
                          color: isFuture ? C.textMut : isCurrent ? C.primary : C.text,
                          opacity: isFuture ? 0.5 : 1, fontWeight: isCurrent ? 700 : 400,
                        }}
                        onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.background = C.surfAlt; }}
                        onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.background = "transparent"; }}>
                        <span style={{ width:6, height:6, borderRadius:3, flexShrink:0,
                          background: isCurrent ? C.accent : isPast ? C.primary : C.border }} />
                        <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {isCurrent ? "● " : ""}{label}
                        </span>
                      </button>
                    );
                  });
                })()}
                {h.past.length === 0 && h.future.length === 0 && (
                  <div style={{ fontSize:10, color:C.textMut, padding:8, textAlign:"center" }}>
                    Nog geen acties uitgevoerd
                  </div>
                )}
              </div>
            )}
          </div>
        </div>}

        {/* === SVG CANVAS === */}
        <div ref={canvasContainerRef} style={{ flex:1, position:"relative", overflow:"hidden",
          cursor: spaceHeld ? "grab" : pendingBlock ? "crosshair" : tool==="measure"||tool==="line"||tool==="kavel"||tool==="freeform" ? "crosshair" : isPan ? "grabbing" : "default" }}>

          <svg ref={svgRef} width="100%" height="100%" style={{ display:"block", background:C.bg, touchAction:"none" }}
            onMouseDown={handleCDown} onMouseMove={handleMM} onMouseUp={handleMU} onMouseLeave={handleMU}
            onClick={isMobile ? undefined : handleClick} onDoubleClick={handleDblClick} onContextMenu={handleCtx}
            onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
            <g transform={`translate(${pan.x},${pan.y})`}>
              {/* Rotatable background group (WMS + uploaded image + BAG) */}
              <g data-ui="background" transform={`rotate(${mapCfg.rotation || 0}, ${(mapCfg.viewport*sc)/2}, ${(mapCfg.viewport*sc)/2})`}>
                {/* Background: PDOK WMS */}
                {wmsUrl && <image href={wmsUrl} x={0} y={0} width={mapCfg.viewport*sc}
                  height={mapCfg.viewport*sc} opacity={mapCfg.opacity} style={{ imageRendering:"auto" }} />}
                {/* Kadaster overlay on top of luchtfoto */}
                {kadasterOverlayUrl && <image href={kadasterOverlayUrl} x={0} y={0} width={mapCfg.viewport*sc}
                  height={mapCfg.viewport*sc} opacity={0.6} style={{ imageRendering:"auto", mixBlendMode:"multiply" }} />}

                {/* Background: uploaded image */}
                {(mapCfg.imgUrl || mapCfg.imgData) && (() => {
                  const iw = (mapCfg.imgW||300)*sc*(mapCfg.imgScale??1);
                  const ix = (mapCfg.imgX||0)*sc;
                  const iy = (mapCfg.imgY||0)*sc;
                  const ir = mapCfg.imgRotation||0;
                    return (
                      <g transform={`translate(${ix},${iy}) rotate(${ir},${iw/2},${iw/2})`}
                        style={{ cursor: imgDragMode ? "move" : "default", pointerEvents: imgDragMode ? "auto" : "none" }}
                        onMouseDown={imgDragMode ? (e) => {
                          e.stopPropagation();
                          window.__imgDrag = { mx: e.clientX, my: e.clientY, ox: mapCfg.imgX||0, oy: mapCfg.imgY||0 };
                        } : undefined}>
                        <image href={mapCfg.imgUrl || mapCfg.imgData} x={0} y={0}
                          width={iw} height={iw}
                          opacity={mapCfg.imgOpacity??0.6} preserveAspectRatio="xMinYMin meet"
                          style={{ pointerEvents: imgDragMode ? "auto" : "none" }} />
                    </g>
                  );
                })()}

                {/* BAG panden */}
                {layers.bag && bagPanden.length > 0 && mapCfg.rdX > 0 && (
                  <BAGOverlay panden={bagPanden} rdCenter={{ x:mapCfg.rdX, y:mapCfg.rdY }} viewport={mapCfg.viewport} sc={sc} />
                )}
              </g>

              {grid}

              {/* Snap guides */}
              {snapG.map((g, i) => g.type==="v" ?
                <line key={i} data-ui="snap" x1={g.pos*sc} y1={0} x2={g.pos*sc} y2={300*sc} stroke={C.snap} strokeWidth={1} strokeDasharray="4,4" opacity={0.7} /> :
                <line key={i} data-ui="snap" x1={0} y1={g.pos*sc} x2={300*sc} y2={g.pos*sc} stroke={C.snap} strokeWidth={1} strokeDasharray="4,4" opacity={0.7} />
              )}

              {/* Kavel */}
              <KavelDisplay kavel={kavel} sc={sc} editMode={kavelEdit}
                onAddPoint={(i, x, y) => setKavel((k) => { const n=[...k]; n.splice(i,0,[sg(x),sg(y)]); return n; })}
                onRemovePoint={(i) => { if(kavel.length>3) setKavel((k) => k.filter((_,j) => j!==i)); }} />

              {/* Drawing kavel points */}
              {tool === "kavel" && kavelDrawPts.length > 0 && (
                <g data-ui="drawing">
                  <polyline points={kavelDrawPts.map((p) => `${p.x*sc},${p.y*sc}`).join(" ")}
                    fill="none" stroke={C.accent} strokeWidth={2.5} strokeDasharray="8,4" />
                  {kavelDrawPts.map((p, i) => (
                    <circle key={i} cx={p.x*sc} cy={p.y*sc} r={i === 0 && kavelDrawPts.length >= 3 ? 10 : 5}
                      fill={i === 0 && kavelDrawPts.length >= 3 ? "rgba(34,197,94,0.3)" : C.accent}
                      stroke={i === 0 && kavelDrawPts.length >= 3 ? "#22c55e" : "white"} strokeWidth={2}
                      style={i === 0 && kavelDrawPts.length >= 3 ? { cursor:"pointer" } : {}} />
                  ))}
                  {kavelDrawPts.length >= 3 && (
                    <>
                      <circle cx={kavelDrawPts[0].x*sc} cy={kavelDrawPts[0].y*sc} r={16}
                        fill="none" stroke="#22c55e" strokeWidth={1.5} opacity={0.6}>
                        <animate attributeName="r" values="12;18;12" dur="1.5s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0.8;0.2;0.8" dur="1.5s" repeatCount="indefinite" />
                      </circle>
                      <text x={kavelDrawPts[0].x*sc} y={kavelDrawPts[0].y*sc - 20} textAnchor="middle"
                        fill="#22c55e" fontSize={9} fontWeight="700" style={{ pointerEvents:"none" }}>
                        Klik om te sluiten
                      </text>
                    </>
                  )}
                </g>
              )}

              {/* Drawing freeform polygon points */}
              {tool === "freeform" && freeformDrawPts.length > 0 && (() => {
                const ft = getT(freeformType);
                const flc = ft ? getLC(ft.cat, freeformType) : { s:"#DC2626", f:"rgba(220,38,38,0.1)" };
                return (
                  <g data-ui="drawing">
                    <polyline points={freeformDrawPts.map((p) => `${p.x*sc},${p.y*sc}`).join(" ")}
                      fill={flc.f} fillOpacity={0.15} stroke={flc.s} strokeWidth={2.5} strokeDasharray="8,4" />
                    {freeformDrawPts.map((p, i) => (
                      <circle key={i} cx={p.x*sc} cy={p.y*sc}
                        r={i === 0 && freeformDrawPts.length >= 3 ? 10 : 5}
                        fill={i === 0 && freeformDrawPts.length >= 3 ? `${flc.s}33` : flc.s}
                        stroke={i === 0 && freeformDrawPts.length >= 3 ? "#22c55e" : "white"} strokeWidth={2}
                        style={i === 0 && freeformDrawPts.length >= 3 ? { cursor:"pointer" } : {}} />
                    ))}
                    {freeformDrawPts.length >= 3 && (
                      <>
                        <circle cx={freeformDrawPts[0].x*sc} cy={freeformDrawPts[0].y*sc} r={16}
                          fill="none" stroke="#22c55e" strokeWidth={1.5} opacity={0.6}>
                          <animate attributeName="r" values="12;18;12" dur="1.5s" repeatCount="indefinite" />
                          <animate attributeName="opacity" values="0.8;0.2;0.8" dur="1.5s" repeatCount="indefinite" />
                        </circle>
                        <text x={freeformDrawPts[0].x*sc} y={freeformDrawPts[0].y*sc - 20} textAnchor="middle"
                          fill="#22c55e" fontSize={9} fontWeight="700" style={{ pointerEvents:"none" }}>
                          Klik om te sluiten
                        </text>
                      </>
                    )}
                    <text x={freeformDrawPts[0].x*sc} y={freeformDrawPts[0].y*sc - (freeformDrawPts.length >= 3 ? 32 : 12)} textAnchor="middle"
                      fill={flc.s} fontSize={9} fontWeight="700" style={{ pointerEvents:"none" }}>
                      {ft?.icon} {ft?.label || "Polygoon"}
                    </text>
                  </g>
                );
              })()}

              {/* Drawn lines */}
              {layers.lijnen && lines.map((ln) => {
                if (!ln.points || ln.points.length < 2) return null;
                const pts = ln.points.map((p) => `${p.x*sc},${p.y*sc}`).join(" ");
                const st = { hekwerk:{s:"#6B7280",d:"6,3"}, kabel:{s:"#EAB308",d:"8,4,2,4"},
                  rooilijn:{s:"#DC2626",d:"10,5"}, erfgrens:{s:"#014336",d:"4,4"},
                  vrij:{s:"#6B7280",d:""} }[ln.lineType] || { s:"#6B7280", d:"" };
                return <polyline key={ln.id} points={pts} fill="none"
                  stroke={selLine===ln.id ? C.accent : st.s} strokeWidth={selLine===ln.id ? 3 : 2}
                  strokeDasharray={st.d} style={{ cursor:"pointer" }}
                  onMouseDown={(e) => { e.stopPropagation(); setSelLine(ln.id); setSel(null); }} />;
              })}

              {/* Active line drawing */}
              {tool === "line" && linePts.length > 0 && (
                <g data-ui="drawing">
                  <polyline points={linePts.map((p) => `${p.x*sc},${p.y*sc}`).join(" ")}
                    fill="none" stroke={C.accent} strokeWidth={2} strokeDasharray="6,3" />
                  {linePts.map((p, i) => (
                    <circle key={i} cx={p.x*sc} cy={p.y*sc} r={4} fill="white" stroke={C.accent} strokeWidth={2} />
                  ))}
                </g>
              )}

              {/* Blocks — selected block rendered last for z-order */}
              {[...blocks].sort((a, b) => {
                if (a.id === sel) return 1;
                if (b.id === sel) return -1;
                return 0;
              }).map((b) => (
                <BlockShape key={b.id} b={b} sc={sc} selected={sel===b.id}
                  multiSel={multiSel.includes(b.id)} onMD={handleMD}
                  editingEdge={editingEdge}
                  onEditEdge={setEditingEdge}
                  onEdgeResize={(blockId, edgeIdx, np0, np1) => {
                    commit((prev) => prev.map((blk) => {
                      if (blk.id !== blockId || !blk.polygon) return blk;
                      const newPoly = blk.polygon.map((pt, j) => {
                        if (j === edgeIdx) return np0;
                        if (j === (edgeIdx + 1) % blk.polygon.length) return np1;
                        return pt;
                      });
                      const xs = newPoly.map(p=>p[0]), ys = newPoly.map(p=>p[1]);
                      return { ...blk, polygon: newPoly, x:Math.min(...xs), y:Math.min(...ys), w:Math.max(...xs)-Math.min(...xs), h:Math.max(...ys)-Math.min(...ys) };
                    }), "Maat gewijzigd");
                  }}
                  onResizeStart={(id, e) => {
                    dragStartRef.current = blocks;
                    const blk = blocks.find(bl => bl.id === id);
                    if (blk) window.__rsz = { id, mx:e.clientX, my:e.clientY, bw:blk.w, bh:blk.h };
                  }}
                  layerVis={layers[getT(b.type)?.cat] !== false} />
              ))}

              {/* Measure line */}
              {mPts.a && mPts.b && (() => {
                const x1=mPts.a.x*sc, y1=mPts.a.y*sc, x2=mPts.b.x*sc, y2=mPts.b.y*sc;
                const d = Math.sqrt((mPts.b.x-mPts.a.x)**2 + (mPts.b.y-mPts.a.y)**2);
                const mx = (x1+x2)/2, my = (y1+y2)/2;
                return (
                  <g data-ui="measure">
                    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={C.err} strokeWidth={2} strokeDasharray="6,3" />
                    <circle cx={x1} cy={y1} r={4} fill={C.err} />
                    <circle cx={x2} cy={y2} r={4} fill={C.err} />
                    <rect x={mx-25} y={my-10} width={50} height={20} rx={4} fill={C.err} />
                    <text x={mx} y={my+1} textAnchor="middle" dominantBaseline="middle"
                      fill="white" fontSize={10} fontWeight="700" fontFamily="'Inter',sans-serif">
                      {d.toFixed(1)}m
                    </text>
                  </g>
                );
              })()}
            </g>

            {/* Scale bar */}
            <g data-ui="scalebar" transform="translate(12,12)">
              <rect width={110} height={24} rx={5} fill="white" fillOpacity={0.95} stroke={C.border} />
              <line x1={8} y1={16} x2={8+20*sc} y2={16} stroke={C.primary} strokeWidth={2} />
              <line x1={8} y1={12} x2={8} y2={20} stroke={C.primary} strokeWidth={1.5} />
              <line x1={8+20*sc} y1={12} x2={8+20*sc} y2={20} stroke={C.primary} strokeWidth={1.5} />
              <text x={8+10*sc} y={10} textAnchor="middle" fill={C.text} fontSize={9} fontWeight="600" fontFamily="'Inter',sans-serif">20m</text>
            </g>
          </svg>

          {/* Zoom buttons */}
          <div style={{ position:"absolute", bottom: isMobile ? 64 : 44, right: isMobile ? 12 : "auto", left: isMobile ? "auto" : 12, display:"flex", flexDirection:"column", gap:2, zIndex:20 }}>
            <button onClick={() => setZoom(z => Math.min(8, z * 1.3))}
              style={{ width: isMobile ? 44 : 32, height: isMobile ? 44 : 32, border:`1px solid ${C.border}`, borderRadius:"8px 8px 0 0",
                background:"rgba(255,255,255,0.92)", cursor:"pointer", fontSize: isMobile ? 20 : 16, fontWeight:700,
                display:"flex", alignItems:"center", justifyContent:"center", color:C.text, lineHeight:1,
                backdropFilter:"blur(4px)" }}
              title="Inzoomen">+</button>
            <button onClick={() => setZoom(z => Math.max(0.15, z / 1.3))}
              style={{ width: isMobile ? 44 : 32, height: isMobile ? 44 : 32, border:`1px solid ${C.border}`, borderTop:"none",
                borderRadius:"0 0 8px 8px",
                background:"rgba(255,255,255,0.92)", cursor:"pointer", fontSize: isMobile ? 20 : 16, fontWeight:700,
                display:"flex", alignItems:"center", justifyContent:"center", color:C.text, lineHeight:1,
                backdropFilter:"blur(4px)" }}
              title="Uitzoomen">−</button>
          </div>

          {/* Tool indicator */}
          {(tool !== "select" || pendingBlock) && (
            <div style={{ position:"absolute", top:8, left:"50%", transform:"translateX(-50%)",
              fontSize:10, fontWeight:600, color:"white",
              background: tool==="measure" ? C.err : C.primary,
              padding:"4px 12px", borderRadius:16 }}>
              {pendingBlock ? `📍 Klik op de kaart om ${getT(pendingBlock.type)?.label || "blok"} te plaatsen · ESC annuleer`
                : tool==="measure" ? "📏 Klik 2 punten · ESC stop"
                : tool==="line" ? "✏️ Klik punten · Enter afronden · ESC stop"
                : tool==="freeform" ? `✎ ${getT(freeformType)?.label || "Polygoon"} — klik omtrek · Enter sluiten · ESC stop`
                : "⬠ Klik kavelpunten · Enter sluiten · ESC stop"}
            </div>
          )}
          {altHeld && (
            <div style={{ position:"absolute", bottom:40, left:"50%", transform:"translateX(-50%)",
              fontSize:10, fontWeight:600, color:"white", background:C.snap,
              padding:"4px 12px", borderRadius:16, pointerEvents:"none", opacity:0.9 }}>
              🧲 Snap uit — Alt loslaten om snap te herstellen
            </div>
          )}
          {kavelEdit && (
            <div style={{ position:"absolute", top:8, left:"50%", transform:"translateX(-50%)",
              fontSize:10, fontWeight:600, color:"white", background:C.accent,
              padding:"4px 12px", borderRadius:16 }}>
              ⬠ Kavel bewerken — sleep punten · klik + = nieuw punt · rechtermuisklik = verwijder
            </div>
          )}

          {/* Bottom shortcuts — desktop only */}
          {!isMobile && (
            <div style={{ position:"absolute", bottom:6, left:"50%", transform:"translateX(-50%)",
              fontSize:8, color:C.textSec, background:"rgba(255,255,255,0.92)",
              padding:"3px 10px", borderRadius:5, border:`1px solid ${C.border}`,
              display:"flex", gap:10, backdropFilter:"blur(8px)" }}>
              {[["V","select"],["M","meten"],["N","lijn"],["K","kavel"],["R","roteer"],
                ["Del","verwijder"],["⌘Z","undo"],["⇧⌘Z","redo"],["Space","pan"],["Shift+klik","multi"]].map(([k, l]) => (
                <span key={k}>
                  <kbd style={{ padding:"1px 3px", background:C.surfAlt, borderRadius:2,
                    border:`1px solid ${C.border}`, fontSize:7 }}>{k}</kbd> {l}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* === RIGHT SIDEBAR: PROPERTIES — hidden on mobile === */}
        {!isMobile && <div style={{ width:210, background:C.surface, borderLeft:`1px solid ${C.border}`,
          display:"flex", flexDirection:"column", flexShrink:0, overflowY:"auto", overflowX:"hidden" }}>
          {selB && selT ? (
            <>
              <div style={{ padding:"8px 12px", borderBottom:`1px solid ${C.border}`, background:C.surfAlt }}>
                <div style={{ fontSize:9, color:C.textMut, fontWeight:600, letterSpacing:1.5 }}>PROPERTIES</div>
                <div style={{ fontSize:12, fontWeight:700, color:C.primary, marginTop:2 }}>
                  {selT.icon} {selT.label} {selB.locked && "🔒"}
                </div>
              </div>
              <div style={{ padding:"8px 12px", display:"flex", flexDirection:"column", gap:5 }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:4 }}>
                  <Inp label="X" value={selB.x} onChange={(v) => updateB("x",v)} step={0.5} unit="m" />
                  <Inp label="Y" value={selB.y} onChange={(v) => updateB("y",v)} step={0.5} unit="m" />
                  <Inp label="B" value={selB.w} onChange={(v) => updateB("w",v)} step={0.5} unit="m" />
                  <Inp label="H" value={selB.h} onChange={(v) => updateB("h",v)} step={0.5} unit="m" />
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:10,
                  padding:"3px 0", borderTop:`1px solid ${C.borderLt}` }}>
                  <span style={{ color:C.textSec }}>Oppervlakte</span>
                  <span style={{ fontWeight:700, color:C.primary }}>
                    {(selB.w*selB.h).toLocaleString("nl-NL", { maximumFractionDigits:0 })} m²
                  </span>
                </div>
                {!selB.polygon && (
                  <>
                    <div style={{ fontSize:9, color:C.textMut, fontWeight:700, borderTop:`1px solid ${C.border}`, paddingTop:5 }}>ROTATIE</div>
                    <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                      <input type="range" min={0} max={359} step={1} value={selB.rotation||0}
                        onChange={(e) => setBlockRotation(+e.target.value)}
                        style={{ flex:1, accentColor:C.accent }} />
                      <input type="number" min={0} max={359} value={selB.rotation||0}
                        onChange={(e) => setBlockRotation(+e.target.value)}
                        style={{ width:42, padding:"2px 4px", fontSize:10, border:`1px solid ${C.border}`,
                          borderRadius:3, fontFamily:"inherit", textAlign:"right" }} />
                      <span style={{ fontSize:9, color:C.textMut }}>°</span>
                      <Btn onClick={rotBlock} small>+90</Btn>
                    </div>
                  </>
                )}
                <div style={{ display:"flex", gap:3, flexWrap:"wrap" }}>
                  <Btn onClick={lockBlock} small>{selB.locked ? "🔓" : "🔒"}</Btn>
                  {selT.cat === "voertuigen" && (
                    <Btn onClick={toggleTurn} small>
                      {selB.params?.showTurn ? "◯ Hide" : "◯ Show"} R
                    </Btn>
                  )}
                </div>
                {/* Opacity slider */}
                <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:5 }}>
                  <div style={{ fontSize:9, color:C.textMut, fontWeight:700, marginBottom:3 }}>OPACITY</div>
                  <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                    <input type="range" min={5} max={100} step={5}
                      value={Math.round((selB.opacity ?? 1.0) * 100)}
                      onChange={(e) => updateB("opacity", +e.target.value / 100)}
                      style={{ flex:1, accentColor:C.accent }} />
                    <span style={{ fontSize:9, color:C.textSec, minWidth:26, textAlign:"right" }}>
                      {Math.round((selB.opacity ?? 1.0) * 100)}%
                    </span>
                  </div>
                </div>
              </div>
              {selT.pd.length > 0 && (
                <div style={{ padding:"0 12px 8px", display:"flex", flexDirection:"column", gap:5 }}>
                  <div style={{ fontSize:9, color:C.textMut, fontWeight:700,
                    borderTop:`1px solid ${C.border}`, paddingTop:8 }}>PARAMETERS</div>
                  {selT.pd.map((pd) => (
                    <Inp key={pd.k} label={pd.l} value={selB.params?.[pd.k] ?? ""}
                      onChange={(v) => updateP(pd.k, v)} step={pd.s} />
                  ))}
                </div>
              )}
              {/* Polygon road width tool */}
              {selB.polygon && selB.polygon.length === 4 && (selB.type === "road" || selB.type === "voorterrein") && (() => {
                // Calculate current width: average distance between the two "sides"
                const pts = selB.polygon;
                const side1Mid = [(pts[0][0]+pts[1][0])/2, (pts[0][1]+pts[1][1])/2];
                const side2Mid = [(pts[2][0]+pts[3][0])/2, (pts[2][1]+pts[3][1])/2];
                const currentWidth = Math.round(Math.hypot(side1Mid[0]-side2Mid[0], side1Mid[1]-side2Mid[1]) * 10) / 10;
                
                const setRoadWidth = (newWidth) => {
                  const w = +newWidth;
                  if (w <= 0 || isNaN(w)) return;
                  // Center line
                  const cx0 = (pts[0][0]+pts[3][0])/2, cy0 = (pts[0][1]+pts[3][1])/2;
                  const cx1 = (pts[1][0]+pts[2][0])/2, cy1 = (pts[1][1]+pts[2][1])/2;
                  // Direction perpendicular to center line
                  const dx = cx1 - cx0, dy = cy1 - cy0;
                  const len = Math.hypot(dx, dy) || 1;
                  const nx = -dy / len, ny = dx / len;
                  const half = w / 2;
                  const newPoly = [
                    [cx0 + nx * half, cy0 + ny * half],
                    [cx1 + nx * half, cy1 + ny * half],
                    [cx1 - nx * half, cy1 - ny * half],
                    [cx0 - nx * half, cy0 - ny * half],
                  ];
                  const xs = newPoly.map(p => p[0]), ys = newPoly.map(p => p[1]);
                  commit((prev) => prev.map(b => b.id === selB.id
                    ? { ...b, polygon: newPoly, x: Math.min(...xs), y: Math.min(...ys),
                        w: Math.max(...xs) - Math.min(...xs) || 1, h: Math.max(...ys) - Math.min(...ys) || 1 } : b), "Wegbreedte gewijzigd");
                };
                return (
                  <div style={{ padding:"0 12px 8px" }}>
                    <div style={{ fontSize:9, color:C.textMut, fontWeight:700,
                      borderTop:`1px solid ${C.border}`, paddingTop:8, marginBottom:4 }}>WEGBREEDTE</div>
                    <Inp label="Breedte (m)" value={currentWidth} onChange={setRoadWidth} step={0.5} unit="m" />
                  </div>
                );
              })()}
              {/* Polygon vertex editor */}
              {selB.polygon && selB.polygon.length >= 3 && (() => {
                const updateVertex = (idx, axis, val) => {
                  const newPoly = selB.polygon.map((p) => [...p]);
                  newPoly[idx][axis] = +val;
                  const xs = newPoly.map((p) => p[0]), ys = newPoly.map((p) => p[1]);
                  const nx = Math.min(...xs), ny = Math.min(...ys);
                  const nw = Math.max(...xs) - nx, nh = Math.max(...ys) - ny;
                  commit((prev) => prev.map((b) => b.id === selB.id
                    ? { ...b, polygon: newPoly, x: nx, y: ny, w: nw || 1, h: nh || 1 } : b), "Hoekpunt gewijzigd");
                };
                const removeVertex = (idx) => {
                  if (selB.polygon.length <= 3) return;
                  const newPoly = selB.polygon.filter((_, i) => i !== idx);
                  const xs = newPoly.map((p) => p[0]), ys = newPoly.map((p) => p[1]);
                  const nx = Math.min(...xs), ny = Math.min(...ys);
                  const nw = Math.max(...xs) - nx, nh = Math.max(...ys) - ny;
                  commit((prev) => prev.map((b) => b.id === selB.id
                    ? { ...b, polygon: newPoly, x: nx, y: ny, w: nw || 1, h: nh || 1 } : b), "Hoekpunt verwijderd");
                };
                return (
                  <div style={{ padding:"0 12px 8px", display:"flex", flexDirection:"column", gap:3 }}>
                    <div style={{ fontSize:9, color:C.textMut, fontWeight:700,
                      borderTop:`1px solid ${C.border}`, paddingTop:8 }}>HOEKPUNTEN ({selB.polygon.length})</div>
                    {selB.polygon.map((pt, i) => (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:3 }}>
                        <span style={{ fontSize:8, color:C.textSec, minWidth:16 }}>P{i+1}</span>
                        <Inp label="X" value={pt[0]} onChange={(v) => updateVertex(i, 0, v)} step={0.5} />
                        <Inp label="Y" value={pt[1]} onChange={(v) => updateVertex(i, 1, v)} step={0.5} />
                        {selB.polygon.length > 3 && (
                          <button onClick={() => removeVertex(i)}
                            style={{ background:"none", border:"none", color:C.danger || "#ef4444",
                              cursor:"pointer", fontSize:12, padding:0, lineHeight:1 }}>✕</button>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
              {/* Parking capacity calculator */}
              {(selB.type === "parking_car" || selB.type === "parking_truck") && (() => {
                const sw = selB.params?.sw || 2.5;
                const sl = selB.params?.sl || 5.0;
                const roadW = selB.params?.roadW || 6.0;
                const rows = selB.params?.rows || 2;
                const slotsPerRow = Math.floor(selB.w / sw);
                const totalSlots = slotsPerRow * rows;
                const idealH = rows >= 2 ? (2 * sl + roadW) : sl;
                return (
                  <div style={{ padding:"0 12px 8px", display:"flex", flexDirection:"column", gap:3 }}>
                    <div style={{ fontSize:9, color:C.textMut, fontWeight:700,
                      borderTop:`1px solid ${C.border}`, paddingTop:8 }}>CAPACITEIT</div>
                    <div style={{ fontSize:10, color:C.text, lineHeight:1.5 }}>
                      <div>{slotsPerRow} × {rows} rij = <strong style={{ color:C.primary }}>{totalSlots} pp</strong></div>
                      <div style={{ fontSize:9, color:C.textSec }}>
                        Vakken {sw}×{sl}m · Rijweg {roadW}m
                      </div>
                      <div style={{ fontSize:9, color:C.textSec }}>
                        Ideale hoogte: {idealH.toFixed(1)}m
                      </div>
                    </div>
                    <Btn onClick={() => {
                      updateB("h", idealH);
                    }} small>📐 Pas hoogte aan ({idealH.toFixed(1)}m)</Btn>
                  </div>
                );
              })()}
              {selB.type === "warehouse" && (
                <div style={{ padding:"0 12px 8px", display:"flex", flexDirection:"column", gap:3 }}>
                  <div style={{ fontSize:9, color:C.textMut, fontWeight:700,
                    borderTop:`1px solid ${C.border}`, paddingTop:8 }}>STRAMIEN</div>
                  {[{ x:10.8, y:21.6 }, { x:12, y:24 }].map((s) => (
                    <Btn key={s.x} onClick={() => {
                      updateP("bayX", s.x); updateP("bayY", s.y);
                      setTimeout(() => {
                        updateP("baysX", Math.round(selB.w/s.x));
                        updateP("baysY", Math.round(selB.h/s.y));
                      }, 10);
                    }} small>📐 {s.x}×{s.y}m</Btn>
                  ))}
                </div>
              )}
              <div style={{ padding:"6px 12px", borderTop:`1px solid ${C.border}`,
                display:"flex", gap:3, marginTop:"auto" }}>
                <Btn onClick={dupBlock} style={{ flex:1 }}>⧉ Dup</Btn>
                <Btn onClick={delBlock} danger style={{ flex:1 }}>✕ Del</Btn>
              </div>
            </>
          ) : (
            <div style={{ padding:"14px 12px", color:C.textSec, fontSize:11 }}>
              <div style={{ fontSize:9, color:C.textMut, fontWeight:700, letterSpacing:1.5, marginBottom:8 }}>PROPERTIES</div>
              <p>Selecteer een blok om te bewerken.</p>
            </div>
          )}
        </div>}
      </div>

      {/* === MOBILE BOTTOM TAB BAR === */}
      {isMobile && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-around",
          padding:"6px 0", borderTop:`1px solid ${C.border}`, background:C.surface, flexShrink:0,
          paddingBottom:"max(6px, env(safe-area-inset-bottom))" }}>
          {[
            { id:"blocks", icon:"📦", label:"Blokken" },
            { id:"map", icon:"🗺", label:"Kaart" },
            { id:"tools", icon:"🔧", label:"Tools" },
            { id:"props", icon:"📐", label:"Props" },
            { id:"more", icon:"⚙️", label:"Meer" },
          ].map((tab) => (
            <button key={tab.id} onClick={() => setMobileTab(mobileTab === tab.id ? null : tab.id)}
              style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2,
                background:"none", border:"none", cursor:"pointer", padding:"4px 8px",
                color: mobileTab === tab.id ? C.primary : C.textMut, fontSize:18, minWidth:44, minHeight:44 }}>
              <span>{tab.icon}</span>
              <span style={{ fontSize:8, fontWeight: mobileTab === tab.id ? 700 : 500 }}>{tab.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* === MOBILE BOTTOM SHEETS === */}
      {isMobile && (
        <>
          {/* Blocks sheet */}
          <MobileSheet open={mobileTab === "blocks"} onClose={() => setMobileTab(null)} title="📦 Blokken plaatsen">
            <div style={{ padding:"0 12px" }}>
              {cats.map((cat) => (
                <div key={cat}>
                  <div style={{ padding:"8px 0 4px", fontSize:9, color:C.textMut, letterSpacing:1.5, fontWeight:700, textTransform:"uppercase" }}>{cat}</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                    {BT.filter((t) => t.cat===cat).map((t) => (
                      <button key={t.id} onClick={() => { addBlock(t.id); setMobileTab(null); }}
                        style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 12px",
                          background:C.surfAlt, border:`1px solid ${C.border}`, borderRadius:8,
                          cursor:"pointer", fontSize:11, fontFamily:"inherit", textAlign:"left", minHeight:44 }}>
                        <span style={{ width:24, height:24, borderRadius:5, background:getLC(cat, t.id).f,
                          border:`1.5px solid ${getLC(cat, t.id).s}`, display:"flex", alignItems:"center",
                          justifyContent:"center", fontSize:12, flexShrink:0 }}>{t.icon}</span>
                        <div>
                          <div style={{ fontWeight:600, color:C.text }}>{t.label}</div>
                          <div style={{ fontSize:9, color:C.textMut }}>{t.dw}×{t.dh}m</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </MobileSheet>

          {/* Map sheet */}
          <MobileSheet open={mobileTab === "map"} onClose={() => setMobileTab(null)} title="🗺 Kaartinstellingen">
            <div style={{ padding:"0 12px", display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ fontSize:10, color:C.textMut, fontWeight:700 }}>ADRES ZOEKEN</div>
              <div style={{ display:"flex", gap:4 }}>
                <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                  onKeyDown={(e) => e.key==="Enter" && doSearch()}
                  placeholder="Adres, postcode..."
                  style={{ flex:1, padding:"8px 10px", fontSize:12, border:`1px solid ${C.border}`,
                    borderRadius:8, fontFamily:"inherit", outline:"none", background:C.surfAlt }} />
                <Btn onClick={doSearch} primary>{searching ? "…" : "🔍"}</Btn>
              </div>
              {searchRes.length > 0 && (
                <div style={{ border:`1px solid ${C.border}`, borderRadius:8, overflow:"hidden" }}>
                  {searchRes.map((r, i) => (
                    <button key={i} onClick={() => { pickLoc(r); setMobileTab(null); }} style={{
                      display:"block", width:"100%", padding:"10px 12px", fontSize:12, textAlign:"left",
                      border:"none", borderBottom:`1px solid ${C.borderLt}`, cursor:"pointer",
                      background:"white", fontFamily:"inherit", color:C.text, minHeight:44,
                    }}>{r.label}</button>
                  ))}
                </div>
              )}
              {mapCfg.rdX > 0 && (
                <div style={{ padding:8, background:C.accentDim, borderRadius:8, fontSize:10, color:C.primary }}>
                  📍 RD: {mapCfg.rdX.toFixed(0)}, {mapCfg.rdY.toFixed(0)}
                </div>
              )}
              <Btn onClick={loadBAG} primary disabled={!mapCfg.rdX || bagLoading}>
                {bagLoading ? "Laden..." : `🏢 BAG panden ${bagPanden.length > 0 ? `(${bagPanden.length})` : "laden"}`}
              </Btn>
              <div style={{ fontSize:10, color:C.textMut, fontWeight:700 }}>BEREIK: {mapCfg.viewport}m</div>
              <input type="range" min={200} max={1000} step={50} value={mapCfg.viewport}
                onChange={(e) => setMapCfg((c) => ({ ...c, viewport:+e.target.value }))}
                style={{ width:"100%", accentColor:C.accent }} />
              <div style={{ fontSize:10, color:C.textMut, fontWeight:700 }}>TRANSPARANTIE: {Math.round(mapCfg.opacity*100)}%</div>
              <input type="range" min={0} max={100} value={mapCfg.opacity*100}
                onChange={(e) => setMapCfg((c) => ({ ...c, opacity:+e.target.value/100 }))}
                style={{ width:"100%", accentColor:C.accent }} />
            </div>
          </MobileSheet>

          {/* Tools sheet */}
          <MobileSheet open={mobileTab === "tools"} onClose={() => setMobileTab(null)} title="🔧 Gereedschap">
            <div style={{ padding:"0 12px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {[
                { icon:"↖", label:"Selecteer", fn:() => { setTool("select"); setMobileTab(null); }, active: tool==="select" },
                { icon:"📏", label:"Meten", fn:() => { setTool("measure"); setMPts({a:null,b:null}); setMobileTab(null); }, active: tool==="measure" },
                { icon:"✏️", label:"Lijn", fn:() => { setTool("line"); setLinePts([]); setMobileTab(null); }, active: tool==="line" },
                { icon:"⬠", label:"Kavel", fn:() => { setTool("kavel"); setKavelDrawPts([]); setMobileTab(null); }, active: tool==="kavel" },
                { icon:"✎", label:"Vrije vorm", fn:() => { startFreeform("laadplein"); setMobileTab(null); }, active: tool==="freeform" },
                { icon:"↩", label:"Ongedaan", fn:() => hD({type:"U"}) },
                { icon:"↪", label:"Opnieuw", fn:() => hD({type:"R"}) },
              ].map((t, i) => (
                <button key={i} onClick={t.fn}
                  style={{ display:"flex", alignItems:"center", gap:8, padding:"12px",
                    background: t.active ? C.accentDim : C.surfAlt, border:`1px solid ${t.active ? C.accentMid : C.border}`,
                    borderRadius:8, cursor:"pointer", fontSize:12, fontFamily:"inherit", minHeight:44,
                    color: t.active ? C.primary : C.text, fontWeight: t.active ? 700 : 500 }}>
                  <span style={{ fontSize:16 }}>{t.icon}</span>
                  {t.label}
                </button>
              ))}
            </div>
          </MobileSheet>

          {/* Properties sheet */}
          <MobileSheet open={mobileTab === "props"} onClose={() => setMobileTab(null)} title="📐 Eigenschappen">
            {selB && selT ? (
              <div style={{ padding:"0 12px", display:"flex", flexDirection:"column", gap:8 }}>
                <div style={{ fontSize:13, fontWeight:700, color:C.primary }}>
                  {selT.icon} {selT.label} {selB.locked && "🔒"}
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                  <Inp label="X" value={selB.x} onChange={(v) => updateB("x",v)} step={0.5} unit="m" />
                  <Inp label="Y" value={selB.y} onChange={(v) => updateB("y",v)} step={0.5} unit="m" />
                  <Inp label="B" value={selB.w} onChange={(v) => updateB("w",v)} step={0.5} unit="m" />
                  <Inp label="H" value={selB.h} onChange={(v) => updateB("h",v)} step={0.5} unit="m" />
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, padding:"4px 0", borderTop:`1px solid ${C.borderLt}` }}>
                  <span style={{ color:C.textSec }}>Oppervlakte</span>
                  <span style={{ fontWeight:700, color:C.primary }}>{(selB.w*selB.h).toLocaleString("nl-NL", { maximumFractionDigits:0 })} m²</span>
                </div>
                {selT.pd.length > 0 && (
                  <>
                    <div style={{ fontSize:10, color:C.textMut, fontWeight:700, borderTop:`1px solid ${C.border}`, paddingTop:8 }}>PARAMETERS</div>
                    {selT.pd.map((pd) => (
                      <Inp key={pd.k} label={pd.l} value={selB.params?.[pd.k] ?? ""} onChange={(v) => updateP(pd.k, v)} step={pd.s} />
                    ))}
                  </>
                )}
                <div style={{ display:"flex", gap:6, marginTop:4 }}>
                  <Btn onClick={dupBlock} style={{ flex:1 }}>⧉ Dupliceer</Btn>
                  <Btn onClick={rotBlock} style={{ flex:1 }}>↻ Roteer</Btn>
                  <Btn onClick={delBlock} danger style={{ flex:1 }}>✕ Verwijder</Btn>
                </div>
              </div>
            ) : (
              <div style={{ padding:"20px 12px", color:C.textSec, fontSize:12, textAlign:"center" }}>
                Selecteer een blok om te bewerken
              </div>
            )}
          </MobileSheet>

          {/* More sheet */}
          <MobileSheet open={mobileTab === "more"} onClose={() => setMobileTab(null)} title="⚙️ Meer opties">
            <div style={{ padding:"0 12px", display:"flex", flexDirection:"column", gap:8 }}>
              {scenarios.length > 0 && (
                <div>
                  <div style={{ fontSize:10, color:C.textMut, fontWeight:700, marginBottom:6 }}>SCENARIO'S</div>
                  <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                    {scenarios.map((s) => (
                      <button key={s.id} onClick={() => { switchScenario(s.id); setMobileTab(null); }}
                        style={{ background: s.id===actSc ? C.accentDim : C.surfAlt, color: s.id===actSc ? C.primary : C.textSec,
                          border:`1px solid ${s.id===actSc ? C.accentMid : C.border}`, padding:"8px 14px", fontSize:11,
                          cursor:"pointer", borderRadius:8, fontWeight: s.id===actSc ? 700 : 500, fontFamily:"inherit", minHeight:44 }}>
                        {s.name}
                      </button>
                    ))}
                    <Btn onClick={() => { addScenario(); setMobileTab(null); }}>+ Nieuw</Btn>
                  </div>
                </div>
              )}
              <div style={{ height:1, background:C.border }} />
              <button onClick={() => { setExportOpen(true); setMobileTab(null); }}
                style={{ display:"flex", alignItems:"center", gap:8, padding:"12px",
                  background:C.surfAlt, border:`1px solid ${C.border}`, borderRadius:8,
                  cursor:"pointer", fontSize:12, fontFamily:"inherit", minHeight:44, color:C.text }}>
                ⬇ Exporteren (PDF/PNG/SVG/DXF)
              </button>
              <button onClick={() => { setAiRenderOpen(true); setMobileTab(null); }}
                style={{ display:"flex", alignItems:"center", gap:8, padding:"12px",
                  background:"linear-gradient(135deg, #014336, #02714D)", border:"none", borderRadius:8,
                  cursor:"pointer", fontSize:12, fontFamily:"inherit", minHeight:44, color:"#fff", fontWeight:700 }}>
                🎨 AI Render
              </button>
              <button onClick={() => { setShow3DView(true); setMobileTab(null); }}
                style={{ display:"flex", alignItems:"center", gap:8, padding:"12px",
                  background:"linear-gradient(135deg, #1E40AF, #3B82F6)", border:"none", borderRadius:8,
                  cursor:"pointer", fontSize:12, fontFamily:"inherit", minHeight:44, color:"#fff", fontWeight:700 }}>
                🎮 3D Preview
              </button>
              <div style={{ height:1, background:C.border }} />
              <div style={{ fontSize:10, color:C.textMut, fontWeight:700 }}>LAGEN</div>
              {Object.entries(layers).map(([k, v]) => (
                <label key={k} style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color:C.text, minHeight:36, cursor:"pointer" }}>
                  <input type="checkbox" checked={v} onChange={() => setLayers(l => ({ ...l, [k]: !l[k] }))}
                    style={{ accentColor:C.accent, width:18, height:18 }} />
                  {k.charAt(0).toUpperCase() + k.slice(1)}
                </label>
              ))}
            </div>
          </MobileSheet>
        </>
      )}

      {/* CONTEXT MENU */}
      {ctxMenu && <CtxMenu x={ctxMenu.x} y={ctxMenu.y} items={ctxItems} onClose={() => setCtxMenu(null)} />}

      {/* AI RENDER MODAL */}
      {aiRenderOpen && (
        <div style={{ position:"fixed", inset:0, zIndex:10000, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.6)", backdropFilter:"blur(4px)" }}
            onClick={() => { if (!aiRendering) setAiRenderOpen(false); }} />
          <div style={{ position:"relative", background:C.surface, borderRadius: isMobile ? 0 : 12, padding: isMobile ? 16 : 24, width: isMobile ? "100%" : 560,
            maxHeight: isMobile ? "100vh" : "90vh", height: isMobile ? "100vh" : "auto", overflow:"auto", boxShadow:"0 25px 60px rgba(0,0,0,0.3)", fontFamily:"'Inter',sans-serif" }}>
            
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
              <div>
                <h2 style={{ fontSize:16, fontWeight:800, color:C.primary, margin:0 }}>🎨 AI 3D Render</h2>
                <p style={{ fontSize:11, color:C.textSec, margin:"4px 0 0" }}>
                  Genereer een fotorealistische 3D-visualisatie van je terreinplan
                </p>
              </div>
              {!aiRendering && (
                <button onClick={() => setAiRenderOpen(false)}
                  style={{ background:"none", border:"none", fontSize:18, cursor:"pointer", color:C.textMut, padding:4 }}>✕</button>
              )}
            </div>

            {/* 3D Screenshot indicator — shown ABOVE camera selection */}
            {screenshot3D && (
              <div style={{ marginBottom:12, background:C.accentDim, padding:"10px 12px", borderRadius:6,
                border:`1px solid ${C.accentMid}` }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:C.primary }}>📸 3D Screenshot actief</span>
                  <button onClick={() => setScreenshot3D(null)}
                    style={{ marginLeft:"auto", fontSize:9, color:C.textSec, background:"none", border:"none", cursor:"pointer" }}>✕ Verwijder</button>
                </div>
                <img src={screenshot3D?.dataUrl || screenshot3D} alt="3D screenshot" style={{ width:"100%", borderRadius:4, border:`1px solid ${C.border}` }} />
                <div style={{ fontSize:9, color:C.textSec, marginTop:4 }}>
                  Camera, perspectief en verhoudingen worden exact overgenomen uit dit screenshot. De AI verrijkt dit met fotorealistische materialen en belichting.
                </div>
              </div>
            )}

            {/* Camera preset — hidden when 3D screenshot is active */}
            {!screenshot3D && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:10, fontWeight:700, color:C.textMut, marginBottom:6, letterSpacing:1 }}>CAMERAHOEK</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                  {[
                    { id:"isometric", icon:"🔷", label:"Isometrisch", desc:"45° vanuit zuidoost" },
                    { id:"birds_eye", icon:"🦅", label:"Vogelvlucht", desc:"60° van bovenaf" },
                    { id:"street", icon:"🚶", label:"Straatperspectief", desc:"Ooghoogte vanaf entree" },
                    { id:"side", icon:"👁", label:"Zijaanzicht", desc:"30° vanaf de zijkant" },
                  ].map(cam => (
                    <button key={cam.id} onClick={() => setAiRenderCamera(cam.id)}
                      style={{ padding:"10px 12px", border:`2px solid ${aiRenderCamera===cam.id ? C.accent : C.border}`,
                        borderRadius:8, background:aiRenderCamera===cam.id ? C.accentDim : "transparent",
                        cursor:"pointer", textAlign:"left", fontFamily:"inherit", transition:"all 0.15s" }}>
                      <div style={{ fontSize:16, marginBottom:2 }}>{cam.icon}</div>
                      <div style={{ fontSize:11, fontWeight:700, color:C.text }}>{cam.label}</div>
                      <div style={{ fontSize:9, color:C.textSec }}>{cam.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Style preset — only style is relevant when screenshot active */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:10, fontWeight:700, color:C.textMut, marginBottom:6, letterSpacing:1 }}>VISUELE STIJL</div>
              <div style={{ display:"flex", gap:6 }}>
                {[
                  { id:"photorealistic", icon:"📸", label:"Fotorealistisch" },
                  { id:"sketch", icon:"✏️", label:"Architectuur-sketch" },
                  { id:"evening", icon:"🌅", label:"Avondsfeer" },
                ].map(st => (
                  <button key={st.id} onClick={() => setAiRenderStyle(st.id)}
                    style={{ flex:1, padding:"10px", border:`2px solid ${aiRenderStyle===st.id ? C.accent : C.border}`,
                      borderRadius:8, background:aiRenderStyle===st.id ? C.accentDim : "transparent",
                      cursor:"pointer", textAlign:"center", fontFamily:"inherit", transition:"all 0.15s" }}>
                    <div style={{ fontSize:16 }}>{st.icon}</div>
                    <div style={{ fontSize:10, fontWeight:600, color:C.text, marginTop:2 }}>{st.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Model selector */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:10, fontWeight:700, color:C.textMut, marginBottom:6, letterSpacing:1, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span>AI MODEL</span>
                <span style={{
                  fontSize:9, padding:"2px 8px", borderRadius:10, fontWeight:700, letterSpacing:0.5,
                  background: aiRenderModel.startsWith("openai/") ? "#10A37F" : "#4285F4",
                  color:"#fff",
                }}>
                  {aiRenderModel.startsWith("openai/") ? "🟢 OPENAI" : "🔵 GEMINI"}
                </span>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                {[
                  { id:"google/gemini-3.1-flash-image-preview", icon:"⚡", label:"Snel", desc:"Gemini · Nano 2", provider:"gemini" },
                  { id:"google/gemini-2.5-flash-image", icon:"🎯", label:"Standaard", desc:"Gemini · Nano", provider:"gemini" },
                  { id:"google/gemini-3-pro-image-preview", icon:"👑", label:"Pro", desc:"Gemini · Beste", provider:"gemini" },
                  { id:"openai/gpt-image-2", icon:"🏆", label:"OpenAI", desc:"gpt-image-2 · Fotoreal", provider:"openai" },
                ].map(m => {
                  const isOpenai = m.provider === "openai";
                  const accent = isOpenai ? "#10A37F" : C.accent;
                  const accentDim = isOpenai ? "rgba(16,163,127,0.12)" : C.accentDim;
                  const selected = aiRenderModel === m.id;
                  return (
                    <button key={m.id} onClick={() => setAiRenderModel(m.id)}
                      style={{ padding:"10px", border:`2px solid ${selected ? accent : C.border}`,
                        borderRadius:8, background:selected ? accentDim : "transparent",
                        cursor:"pointer", textAlign:"center", fontFamily:"inherit", transition:"all 0.15s",
                        position:"relative" }}>
                      {isOpenai && (
                        <div style={{ position:"absolute", top:4, right:4, fontSize:8, fontWeight:700,
                          background:"#10A37F", color:"#fff", padding:"1px 5px", borderRadius:4 }}>
                          NIEUW
                        </div>
                      )}
                      <div style={{ fontSize:16 }}>{m.icon}</div>
                      <div style={{ fontSize:10, fontWeight:600, color:C.text, marginTop:2 }}>{m.label}</div>
                      <div style={{ fontSize:8, color:C.textSec }}>{m.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ACTIEF MODEL BANNER + Test verbinding (alleen bij OpenAI) */}
            {(() => {
              const isOpenai = aiRenderModel.startsWith("openai/");
              const accent = isOpenai ? "#10A37F" : "#4285F4";
              const accentBg = isOpenai ? "rgba(16,163,127,0.10)" : "rgba(66,133,244,0.10)";
              return (
                <div style={{ marginBottom:14, padding:"10px 12px", borderRadius:8,
                  background:accentBg, border:`2px solid ${accent}` }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <div style={{ width:8, height:8, borderRadius:"50%", background:accent, boxShadow:`0 0 8px ${accent}` }} />
                      <div>
                        <div style={{ fontSize:9, fontWeight:700, color:C.textMut, letterSpacing:1 }}>ACTIEF MODEL</div>
                        <div style={{ fontSize:12, fontWeight:700, color:accent, fontFamily:"'JetBrains Mono', monospace" }}>
                          {aiRenderModel}
                        </div>
                      </div>
                    </div>
                    {isOpenai && (
                      <button
                        disabled={testingOpenai}
                        onClick={async () => {
                          setTestingOpenai(true);
                          setOpenaiHealth(null);
                          try {
                            const { data, error } = await supabase.functions.invoke("openai-health");
                            if (error) throw error;
                            setOpenaiHealth(data);
                          } catch (e) {
                            setOpenaiHealth({ ok:false, error: e.message || String(e) });
                          } finally {
                            setTestingOpenai(false);
                          }
                        }}
                        style={{ padding:"6px 10px", fontSize:10, fontWeight:600,
                          background:"#fff", color:accent, border:`1px solid ${accent}`,
                          borderRadius:6, cursor:testingOpenai ? "wait" : "pointer", fontFamily:"inherit" }}>
                        {testingOpenai ? "⏳ Test…" : "🔌 Test verbinding"}
                      </button>
                    )}
                  </div>
                  {openaiHealth && isOpenai && (
                    <div style={{ marginTop:8, padding:"8px 10px", borderRadius:6, fontSize:10,
                      background: openaiHealth.ok ? "rgba(34,197,94,0.10)" : "rgba(239,68,68,0.10)",
                      border:`1px solid ${openaiHealth.ok ? "#22C55E" : "#EF4444"}`,
                      color: openaiHealth.ok ? "#16A34A" : "#DC2626", fontWeight:500 }}>
                      {openaiHealth.ok
                        ? `✅ ${openaiHealth.message || "Verbinding OK"}`
                        : `❌ ${openaiHealth.error || "Onbekende fout"}${openaiHealth.httpStatus ? ` [HTTP ${openaiHealth.httpStatus}]` : ""}`}
                      {openaiHealth.openaiError && (
                        <div style={{ marginTop:4, fontSize:9, fontFamily:"monospace", opacity:0.8 }}>
                          {typeof openaiHealth.openaiError === "string"
                            ? openaiHealth.openaiError
                            : JSON.stringify(openaiHealth.openaiError).substring(0, 200)}
                        </div>
                      )}
                    </div>
                  )}
                  {isOpenai && (
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginTop:10 }}>
                      <div>
                        <div style={{ fontSize:9, fontWeight:700, color:C.textMut, marginBottom:3, letterSpacing:0.5 }}>RESOLUTIE</div>
                        <select value={openaiSize} onChange={(e) => setOpenaiSize(e.target.value)}
                          style={{ width:"100%", padding:"5px 8px", fontSize:11, border:`1px solid ${C.border}`,
                            borderRadius:5, background:"#fff", color:C.text, fontFamily:"inherit", cursor:"pointer" }}>
                          <option value="1024x1024">1024×1024 (vierkant)</option>
                          <option value="1536x1024">1536×1024 (landscape)</option>
                          <option value="1024x1536">1024×1536 (portrait)</option>
                        </select>
                      </div>
                      <div>
                        <div style={{ fontSize:9, fontWeight:700, color:C.textMut, marginBottom:3, letterSpacing:0.5 }}>KWALITEIT</div>
                        <select value={openaiQuality} onChange={(e) => setOpenaiQuality(e.target.value)}
                          style={{ width:"100%", padding:"5px 8px", fontSize:11, border:`1px solid ${C.border}`,
                            borderRadius:5, background:"#fff", color:C.text, fontFamily:"inherit", cursor:"pointer" }}>
                          <option value="high">High (fotoreal)</option>
                          <option value="medium">Medium</option>
                          <option value="low">Low (snel/goedkoop)</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Prompt Editor Toggle */}
            <div style={{ marginBottom:12 }}>
              <button onClick={() => {
                if (!showPromptEditor) {
                  // Build richer prompt preview matching server-side structure
                  const siteW = Math.max(...blocks.map(b => b.polygon ? Math.max(...b.polygon.map(p=>p[0])) : b.x + b.w), 200);
                  const siteH = Math.max(...blocks.map(b => b.polygon ? Math.max(...b.polygon.map(p=>p[1])) : b.y + b.h), 200);
                  const layoutLines = blocks.map(b => {
                    const t = getT(b.type);
                    const bw = b.polygon ? Math.round(Math.max(...b.polygon.map(p=>p[0])) - Math.min(...b.polygon.map(p=>p[0]))) : Math.round(b.w);
                    const bh = b.polygon ? Math.round(Math.max(...b.polygon.map(p=>p[1])) - Math.min(...b.polygon.map(p=>p[1]))) : Math.round(b.h);
                    return `- ${t?.label || b.type}: ${bw}m × ${bh}m at (${Math.round(b.x)},${Math.round(b.y)})${b.polygon ? ` [polygon ${b.polygon.length}pts]` : ""}`;
                  });
                  const preview = screenshot3D
                    ? `[Vision mode: 3D screenshot + layout data worden gecombineerd]\n\nSite: ${Math.round(siteW)}m × ${Math.round(siteH)}m\nStijl: ${aiRenderStyle}\nModel: ${aiRenderModel.split("/").pop()}\n\nElementen:\n${layoutLines.join("\n")}\n\n⚡ De server verrijkt dit met ASCII map, materiaal-beschrijvingen, adjacency relaties en proportionele verhoudingen.`
                    : `Industrial logistics site: ${Math.round(siteW)}m × ${Math.round(siteH)}m\nCamera: ${aiRenderCamera} | Stijl: ${aiRenderStyle}\n\nElementen:\n${layoutLines.join("\n")}\n\n⚡ De server genereert een volledige prompt met ASCII map, materialen en ruimtelijke relaties.`;
                  setCustomPrompt(preview);
                }
                setShowPromptEditor(!showPromptEditor);
              }}
              style={{ width:"100%", padding:"8px 12px", background:showPromptEditor ? C.accentDim : C.surfAlt,
                border:`1px solid ${showPromptEditor ? C.accent : C.border}`, borderRadius:6,
                fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit", color:C.text, textAlign:"left" }}>
                📝 {showPromptEditor ? "Verberg prompt" : "Bekijk & pas prompt aan"}
              </button>
              {showPromptEditor && (
                <textarea value={customPrompt} onChange={(e) => setCustomPrompt(e.target.value)}
                  style={{ width:"100%", marginTop:8, minHeight:160, padding:10, fontSize:10, fontFamily:"'JetBrains Mono', monospace",
                    border:`1px solid ${C.border}`, borderRadius:6, resize:"vertical", lineHeight:1.5,
                    background:C.surfAlt, color:C.text, boxSizing:"border-box" }}
                  placeholder="AI prompt wordt hier gegenereerd..." />
              )}
            </div>

            {/* Info — provider + rate limit + verbruiks-meter */}
            {(() => {
              const isOpenai = aiRenderModel.startsWith("openai/");
              const provider = isOpenai ? "openai" : "gemini";
              const limit = isOpenai ? 10 : 30;
              // Tel renders dit uur uit history per provider
              const oneHourAgo = Date.now() - 3600 * 1000;
              const usedOpenai = renderHistory.filter(r => r.provider === "openai" && r.timestamp > oneHourAgo).length;
              const usedGemini = renderHistory.filter(r => r.provider !== "openai" && r.timestamp > oneHourAgo).length;
              const usedNow = isOpenai ? usedOpenai : usedGemini;
              const remaining = Math.max(0, limit - usedNow);
              const pct = Math.min(100, (usedNow / limit) * 100);
              const accent = isOpenai ? "#10A37F" : "#4285F4";
              const meterColor = pct > 80 ? C.err : pct > 50 ? "#F59E0B" : accent;
              return (
                <div style={{ background:C.surfAlt, padding:"10px 12px", borderRadius:8, marginBottom:16, border:`1px solid ${C.border}` }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                    <div style={{ fontSize:10, color:C.textSec }}>
                      📊 <strong>{blocks.length} blokken</strong> · <strong style={{color:accent}}>{isOpenai ? "OpenAI gpt-image-2" : "Google Gemini"}</strong>
                      {isOpenai ? " · ~20-40s 🏆" : aiRenderModel.includes("pro") ? " · ~30s 👑" : " · ~15-30s ⚡"}
                    </div>
                    <div style={{ fontSize:10, fontWeight:700, color:meterColor }}>
                      {remaining} / {limit} over
                    </div>
                  </div>
                  {/* Verbruiks-meter */}
                  <div style={{ height:6, background:C.border, borderRadius:3, overflow:"hidden", marginBottom:6 }}>
                    <div style={{ height:"100%", width:`${pct}%`, background:meterColor, transition:"width 0.4s, background 0.3s" }} />
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:9, color:C.textMut }}>
                    <span>🟢 OpenAI: {usedOpenai}/10</span>
                    <span>🔵 Gemini: {usedGemini}/30</span>
                    <span style={{ color:C.textSec }}>per uur · resetter loopt</span>
                  </div>
                </div>
              );
            })()}

            {/* Realtime status / progress */}
            {aiRendering && renderStatus && (
              <div style={{ background:"#EFF6FF", border:"1px solid #BFDBFE", padding:"10px 12px", borderRadius:8, marginBottom:16 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"#1E40AF", display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ display:"inline-block", width:8, height:8, borderRadius:"50%", background:"#3B82F6", animation:"pulse 1.4s ease-in-out infinite" }} />
                    {renderStatus.phase === "queued" && "⏳ In wachtrij…"}
                    {renderStatus.phase === "preparing" && "📋 Layout voorbereiden…"}
                    {renderStatus.phase === "generating" && "🎨 AI genereert render…"}
                    {renderStatus.phase === "fallback" && "🔁 Terugvallen op alternatief model…"}
                    {renderStatus.phase === "finalizing" && "✨ Afronden…"}
                  </div>
                  <div style={{ fontSize:10, fontWeight:700, color:"#1E40AF" }}>{Math.round(renderStatus.progress)}%</div>
                </div>
                <div style={{ height:6, background:"#DBEAFE", borderRadius:3, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${renderStatus.progress}%`, background:"linear-gradient(90deg, #3B82F6, #60A5FA)", transition:"width 0.5s" }} />
                </div>
                {renderStatus.message && (
                  <div style={{ fontSize:10, color:"#1E40AF", marginTop:6, opacity:0.85 }}>{renderStatus.message}</div>
                )}
              </div>
            )}

            {/* Error met provider/model + acties */}
            {aiRenderError && (() => {
              const errMsg = String(aiRenderError.message || aiRenderError);
              const isRateLimit = /rate.?limit|limiet|429|quota/i.test(errMsg) || aiRenderError.httpStatus === 429;
              const isAuthErr = /401|403|unauthorized|api.?key|forbidden|verified/i.test(errMsg) || [401, 403].includes(aiRenderError.httpStatus);
              const errProvider = aiRenderError.provider || (aiRenderModel.startsWith("openai/") ? "openai" : "gemini");
              const errModelShort = (aiRenderError.model || aiRenderModel).split("/").pop();
              const recovery = aiRenderError.recovery;
              const params = aiRenderError.params;
              const altModel = recovery?.altModel || (errProvider === "openai"
                ? "google/gemini-3.1-flash-image-preview"
                : "openai/gpt-image-2");
              const altLabel = errProvider === "openai" ? "🔵 Probeer Gemini" : "🟢 Probeer OpenAI";
              return (
                <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", padding:"10px 12px", borderRadius:8, marginBottom:16 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                    <span style={{ fontSize:14 }}>⚠️</span>
                    <div style={{ fontSize:11, fontWeight:700, color:C.err }}>
                      {isRateLimit ? "Rate limit bereikt" : isAuthErr ? "Authenticatie / verificatie probleem" : "Render mislukt"}
                      {aiRenderError.httpStatus ? ` · HTTP ${aiRenderError.httpStatus}` : ""}
                      {aiRenderError.apiCode ? ` · ${aiRenderError.apiCode}` : ""}
                    </div>
                    <div style={{ fontSize:9, padding:"2px 6px", borderRadius:8, background:errProvider === "openai" ? "#10A37F" : "#4285F4", color:"#fff", fontWeight:700, marginLeft:"auto" }}>
                      {errProvider === "openai" ? "🟢 OPENAI" : "🔵 GEMINI"} · {errModelShort}
                    </div>
                  </div>
                  <div style={{ fontSize:10, color:C.err, marginBottom:8, lineHeight:1.4 }}>{errMsg}</div>
                  {(params || aiRenderError.apiParam) && (
                    <div style={{ fontSize:9, color:C.textSec, marginBottom:8, fontFamily:"monospace", background:"#fff", border:`1px solid ${C.border}`, padding:"4px 6px", borderRadius:4 }}>
                      {params?.endpoint && <>endpoint=<b>{params.endpoint}</b> · </>}
                      {params?.size && <>size={params.size} · </>}
                      {params?.quality && <>quality={params.quality} · </>}
                      {aiRenderError.apiParam && <>param=<b>{aiRenderError.apiParam}</b> · </>}
                      {params?.triedModels && <>tried=[{params.triedModels.join(", ")}]</>}
                    </div>
                  )}
                  {recovery?.label && (
                    <div style={{ fontSize:10, color:"#0369A1", background:"#E0F2FE", border:"1px solid #BAE6FD", padding:"6px 8px", borderRadius:5, marginBottom:8 }}>
                      💡 <b>Aanbevolen actie:</b> {recovery.label}
                    </div>
                  )}
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    <button onClick={() => { setAiRenderError(null); }}
                      style={{ padding:"6px 10px", background:C.primary, color:"#fff", border:"none", borderRadius:5, fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                      🔄 Opnieuw
                    </button>
                    <button onClick={() => { setAiRenderModel(altModel); setAiRenderError(null); }}
                      style={{ padding:"6px 10px", background:"#fff", color:C.text, border:`1px solid ${C.border}`, borderRadius:5, fontSize:10, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                      {altLabel}
                    </button>
                    {recovery?.url && (
                      <a href={recovery.url} target="_blank" rel="noreferrer"
                        style={{ padding:"6px 10px", background:"#fff", color:"#0369A1", border:"1px solid #BAE6FD", borderRadius:5, fontSize:10, fontWeight:600, textDecoration:"none", fontFamily:"inherit" }}>
                        🔗 {recovery.action === "verify_org" ? "Verifieer org" : recovery.action === "add_billing" ? "Open billing" : "Open link"}
                      </a>
                    )}
                    {isRateLimit && (
                      <span style={{ padding:"6px 10px", fontSize:10, color:C.textSec, fontStyle:"italic" }}>
                        💡 Wacht tot het volgende uur of switch provider
                      </span>
                    )}
                  </div>
                  {aiRenderError.openaiBody && (
                    <details style={{ marginTop:8 }}>
                      <summary style={{ fontSize:10, color:C.textSec, cursor:"pointer", fontWeight:600 }}>Toon technische details</summary>
                      <pre style={{ fontSize:9, color:C.textSec, background:"#fff", border:`1px solid ${C.border}`, padding:6, borderRadius:4, marginTop:4, overflow:"auto", maxHeight:160, whiteSpace:"pre-wrap", wordBreak:"break-word" }}>
{typeof aiRenderError.openaiBody === "string" ? aiRenderError.openaiBody : JSON.stringify(aiRenderError.openaiBody, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              );
            })()}

            {/* Result */}
            {aiRenderResult && (() => {
              const isOpenai = aiRenderResult.provider === "openai";
              const accent = isOpenai ? "#10A37F" : "#4285F4";
              const accentDim = isOpenai ? "rgba(16,163,127,0.10)" : "rgba(66,133,244,0.10)";
              const modelShort = (aiRenderResult.model || aiRenderResult.provider || "onbekend").split("/").pop();
              const dims = aiRenderResult.dimensions;
              const mp = dims ? ((dims.width * dims.height) / 1_000_000).toFixed(1) : null;
              const latencyS = aiRenderResult.latencyMs ? (aiRenderResult.latencyMs / 1000).toFixed(1) : null;
              const rl = aiRenderResult.rateLimit;
              return (
                <div style={{ marginBottom:16 }}>
                  <img src={aiRenderResult.url} alt="AI Render"
                    style={{ width:"100%", borderRadius:8, border:`1px solid ${C.border}`, display:"block" }} />

                  {/* === Kwaliteits- / metadata-card === */}
                  <div style={{ marginTop:8, border:`1px solid ${C.border}`, borderLeft:`3px solid ${accent}`,
                    borderRadius:8, background:C.surfAlt, overflow:"hidden" }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                      padding:"8px 10px", background:accentDim, borderBottom:`1px solid ${C.border}` }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:14 }}>{isOpenai ? "🟢" : "🔵"}</span>
                        <div>
                          <div style={{ fontSize:10, fontWeight:700, color:accent, letterSpacing:0.5, lineHeight:1 }}>
                            {isOpenai ? "OPENAI" : "GOOGLE GEMINI"}
                          </div>
                          <div style={{ fontSize:11, fontWeight:600, color:C.text, marginTop:2, lineHeight:1 }}>
                            {modelShort}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize:9, padding:"3px 7px", borderRadius:10, fontWeight:700,
                        background:accent, color:"#fff", letterSpacing:0.5 }}>
                        ✓ KLAAR
                      </div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:0 }}>
                      <div style={{ padding:"8px 10px", borderRight:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}` }}>
                        <div style={{ fontSize:9, color:C.textMut, fontWeight:700, letterSpacing:0.5 }}>RESOLUTIE</div>
                        <div style={{ fontSize:12, fontWeight:600, color:C.text, marginTop:2 }}>
                          {dims ? `${dims.width} × ${dims.height}` : "—"}
                        </div>
                        {mp && <div style={{ fontSize:9, color:C.textSec, marginTop:1 }}>{mp} MP</div>}
                      </div>
                      <div style={{ padding:"8px 10px", borderBottom:`1px solid ${C.border}` }}>
                        <div style={{ fontSize:9, color:C.textMut, fontWeight:700, letterSpacing:0.5 }}>LATENCY</div>
                        <div style={{ fontSize:12, fontWeight:600, color:C.text, marginTop:2 }}>
                          {latencyS ? `${latencyS}s` : "—"}
                        </div>
                        <div style={{ fontSize:9, color:C.textSec, marginTop:1 }}>
                          {latencyS && parseFloat(latencyS) < 15 ? "snel" : latencyS && parseFloat(latencyS) < 30 ? "normaal" : "traag"}
                        </div>
                      </div>
                      <div style={{ padding:"8px 10px", borderRight:`1px solid ${C.border}` }}>
                        <div style={{ fontSize:9, color:C.textMut, fontWeight:700, letterSpacing:0.5 }}>CAMERA / STIJL</div>
                        <div style={{ fontSize:11, fontWeight:600, color:C.text, marginTop:2, textTransform:"capitalize" }}>
                          {aiRenderResult.camera || aiRenderCamera}
                        </div>
                        <div style={{ fontSize:9, color:C.textSec, marginTop:1, textTransform:"capitalize" }}>
                          {aiRenderResult.style || aiRenderStyle}
                        </div>
                      </div>
                      <div style={{ padding:"8px 10px" }}>
                        <div style={{ fontSize:9, color:C.textMut, fontWeight:700, letterSpacing:0.5 }}>VERBRUIK</div>
                        <div style={{ fontSize:12, fontWeight:600, color:C.text, marginTop:2 }}>
                          {rl ? `${rl.count} / ${rl.limit}` : "—"}
                        </div>
                        <div style={{ fontSize:9, color:C.textSec, marginTop:1 }}>
                          {rl ? `${rl.limit - rl.count} over dit uur` : "geen limiet info"}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display:"flex", gap:6, marginTop:8, flexWrap:"wrap" }}>
                    <a href={aiRenderResult.url} download={`render-${aiRenderResult.generatedAt || Date.now()}.png`}
                      style={{ flex:"1 1 30%", padding:"8px", background:C.primary, color:"#fff", textAlign:"center",
                        borderRadius:6, fontSize:11, fontWeight:700, textDecoration:"none", fontFamily:"inherit" }}>
                      ⬇ Download
                    </a>
                    <a href={aiRenderResult.url} target="_blank" rel="noopener noreferrer"
                      style={{ flex:"1 1 30%", padding:"8px", background:"#fff", color:C.text, textAlign:"center", border:`1px solid ${C.border}`,
                        borderRadius:6, fontSize:11, fontWeight:700, textDecoration:"none", fontFamily:"inherit" }}>
                      🔗 Open afbeelding
                    </a>
                    <button onClick={() => { setAiRenderResult(null); setAiRenderError(null); }}
                      style={{ flex:"1 1 30%", padding:"8px", background:C.surfAlt, border:`1px solid ${C.border}`,
                        borderRadius:6, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit", color:C.text }}>
                      🔄 Opnieuw
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Generate button */}
            {!aiRenderResult && (
              <button onClick={async () => {
                setAiRendering(true);
                setAiRenderError(null);
                setAiRenderResult(null);
                setRenderStatus({ phase:"queued", progress:5, message:"Verzoek wordt voorbereid…" });
                // Realtime fake-but-honest progression (server is single-shot, we surface phases client-side)
                const phaseTimers = [];
                phaseTimers.push(setTimeout(() => setRenderStatus({ phase:"preparing", progress:18, message:`Layout van ${blocks.length} blokken voorbereiden voor ${aiRenderModel.startsWith("openai/") ? "OpenAI" : "Gemini"}…` }), 400));
                phaseTimers.push(setTimeout(() => setRenderStatus({ phase:"generating", progress:42, message:"AI tekent fotorealistische render…" }), 1500));
                phaseTimers.push(setTimeout(() => setRenderStatus(s => s && s.phase === "generating" ? { ...s, progress:68 } : s), 8000));
                phaseTimers.push(setTimeout(() => setRenderStatus(s => s && s.phase === "generating" ? { ...s, progress:82, message:"Bijna klaar, beeld verwerken…" } : s), 16000));
                try {
                  const body = {
                    blocks, kavel,
                    camera: aiRenderCamera,
                    style: aiRenderStyle,
                    projectName: project?.name || "site",
                    model: aiRenderModel,
                    openaiSize,
                    openaiQuality,
                  };
                  if (showPromptEditor && customPrompt.trim()) body.customPrompt = customPrompt;
                  if (screenshot3D) {
                    body.referenceImage = screenshot3D.dataUrl || screenshot3D;
                    if (screenshot3D.cameraPosition) {
                      body.cameraData = { position: screenshot3D.cameraPosition, target: screenshot3D.cameraTarget, fov: screenshot3D.fov };
                    }
                  }
                  const _t0 = performance.now();
                  const res = await supabase.functions.invoke("render-site-3d", { body });
                  const _latencyMs = Math.round(performance.now() - _t0);
                  if (res.error) {
                    // Try to read context from FunctionsHttpError
                    let extra = {};
                    try {
                      const ctx = res.error?.context;
                      if (ctx && typeof ctx.json === "function") extra = await ctx.json();
                      else if (ctx && typeof ctx.text === "function") {
                        const t = await ctx.text();
                        try { extra = JSON.parse(t); } catch { extra = { openaiBody: t }; }
                      }
                    } catch { /* ignore */ }
                    const e = new Error(extra.error || res.error.message || "Edge function fout");
                    e.provider = extra.provider || (aiRenderModel.startsWith("openai/") ? "openai" : "gemini");
                    e.model = extra.model || aiRenderModel;
                    e.httpStatus = extra.httpStatus;
                    e.apiCode = extra.apiCode;
                    e.apiParam = extra.apiParam;
                    e.params = extra.params;
                    e.recovery = extra.recovery;
                    e.openaiBody = extra.openaiBody;
                    throw e;
                  }
                  const data = res.data;
                  if (data.error) {
                    const e = new Error(data.error);
                    e.provider = data.provider || (aiRenderModel.startsWith("openai/") ? "openai" : "gemini");
                    e.model = data.model || aiRenderModel;
                    e.httpStatus = data.httpStatus;
                    e.apiCode = data.apiCode;
                    e.apiParam = data.apiParam;
                    e.params = data.params;
                    e.recovery = data.recovery;
                    e.openaiBody = data.openaiBody;
                    throw e;
                  }
                  // Detect fallback (server returned different model than requested)
                  if (data.model && data.model !== aiRenderModel) {
                    setRenderStatus({ phase:"fallback", progress:90, message:`Primair model faalde, ${data.model.split("/").pop()} gebruikt` });
                    await new Promise(r => setTimeout(r, 600));
                  }
                  setRenderStatus({ phase:"finalizing", progress:96, message:"Afbeelding laden…" });
                  const _dims = await new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
                    img.onerror = () => resolve(null);
                    img.src = data.url;
                  });
                  const enriched = {
                    ...data,
                    latencyMs: _latencyMs,
                    dimensions: _dims,
                    generatedAt: Date.now(),
                    camera: aiRenderCamera,
                    style: aiRenderStyle,
                    requestedModel: aiRenderModel,
                  };
                  setAiRenderResult(enriched);
                  setRenderHistory(prev => [
                    {
                      url: data.url,
                      camera: aiRenderCamera,
                      style: aiRenderStyle,
                      timestamp: Date.now(),
                      prompt: data.prompt,
                      provider: enriched.provider || (aiRenderModel.startsWith("openai/") ? "openai" : "gemini"),
                      model: enriched.model || aiRenderModel,
                      latencyMs: _latencyMs,
                      dimensions: _dims,
                      rateLimit: enriched.rateLimit,
                    },
                    ...prev,
                  ].slice(0, 20));
                } catch (err) {
                  console.error("AI Render error:", err);
                  setAiRenderError(err);
                } finally {
                  phaseTimers.forEach(clearTimeout);
                  setAiRendering(false);
                  setTimeout(() => setRenderStatus(null), 800);
                }
              }}
              disabled={aiRendering || blocks.length === 0}
              style={{ width:"100%", padding:"12px", background:aiRendering ? C.surfAlt : "linear-gradient(135deg, #014336, #02714D)",
                color:aiRendering ? C.textSec : "#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:700,
                cursor:aiRendering ? "wait" : "pointer", fontFamily:"inherit", transition:"all 0.2s",
                opacity:blocks.length===0 ? 0.5 : 1 }}>
                {aiRendering ? "⏳ Genereren…" : screenshot3D ? "🎨 Render vanuit 3D screenshot" : "🎨 Genereer 3D Render"}
              </button>
            )}

            {blocks.length === 0 && (
              <p style={{ fontSize:10, color:C.textMut, textAlign:"center", marginTop:8 }}>
                Plaats eerst blokken op het terrein om een render te genereren.
              </p>
            )}

            {/* Render History met provider/model/latency badges */}
            {renderHistory.length > 0 && (
              <div style={{ marginTop:20, borderTop:`1px solid ${C.border}`, paddingTop:12 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:C.textMut, letterSpacing:1 }}>📜 RENDERGESCHIEDENIS ({renderHistory.length})</div>
                  <div style={{ display:"flex", gap:6 }}>
                    {compareSelection.length === 2 && (
                      <button onClick={() => setShowCompare(true)}
                        style={{ padding:"4px 8px", background:C.primary, color:"#fff", border:"none", borderRadius:4, fontSize:9, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                        ⚖ Vergelijk ({compareSelection.length})
                      </button>
                    )}
                    {compareSelection.length > 0 && (
                      <button onClick={() => setCompareSelection([])}
                        style={{ padding:"4px 8px", background:C.surfAlt, color:C.textSec, border:`1px solid ${C.border}`, borderRadius:4, fontSize:9, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                        ✕ Wis selectie
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ fontSize:9, color:C.textMut, marginBottom:8 }}>
                  💡 Klik thumbnail = preview · Klik ✓ = selecteer max 2 om te vergelijken
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {renderHistory.map((r, i) => {
                    const isOpenai = r.provider === "openai";
                    const accent = isOpenai ? "#10A37F" : "#4285F4";
                    const modelShort = (r.model || "").split("/").pop() || "—";
                    const isSelected = compareSelection.includes(i);
                    const latS = r.latencyMs ? (r.latencyMs / 1000).toFixed(1) + "s" : "—";
                    const dimStr = r.dimensions ? `${r.dimensions.width}×${r.dimensions.height}` : "—";
                    return (
                      <div key={i}
                        style={{ display:"flex", gap:8, padding:6, borderRadius:6,
                          border:`1px solid ${isSelected ? accent : C.border}`,
                          background: isSelected ? (isOpenai ? "rgba(16,163,127,0.06)" : "rgba(66,133,244,0.06)") : C.surf,
                          transition:"all 0.15s" }}>
                        <img src={r.url} alt={`Render ${i+1}`} onClick={() => setHistoryPreview(r)}
                          style={{ width:64, height:48, objectFit:"cover", borderRadius:4, cursor:"pointer", flexShrink:0 }} />
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:2 }}>
                            <span style={{ fontSize:8, padding:"1px 5px", borderRadius:6, background:accent, color:"#fff", fontWeight:700 }}>
                              {isOpenai ? "🟢 OPENAI" : "🔵 GEMINI"}
                            </span>
                            <span style={{ fontSize:9, color:C.text, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                              {modelShort}
                            </span>
                          </div>
                          <div style={{ fontSize:9, color:C.textSec, display:"flex", gap:8, flexWrap:"wrap" }}>
                            <span>📐 {dimStr}</span>
                            <span>⚡ {latS}</span>
                            <span style={{ textTransform:"capitalize" }}>📷 {r.camera}</span>
                          </div>
                          <div style={{ fontSize:8, color:C.textMut, marginTop:1 }}>
                            {new Date(r.timestamp).toLocaleString("nl-NL", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}
                          </div>
                        </div>
                        <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                          <button onClick={() => {
                            setCompareSelection(prev => {
                              if (prev.includes(i)) return prev.filter(x => x !== i);
                              if (prev.length >= 2) return [prev[1], i];
                              return [...prev, i];
                            });
                          }}
                            title={isSelected ? "Deselecteer" : "Selecteer voor vergelijking"}
                            style={{ width:24, height:24, borderRadius:4, border:`1px solid ${isSelected ? accent : C.border}`,
                              background:isSelected ? accent : "#fff", color:isSelected ? "#fff" : C.textSec,
                              fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                            {isSelected ? "✓" : "○"}
                          </button>
                          <a href={r.url} download={`render-${r.timestamp}.png`} title="Download"
                            style={{ width:24, height:24, borderRadius:4, border:`1px solid ${C.border}`,
                              background:"#fff", color:C.textSec, fontSize:11, display:"flex", alignItems:"center", justifyContent:"center", textDecoration:"none" }}>
                            ⬇
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Vergelijkings-card */}
            {showCompare && compareSelection.length === 2 && (() => {
              const [aIdx, bIdx] = compareSelection;
              const a = renderHistory[aIdx];
              const b = renderHistory[bIdx];
              if (!a || !b) return null;
              const Cell = ({ render, label }) => {
                const isOpenai = render.provider === "openai";
                const accent = isOpenai ? "#10A37F" : "#4285F4";
                const dimStr = render.dimensions ? `${render.dimensions.width}×${render.dimensions.height}` : "—";
                const mp = render.dimensions ? ((render.dimensions.width * render.dimensions.height) / 1_000_000).toFixed(1) + " MP" : "—";
                const latS = render.latencyMs ? (render.latencyMs / 1000).toFixed(1) + "s" : "—";
                return (
                  <div style={{ flex:1, border:`1px solid ${C.border}`, borderTop:`3px solid ${accent}`, borderRadius:8, overflow:"hidden", background:C.surf }}>
                    <div style={{ padding:"6px 10px", background:isOpenai ? "rgba(16,163,127,0.08)" : "rgba(66,133,244,0.08)", fontSize:10, fontWeight:700, color:accent }}>
                      {label} · {isOpenai ? "🟢 OPENAI" : "🔵 GEMINI"}
                    </div>
                    <img src={render.url} alt={label} style={{ width:"100%", height:160, objectFit:"cover", display:"block" }} />
                    <div style={{ padding:"8px 10px", fontSize:10, color:C.text }}>
                      <div style={{ fontWeight:700, marginBottom:4 }}>{(render.model || "").split("/").pop()}</div>
                      <div style={{ display:"grid", gridTemplateColumns:"auto 1fr", gap:"3px 8px", color:C.textSec, fontSize:9 }}>
                        <span>📐 Resolutie</span><span style={{ color:C.text, fontWeight:600 }}>{dimStr} ({mp})</span>
                        <span>⚡ Latency</span><span style={{ color:C.text, fontWeight:600 }}>{latS}</span>
                        <span>📷 Camera</span><span style={{ color:C.text, textTransform:"capitalize" }}>{render.camera}</span>
                        <span>🎨 Stijl</span><span style={{ color:C.text, textTransform:"capitalize" }}>{render.style}</span>
                        <span>📊 Verbruik</span><span style={{ color:C.text }}>{render.rateLimit ? `${render.rateLimit.count}/${render.rateLimit.limit}` : "—"}</span>
                      </div>
                    </div>
                  </div>
                );
              };
              return (
                <div style={{ position:"fixed", inset:0, zIndex:10002, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
                  <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.75)" }} onClick={() => setShowCompare(false)} />
                  <div style={{ position:"relative", background:C.surf, borderRadius:12, padding:16, maxWidth:900, width:"100%", maxHeight:"90vh", overflow:"auto" }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                      <div style={{ fontSize:14, fontWeight:700, color:C.text }}>⚖ Render vergelijking</div>
                      <button onClick={() => setShowCompare(false)}
                        style={{ width:28, height:28, borderRadius:6, border:`1px solid ${C.border}`, background:"#fff", cursor:"pointer", fontSize:14 }}>✕</button>
                    </div>
                    <div style={{ display:"flex", gap:12 }}>
                      <Cell render={a} label="A" />
                      <Cell render={b} label="B" />
                    </div>
                    {/* Diff samenvatting */}
                    <div style={{ marginTop:12, padding:10, background:C.surfAlt, borderRadius:8, fontSize:10, color:C.textSec }}>
                      <strong style={{ color:C.text }}>Verschil:</strong>{" "}
                      Latency Δ {Math.abs((a.latencyMs || 0) - (b.latencyMs || 0)) / 1000 > 0 ? `${(Math.abs((a.latencyMs || 0) - (b.latencyMs || 0)) / 1000).toFixed(1)}s` : "—"} ·{" "}
                      {a.provider === b.provider ? "Zelfde provider" : "Verschillende providers"} ·{" "}
                      {a.model === b.model ? "Zelfde model" : "Verschillende modellen"}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* History Preview Lightbox */}
            {historyPreview && (
              <div style={{ position:"fixed", inset:0, zIndex:10001, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.75)" }}
                  onClick={() => setHistoryPreview(null)} />
                <div style={{ position:"relative", maxWidth:"90vw", maxHeight:"90vh" }}>
                  <img src={historyPreview.url} alt="Render preview"
                    style={{ maxWidth:"100%", maxHeight:"85vh", borderRadius:8, boxShadow:"0 20px 60px rgba(0,0,0,0.5)" }} />
                  <div style={{ display:"flex", gap:8, marginTop:8, justifyContent:"center" }}>
                    <a href={historyPreview.url} download={`render-${historyPreview.timestamp}.png`}
                      style={{ padding:"8px 16px", background:C.primary, color:"#fff", borderRadius:6,
                        fontSize:11, fontWeight:700, textDecoration:"none" }}>⬇ Download</a>
                    <a href={historyPreview.url} target="_blank" rel="noopener noreferrer"
                      style={{ padding:"8px 16px", background:"#fff", color:C.text, borderRadius:6,
                        fontSize:11, fontWeight:700, textDecoration:"none", border:`1px solid ${C.border}` }}>🔗 Open</a>
                    <button onClick={() => setHistoryPreview(null)}
                      style={{ padding:"8px 16px", background:"rgba(255,255,255,0.9)", border:"none",
                        borderRadius:6, fontSize:11, fontWeight:600, cursor:"pointer" }}>✕ Sluiten</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 3D PREVIEW OVERLAY */}
      {show3DView && (
        <Suspense fallback={<div style={{ position:"fixed", inset:0, zIndex:10000, background:"#1a1a1a", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff" }}>Laden 3D omgeving...</div>}>
          <SitePlanner3DView
            blocks={blocks}
            kavel={kavel}
            onScreenshot={(data) => {
              setScreenshot3D(data);
            }}
            onClose={() => {
              setShow3DView(false);
              // If screenshot was taken, open AI render modal with it
              if (screenshot3D) {
                setAiRenderOpen(true);
              }
            }}
          />
        </Suspense>
      )}
    </div>
  );
}
